import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
// ⚠️ Les modales passent par un portail : voir `.claude/CLAUDE.md` — sans lui, la sidebar
// et les parents à `overflow: hidden` les rognent ou passent au-dessus.
import ReactDOM from 'react-dom';
import config from '../config';
import {
  Store,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  Info,
  Check,
  X,
  Sparkles,
  FileText,
  Copy,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Save,
  CheckCircle,
  Loader2,
  Eye,
  Filter,
  ImageOff,
  Search,
  Link,
  Plus,
  ChevronLeft,
  Tag,
  Clock,
  RotateCcw,
  ArrowUp,
  ChevronUp,
  Layers,
  ArrowDown,
  ArrowUpRight,
  Pencil,
  HelpCircle
} from 'lucide-react';
import {
  estAdresse, nomDeFichier, normaliserEnum, texteAttribut, vignetteDe, lireMesureSaisie
} from './texteAttribut';
// ⚠️ L'unique infobulle du projet — voir `.claude/CLAUDE.md` : un `title` natif ne sort pas
// sur une cellule de tableau, et une bulle maison est rognée par l'`overflow: hidden` des listes.
import { useBulleAuSurvol } from './BulleSurvol';
import { messageErreur, texteAffichable } from '../utils/messageErreur';
import {
  produitDeLaFiche,
  attributsInconnusDeLAnalyse,
  attributsNonRelevesPour,
  enTableau
} from './ficheProduit';
import {
  BRIQUES_TITRE,
  libelleBrique,
  MOTS_ETAT_BRIQUE,
  dejaDansLeTitre,
  gabaritPose,
  gabaritDepuisLeTitre
} from './gabaritTitre';

const API_URL = config.API_URL;

/**
 * Ce que vaut le verdict d'une image d'ambiance, en toutes lettres.
 *
 * ⚠️ **Les trois niveaux sont montrés, aucun n'est masqué.** La frontière entre un plan
 * studio et une scène est réellement floue sur une partie des cas — mesuré : un à-plat de
 * tongs sur fond sableux, défendable dans les deux sens, à quoi le modèle a justement répondu
 * « probable ». Ne présenter que les « sûre » priverait d'une bonne réponse une fois sur
 * deux ; les présenter toutes du même ton ferait valider sans regarder.
 *
 * Les clés sont celles de `CONFIANCES`, dans `backend/src/lib/imageAmbiance.js`.
 */
/**
 * Le nombre de produits qu'une seule requête de génération transporte.
 *
 * ⚠️ **C'est le plafond du serveur, recopié ici — et les deux doivent rester d'accord.**
 * `POST /generate-suggestions` refuse au-delà de 50 : le service traite cinq produits à la
 * fois avec une pause entre les paquets, si bien que cinquante tiennent une requête HTTP
 * ouverte une minute et demie. Monter ce nombre sans le monter là-bas fait répondre 400 ;
 * le monter des deux côtés fabrique des requêtes que les frontaux finissent par couper.
 *
 * L'écran découpe donc lui-même : cocher cent produits lance deux requêtes, et le bouton dit
 * où il en est. Avant, la requête partait entière, revenait en 400, et le bandeau d'erreur
 * s'affichait **trois cent soixante lignes plus haut** que le bouton — hors écran sur une
 * liste de deux mille produits. Rien ne paraissait se passer.
 */
/*
 * ⚠️ **Dix, et non cinquante : c'est la passerelle qui décide, pas la route.**
 *
 * `POST /generate-suggestions` accepte cinquante produits — c'est sa limite, et elle tient.
 * Mais le serveur les traite **cinq à la fois** (`batchSize` dans `feedOptimizerAI`), avec
 * une seconde de pause entre les tours : cinquante produits font dix tours, et un tour dure
 * le temps du plus lent des cinq appels au modèle. Relevé en production, la requête n'est
 * jamais revenue — « POST …/generate-suggestions **502 (Bad Gateway)** », la passerelle
 * ayant coupé avant la fin.
 *
 * Dix produits font deux tours : la requête reste de l'ordre de la minute, et le bouton
 * annonce son avancement tous les dix produits au lieu de tous les cinquante.
 *
 * ⚠️ **Ce n'est pas un réglage de confort, et le baisser ne coûte rien** : le travail total
 * est identique — même nombre d'appels au modèle, mêmes jetons —, seule la découpe change.
 * Le remonter sans savoir où la passerelle coupe ferait revenir la panne.
 *
 * ⚠️ **Un lot perdu n'annule pas les précédents.** Chaque requête enregistre ses suggestions
 * avant de répondre : un 502 au septième lot laisse les six premiers en base. Le message
 * d'échec le dit, sans quoi on relancerait tout.
 */
const LOT_GENERATION = 10;

/**
 * Au-delà de ce nombre, la modale propose le **lot asynchrone** plutôt que le chemin direct.
 *
 * ⚠️ **Cinquante, et le seuil est un jugement, pas une limite technique.** Le chemin
 * synchrone tient jusqu'à la patience de qui regarde : cinquante produits font cinq requêtes
 * et quelques minutes, ce qu'on attend volontiers pour travailler dessus tout de suite. Cent
 * produits font dix requêtes, et c'est déjà l'attente qui a fait naître la file.
 *
 * Le lot, lui, est deux fois moins cher mais rend ses résultats sous une heure en général —
 * au plus tard sous vingt-quatre. Mauvais pour trois produits, seul tenable pour trois mille.
 */
const SEUIL_LOT = 50;

const LIBELLE_CONFIANCE = {
  sure: 'certaine',
  probable: 'probable, à vérifier',
  incertaine: 'incertaine, à vérifier'
};

/**
 * Les trois places possibles de la marque dans un titre.
 *
 * Les clés sont celles que le serveur connaît (`feedOptimizerPositionMarque.js`) : les libellés
 * ne servent qu'à l'écran, et les traduire ici évite d'avoir à les deviner côté API.
 */
/**
 * L'âge d'une analyse, en clair.
 *
 * Une date absolue oblige à calculer de tête ; ce qui compte, en ouvrant l'outil, est de savoir
 * si le relevé date de ce matin ou de la semaine dernière. La date exacte reste en infobulle.
 */
function decrireFraicheur(date) {
  const minutes = Math.round((Date.now() - new Date(date).getTime()) / 60000);
  if (minutes < 1) return 'à l’instant';
  if (minutes < 60) return `il y a ${minutes} min`;

  const heures = Math.round(minutes / 60);
  if (heures < 24) return `il y a ${heures} h`;

  const jours = Math.round(heures / 24);
  return jours === 1 ? 'hier' : `il y a ${jours} jours`;
}

/**
 * Deux écritures de la même marque ?
 *
 * Reprend la règle de `feedOptimizerPositionMarque.js` côté serveur — casse, accents, espaces et
 * traits d'union ignorés. Les deux doivent rester d'accord : si l'écran proposait une graphie que
 * le serveur refuse d'appliquer, le champ afficherait un réglage sans effet.
 */
const memeMarque = (a, b) => {
  const nu = (valeur) => String(valeur || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\s\-–—_]/g, '')
    .toLowerCase();

  const gauche = nu(a);
  return gauche !== '' && gauche === nu(b);
};


/**
 * Les quatre états d'une suggestion, en français.
 *
 * La liste des produits les traduisait, l'onglet Suggestions affichait la valeur de la base —
 * « PENDING », « VALIDATED » — dans une pastille à côté. Un même état sous deux vocabulaires,
 * dont l'un n'est pas une langue. La table est au niveau du module : elle sert aux deux écrans.
 */
/*
  ⚠️ **« Écarté », et non « Refusé ».** Le geste s'appelle « Écarter du flux » depuis qu'on a
  cessé de supprimer la suggestion : elle est conservée et reprenable. « Refusé » laissait
  entendre un rejet définitif, et le libellé du statut doit dire la même chose que le bouton
  qui l'a posé.
*/
const LIBELLES_STATUT = {
  PENDING: 'En attente',
  VALIDATED: 'Validé',
  MODIFIED: 'Modifié',
  REJECTED: 'Écarté'
};

/**
 * Les trois gravités, et leur nom au singulier comme au pluriel.
 *
 * La liste des produits énumérait les messages entiers — « Description trop courte
 * (35 caractères, min recommandé : 100) » — ce qui portait chaque ligne à 183 pixels. Elle
 * affiche maintenant un décompte par gravité, les messages restant en infobulle et la fiche
 * produit les donnant tous avec leur voie de correction.
 */
const GRAVITES = [
  { cle: 'critical', singulier: 'critique', pluriel: 'critiques' },
  { cle: 'warning', singulier: 'avertissement', pluriel: 'avertissements' },
  { cle: 'info', singulier: 'information', pluriel: 'informations' }
];

/**
 * Les colonnes triables de la liste des produits.
 *
 * `sens` donne le premier clic : on cherche d'abord les scores les plus bas et les problèmes
 * les plus nombreux, pas l'inverse. Trier par ordre croissant de gravité obligerait à cliquer
 * deux fois pour obtenir ce qu'on venait chercher.
 */
const COLONNES_PRODUITS = [
  { cle: 'produit', libelle: 'Produit', sens: 'asc' },
  { cle: 'score', libelle: 'Score', sens: 'asc' },
  { cle: 'problemes', libelle: 'Problèmes', sens: 'desc' },
  { cle: 'suggestion', libelle: 'Suggestion', sens: 'asc' }
];

/**
 * Les cinq choix du filtre d'état.
 *
 * ⚠️ **« Validées » couvre `VALIDATED` et `MODIFIED`.** Le flux XML ne distingue pas les deux —
 * `STATUTS_PUBLIES` les sert tous les deux —, et les séparer poserait au lecteur une question
 * que le flux ne pose pas.
 *
 * ⚠️ Une entrée sans `statuts` vise l'**absence** de suggestion : c'est ce qui permet de
 * retrouver ce qu'il reste à générer, et aucun statut ne peut l'exprimer.
 */
const ETATS_SUGGESTION = [
  /*
   * ⚠️ **Le nom de l'axe, pas une formule en « toutes les … ».** Les deux libellés d'origine
   * débordaient de leur `select` depuis que les filtres se partagent la largeur — on lisait
   * « Toutes les gravi… ». Les filtres de segment nomment déjà leur axe (« Marque »,
   * « Catégorie »), et c'est la convention qui tient à l'étroit : un nom court dit ce que la
   * liste propose, et l'absence de choix se lit à ce qu'aucun autre n'est retenu.
   */
  { cle: 'all', libelle: 'Suggestions' },
  { cle: 'PENDING', libelle: 'En attente', statuts: ['PENDING'] },
  /*
   * ⚠️ **« À valider » se juge sur la LIGNE, pas sur un statut.** Une publiée régénérée
   * garde `VALIDATED` — le flux sert toujours l'ancien instantané — alors que la ligne montre
   * désormais la proposition. Sans ce choix, on ne pourrait pas retrouver ce qu'on vient de
   * régénérer : le travail serait fait et invisible.
   *
   * ⚠️ Elle reste comptée dans « Validées », et c'est juste : Google reçoit bien une version
   * validée de ce produit. Les deux cuts se recoupent, et aucun des deux ne ment.
   */
  { cle: 'A_REVALIDER', libelle: 'À valider', predicat: (p) => aRevalider(p) },
  { cle: 'PUBLIEES', libelle: 'Validées', statuts: ['VALIDATED', 'MODIFIED'] },
  { cle: 'REJECTED', libelle: 'Écartées', statuts: ['REJECTED'] },
  { cle: 'AUCUNE', libelle: 'Sans suggestion' }
];
const STATUTS_PUBLIES_ECRAN = ETATS_SUGGESTION.find(e => e.cle === 'PUBLIEES').statuts;

/**
 * Cette ligne montre-t-elle une proposition que le flux ne sert pas encore ?
 *
 * ⚠️ **`enLigne` décrit l'AFFICHÉ, pas le statut** : il est `false` sur une publiée régénérée,
 * dont la ligne porte la proposition pendant que Google reçoit l'instantané précédent. La
 * pastille doit donc dire « À valider » et non « Validé » — sinon elle affirme d'une version
 * non validée qu'elle l'est, sous les chiffres de cette version.
 */
const aRevalider = (produit) => Boolean(produit
  && produit.enLigne === false
  && STATUTS_PUBLIES_ECRAN.includes(produit.statutSuggestion));

/** L'ordre de lecture des statuts quand on trie par suggestion : le travail à faire d'abord. */
const RANG_STATUT = { PENDING: 0, MODIFIED: 1, VALIDATED: 2, REJECTED: 3 };

/**
 * Les quatre attributs que l'en-tête de la fiche porte, et que le tableau ne reprend donc pas.
 *
 * Ce ne sont pas des champs à corriger mais des repères — « de quel produit parle-t-on, à quel
 * prix, est-il en vente, à quoi ressemble-t-il ? ». La table le dit d'elle-même : l'URL est
 * « lue et conservée pour que l'écran puisse situer le produit, sans règle ni correction », et
 * les trois autres sont rangés parmi les « constatés, non corrigeables ». Leurs cartes ne
 * portaient donc qu'une note ; en tête de fiche, elles situent le produit.
 *
 * ⚠️ **Les alertes suivent les champs.** `image_link`, `price` et `availability` sont, eux,
 * diagnostiqués : retirer leur ligne sans déplacer leur constat aurait fait disparaître « Image
 * principale manquante » ou « Prix manquant » de la fiche. La vignette porte l'alerte de
 * l'image (`.fo-fiche-image.manquante`), la rangée des repères celle des deux autres — et
 * reprend aussi leur « non relevé », qui ne figure plus dans l'avertissement du tableau.
 *
 * ⚠️ **On filtre à l'affichage, on ne retire rien de la table.** `attributsMerchant.js` reste la
 * source unique : c'est elle qui décide que le titre est éditable, suggérable et écrit dans le
 * flux, et `valeursEditables()` comme `attributsAEnvoyer()` s'appuient dessus. Ces quatre
 * champs ne sortent que du tableau comparatif.
 */
const ATTRIBUTS_EN_ENTETE = ['link', 'image_link', 'price', 'availability'];

/**
 * Les widgets qui éditent une **liste**, et non une valeur unique.
 *
 * Leur valeur vide est `[]`, jamais `''` : un `.map()` sur une chaîne vide ne lève pas, il
 * rend zéro élément — la rangée d'ajout disparaîtrait sans un mot, et le champ serait
 * inéditable. Trois endroits initialisent les éditions d'une fiche et testaient `widget ===
 * 'liste'` en clair ; l'arrivée de `product_detail`, deuxième widget de liste, aurait demandé
 * de les retrouver tous les trois — c'est exactement l'écart que la table d'attributs existe
 * pour empêcher côté serveur.
 */
const WIDGETS_DE_LISTE = ['liste', 'groupe'];

/** La valeur vide d'un attribut : `[]` pour un éditeur en bloc, `''` pour le reste. */
const valeurVide = (a) => (WIDGETS_DE_LISTE.includes(a.widget) ? [] : '');

/**
 * Le ton de la pastille de disponibilité, sur les trois états de `.kc-etat` de la charte.
 *
 * Une disponibilité est un état, pas une valeur : elle se lit comme « Actif » ou « Accès
 * révoqué » chez les Connecteurs, et prend donc la même pastille à point coloré.
 */
/**
 * Les absences que la fiche signale sans que l'analyse les contrôle.
 *
 * ⚠️ **Ce ne sont pas des constats.** Les points forts sont le seul attribut `suggerable` que
 * `feedOptimizerRules` ne contrôle pas : leur absence ne lève aucun problème, ne compte pas
 * dans le score, et ne paraît pas dans « Problèmes les plus fréquents ». Mais c'est une
 * absence que le modèle sait combler, et la taire sous un « Non renseigné » gris revenait à
 * la ranger avec les champs dont on n'a rien à faire.
 *
 * D'où le ton `info` et une formulation de fait — « Aucun point fort » — plutôt que le
 * « manquant » des vrais constats : l'écran n'emprunte pas la voix de l'analyse pour dire
 * quelque chose qu'elle n'a pas dit.
 *
 * Les faire entrer dans les règles serait l'autre solution. Elle coûterait des points de score
 * à presque tous les produits du parc, et ne vaudrait qu'après une réanalyse de chaque flux :
 * c'est une décision sur le diagnostic, pas sur la fiche.
 */
/*
 * ⚠️ **Vidée, et gardée vide à dessein.**
 *
 * Elle fabriquait côté écran les deux constats que l'analyse ne savait pas produire — « Aucun
 * point fort », « Aucune caractéristique technique ». Ces deux-là sont désormais de **vraies
 * règles** du moteur (`ENRICHISSEMENTS`), avec leur gravité et leur poids au score : les
 * fabriquer ici en plus les afficherait deux fois sur les analyses récentes.
 *
 * Elle sert encore de filet pour les analyses **antérieures** à ces règles, dont l'instantané
 * ne porte ni la clé ni le constat — mais aucun attribut n'y est inscrit, parce qu'en inscrire
 * un reviendrait à promettre un verdict que l'analyse n'a pas rendu. Le badge « Non relevé par
 * cette analyse » est la réponse honnête dans ce cas, et il reste.
 */
/**
 * Ce que le marchand a écrit, pour une ligne de la liste.
 *
 * ⚠️ **Toute demande au modèle, et toute colonne « Flux source », passe par ici.**
 *
 * Les produits reçus de `GET /analysis` portent le contenu **enrichi** dès qu'une suggestion
 * existe — proposée comme publiée : c'est ce qui fait que la liste affiche le vrai titre et la
 * vraie note. `fluxSource` porte alors, à côté, ce que le marchand a écrit.
 *
 * Renvoyer la ligne telle quelle au modèle lui fait reprendre **sa propre sortie précédente**
 * en guise de flux, et trois choses s'ensuivent, toutes muettes :
 *
 * 1. **Les attributs déjà comblés sont écartés comme « identiques à la source ».** Mesuré sur
 *    six produits en attente du parc : 3 à 5 attributs retenus en partant de la source, 0 à 2
 *    en partant de l'enrichi — `google_product_category`, `product_type`, `product_highlight`,
 *    `material` et `age_group` tombent systématiquement. Sur un produit Pébéo, **zéro** : on
 *    clique, ça réussit, et rien ne change. C'est le défaut tel qu'il a été signalé.
 * 2. **La liste des problèmes est celle de l'enrichi** — 13 au lieu de 20 sur le même produit :
 *    le modèle ne se voit même pas demander ce qui manque encore.
 * 3. ⚠️ **Et l'archive du flux est écrasée.** `enregistrerSuggestions` réécrit `productTitle`,
 *    `productDescription`, `productBrand` et `issues` à chaque passage. Le titre dérive donc à
 *    chaque régénération sans jamais revenir à ce que le marchand a écrit, et la colonne « Flux
 *    source » de la fiche finit par montrer l'IA à elle-même. Les deux premiers points se
 *    voient ; celui-là ne se répare pas.
 *
 * Le chemin de la liste appliquait la règle, la fiche non. Elle est donc **nommée une fois** :
 * cinq expressions `ligne.fluxSource || ligne` divergeaient déjà dans ce fichier, et c'est
 * l'une d'elles qui manquait.
 */
const sourceDuMarchand = (ligne) => (ligne ? (ligne.fluxSource || ligne) : ligne);

const CONSTAT_HORS_ANALYSE = {};

/**
 * La mention d'absence, accordée.
 *
 * « Disponibilité non relevé » se lit mal, et `libelle` ne porte pas le genre. Deux attributs
 * passent par ici : deux phrases écrites suffisent, là où un marqueur de genre dans la table
 * d'attributs serait beaucoup pour cela.
 */
const MENTION_NON_RELEVE = {
  price: 'Prix non relevé',
  availability: 'Disponibilité non relevée'
};

const TON_DISPONIBILITE = {
  in_stock: 'actif',
  out_of_stock: 'erreur',
  preorder: 'inactif',
  backorder: 'inactif',
  limited_availability: 'inactif'
};

/**
 * `readOnly` : consultation seule, pour un accès client à qui l'agence a ouvert l'outil.
 *
 * ⚠️ **Deux barrières, et il en faut deux.** Les affordances disparaissent de l'écran — on ne
 * propose pas un geste que le serveur refusera —, **et** chaque fonction d'écriture sort tôt.
 * Masquer seul ne suffit pas : une touche du clavier, un `Enter` dans un champ ou un chemin de
 * rendu oublié atteindraient encore la route. Le serveur tranche en dernier
 * (`feedOptimizerAccesClient.lectureSeule`), mais un 403 affiché en bandeau rouge se lit comme
 * une panne alors que rien n'est cassé.
 *
 * Même contrat que `CompetitorAdWatchTool`, qui a ouvert la voie.
 */
const FeedOptimizerTool = ({ token, selectedClientId, readOnly = false }) => {
  // États principaux
  const [activeTab, setActiveTab] = useState('analysis');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [feedLabels, setFeedLabels] = useState([]);
  const [selectedFeedLabel, setSelectedFeedLabel] = useState('');

  /**
   * La table d'attributs, servie par le backend.
   *
   * Les dix attributs de la fiche produit étaient écrits en dur **quatre fois** dans ce
   * fichier, plus un bloc de JSX de trente-cinq lignes recopié par attribut. Côté serveur,
   * quatre autres listes. Aucune ne dérivait d'une autre, et elles avaient divergé : `mpn` et
   * `product_type` étaient écrits dans le flux sans avoir de champ ici, `age_group` était
   * signalé sans pouvoir être corrigé.
   *
   * La source unique vit désormais dans `backend/src/constants/attributsMerchant.js` et
   * arrive par `GET /api/tools/feed-optimizer/attributs`. Un attribut ajouté là-bas apparaît
   * ici sans qu'on touche à ce composant.
   */
  const [attributs, setAttributs] = useState([]);
  /*
    Les attributs que la prochaine génération doit (re)faire — `null` veut dire « tous ».

    ⚠️ **`null` et non la liste complète**, et la distinction compte : une génération complète
    *remplace* les attributs en bloc, ce qui fait disparaître ceux que le modèle a retirés ;
    une génération partielle *fusionne*. Cocher les seize cases à la main ne doit donc pas
    équivaloir à ne rien cocher, et la route lit exactement cette différence.

    Mesuré : demander deux attributs au lieu des seize fait tomber le prompt de 5 606 jetons
    d'entrée à 1 251, et la réponse de 1 207 à 667.
  */
  const [attributsAGenerer, setAttributsAGenerer] = useState(null);
  /*
    La génération demandée, en attente de confirmation — `null` quand la modale est fermée.

    ⚠️ **C'est le bouton de génération qui l'ouvre**, et non un réglage à découvrir à côté. Le
    panneau replié vivait dans la barre : on le voyait sans savoir qu'il commandait le clic
    suivant, et il fallait l'avoir ouvert *avant* de lancer. Demander au moment du geste met le
    choix là où la décision se prend, et montre ce qu'on s'apprête à payer.

    Porte `{ produit }` depuis la fiche, `{ lot: true }` depuis la liste : c'est ce qui dit
    quelle génération confirmer.
  */
  const [generationADemander, setGenerationADemander] = useState(null);

  // Filtrer les comptes par client sélectionné
  const filteredAccounts = selectedClientId
    ? accounts.filter(account => account.clientId === selectedClientId)
    : accounts;

  // États d'analyse
  const [analysis, setAnalysis] = useState(null);
  /**
   * Quand les produits ont été lus chez Merchant, et par qui.
   *
   * Affiché en permanence : une analyse est une photo, et seule sa date dit si elle vaut encore
   * comme liste de travail. Sans elle, le relevé de 6 h 30 passerait pour l'état de l'instant.
   */
  const [analyzedAt, setAnalyzedAt] = useState(null);
  /**
   * Vrai dès que la question « ce flux a-t-il une analyse enregistrée ? » a reçu sa réponse.
   *
   * Sans lui, l'onglet Analyse n'a aucun moyen de distinguer « pas d'analyse » de « on ne sait
   * pas encore » : les deux valent `analysis === null`, et `loading` ne couvre que l'analyse
   * manuelle, pas la lecture en base qui la précède.
   */
  const [analyseCherchee, setAnalyseCherchee] = useState(false);
  const [analysisSource, setAnalysisSource] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);

  // États de suggestions
  const [suggestions, setSuggestions] = useState([]);
  /*
    La première réponse est-elle arrivée ?

    ⚠️ Sans ce drapeau, un filtre « En attente » posé avant la réponse vide la liste — et une
    liste vide se lit « rien n'est en attente », c'est-à-dire l'inverse de ce qu'on venait
    vérifier. Même raison que `analyseCherchee`, et même remède.
  */
  const [suggestionsChargees, setSuggestionsChargees] = useState(false);
  /*
    Les suggestions dont un changement de statut est parti, pas encore revenu.

    ⚠️ **Chaque `PUT /suggestions/:id` régénère tout le flux XML** (`regenerateFeedsForConfig`).
    Un double clic sur ✓ envoie donc deux écritures **et deux régénérations** sur un flux de
    plusieurs milliers de produits. Rien ne l'empêchait sur les cartes ; la ligne, plus facile
    à cliquer deux fois, le rendait probable.
  */
  const [statutEnVol, setStatutEnVol] = useState({});
  /**
   * Où va la marque dans les titres — le défaut du flux, et le réglage du produit ouvert.
   *
   * Deux états plutôt qu'un : régler un produit isolé ne doit pas déplacer le défaut du flux,
   * et le lot ne doit pas être prisonnier de ce qu'on a choisi sur le dernier produit ouvert.
   */
  // Le défaut d'affichage suit celui du serveur (`POSITION_MARQUE_PAR_DEFAUT`) : la marque en
  // fin de titre. Il n'est lu que le temps du premier chargement, qui apporte la vraie valeur.
  const [brandPosition, setBrandPosition] = useState('FIN');
  /**
   * L'orthographe de la marque dans les titres — « Pébéo » là où le flux dit « Pebeo ».
   *
   * Vide signifie « celle du flux source ». Le champ du lot ne touche que les produits de
   * cette marque-là : c'est le serveur qui tranche, un flux peut en compter plusieurs.
   */
  const [brandOverride, setBrandOverride] = useState('');

  /**
   * L'ordre des éléments du titre — le défaut du flux, et celui du produit ouvert.
   *
   * Deux états pour la même raison que la position de la marque : régler un produit isolé ne
   * doit pas déplacer le défaut du flux. `null` signifie « aucun gabarit », c'est-à-dire que
   * le modèle compose librement — le comportement d'origine, et le défaut.
   */
  const [titreGabarit, setTitreGabarit] = useState(null);
  const [productTitreGabarit, setProductTitreGabarit] = useState(null);
  const [applyingTitreGabarit, setApplyingTitreGabarit] = useState(false);
  /** Ce que donnerait le gabarit en cours, demandé au serveur — voir `rendreGabaritTitre`. */
  const [apercuTitre, setApercuTitre] = useState(null);

  /**
   * Le titre est-il en cours de retouche ?
   *
   * ⚠️ **La proposition de l'IA se lit, elle ne se corrige qu'à la demande.** Le champ et le
   * panneau d'éléments étaient posés en permanence sous le bloc vert : trois façons de régler
   * la même phrase, toutes visibles à la fois, et le champ partait du titre du **flux** quand
   * la proposition n'était pas acceptée — d'où des éléments qui paraissaient faux parce
   * qu'ils décrivaient l'autre titre.
   *
   * « Modifier » ouvre l'atelier ; sans lui, on lit et on tranche.
   */
  const [editionTitre, setEditionTitre] = useState(false);
  /**
   * Le dernier titre que le gabarit a posé dans le champ.
   *
   * ⚠️ **C'est le garde-fou contre l'écrasement d'une saisie.** Le gabarit écrit en direct
   * dans le champ Titre : sans ce témoin, il effacerait un titre retouché à la main dès qu'on
   * déplace une brique. On ne réécrit donc que si le champ contient encore ce que le gabarit
   * y avait mis — autrement dit si personne n'a tapé depuis.
   */
  const titrePoseParGabarit = useRef(null);

  /**
   * Le gabarit a-t-il été touché depuis l'ouverture de cette fiche ?
   *
   * ⚠️ **Sans ce témoin, ouvrir une fiche réécrirait son titre.** L'aperçu part dès que la
   * fiche s'ouvre, puisque le gabarit hérite du défaut du flux ; il écraserait alors le titre
   * du modèle sans que personne n'ait rien demandé. Le réglage agit **quand on y touche**,
   * pas quand on regarde.
   */
  const gabaritTouche = useRef(false);
  const [applyingBrandPosition, setApplyingBrandPosition] = useState(false);
  /*
    Où en est une génération découpée en lots — `null` quand il n'y en a qu'un.

    ⚠️ Sans lui, cocher cent produits laisse le bouton tourner trois minutes sans rien dire,
    et l'on reclique.
  */
  const [avancementGeneration, setAvancementGeneration] = useState(null);
  /** L'examen des galeries est en cours : il dure, et il se paie. */
  const [chercheAmbiances, setChercheAmbiances] = useState(false);

  // États pour le détail d'un produit
  const [selectedProductDetail, setSelectedProductDetail] = useState(null);
  /*
   * L'attribut dont on choisit une valeur dans une modale, ou `null`. Voir `choixDepuis` au
   * catalogue : c'est lui qui dit où puiser les propositions, pas cet écran.
   */
  const [choixVisuel, setChoixVisuel] = useState(null);

  const { survoler, quitter, bulle } = useBulleAuSurvol();

  const [productEdits, setProductEdits] = useState({});
  const [savingProduct, setSavingProduct] = useState(false);
  const [generatingForProduct, setGeneratingForProduct] = useState(false);
  /*
    ⚠️ **Il n'y a plus de place de marque propre à un produit**, et c'est pourquoi l'état qui
    la portait a disparu.

    `productBrandPosition` s'initialisait depuis `suggestion.brandPosition` et repartait tel
    quel à chaque « Regénérer avec l'IA ». Or les trois boutons qui le réglaient — Au début,
    À la fin, Sans marque — sont partis avec le panneau de marque : plus aucun geste ne
    pouvait le changer, et il gouvernait quand même. Une suggestion ayant gardé `DEBUT` d'un
    réglage ancien replaçait donc la marque en tête **à chaque régénération**, contre le
    défaut du flux, sans que rien ne l'affiche. Relevé en production sur Pébéo : la
    configuration dit `FIN`, six suggestions sur six portaient `DEBUT` ou s'en réclamaient.

    La place du produit se règle maintenant dans **son gabarit de titre**, par la brique
    « Marque ». Hors gabarit, c'est le défaut du flux qui vaut — et lui seul.
  */

  // États de flux XML
  const [feeds, setFeeds] = useState([]);
  const [newFeedName, setNewFeedName] = useState('');


  // États UI
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingFeedLabels, setLoadingFeedLabels] = useState(false);
  /*
    ⚠️ `generating` a disparu avec le bouton qu'il désarmait. Son rôle — « un traitement
    tourne » — est désormais porté par `fileGeneration.length > 0`, qui dit en plus combien
    il en reste. Garder un second drapeau pour la même chose, c'est se donner deux vérités.
  */

  /**
   * Les traitements demandés, dans l'ordre — le premier est celui qu'on exécute.
   *
   * ## ⚠️ Une file, et non des requêtes simultanées
   *
   * « Lancer plusieurs traitements en parallèle » a été demandé pour une raison précise : ne
   * plus être bloqué pendant qu'un lot tourne. C'est le besoin, et la file y répond.
   *
   * Les exécuter **réellement en même temps** n'y ajouterait rien et coûterait cher : le
   * plafond de débit d'Anthropic se compte par organisation, si bien que la durée totale est
   * fixée par le modèle et non par le transport — dix requêtes de front ne finiraient pas
   * plus tôt, elles fabriqueraient les 429 que le service passe son temps à éviter. Le
   * commentaire de `traiterUnLot` le disait déjà pour les lots d'une même demande ; c'est la
   * même raison entre demandes.
   *
   * ⚠️ **Un produit ne peut figurer que dans un seul traitement.** Deux demandes portant le
   * même produit le généreraient deux fois — la seconde écrasant la première par `upsert`,
   * pour des jetons payés deux fois et un résultat que rien n'explique. D'où le verrou, et
   * les cases désarmées sur les lignes qu'il tient.
   *
   * @type {[Array<{id: number, offerIds: string[]}>, Function]}
   */
  const [fileGeneration, setFileGeneration] = useState([]);

  /**
   * Les identifiants partis au serveur dans la requête **en vol**.
   *
   * Distincts de la file : une demande de cent produits se découpe en dix requêtes, et seule
   * celle qui voyage mérite « en cours ». Les quatre-vingt-dix autres attendent, et le dire
   * autrement ferait croire que cent appels au modèle tournent de front.
   */
  const [offerIdsEnVol, setOfferIdsEnVol] = useState([]);

  /**
   * Les lots soumis à la Message Batches API, et les produits qu'ils tiennent.
   *
   * ⚠️ **Distinct de la file locale, et pour une raison qui compte.** La file vit en mémoire
   * d'onglet : elle disparaît au rechargement. Un lot vit jusqu'à vingt-quatre heures côté
   * Anthropic, survit au rechargement, au redémarrage du serveur et au changement de
   * navigateur. Son verrou vient donc du serveur, pas d'ici.
   */
  const [lotsIA, setLotsIA] = useState([]);
  const [offerIdsEnLot, setOfferIdsEnLot] = useState([]);

  /*
    ⚠️ **Un verrou en `ref`, pas en état.** L'effet qui vide la file se déclenche à chaque
    changement de `fileGeneration` — y compris celui que la fin d'un traitement provoque. Un
    drapeau d'état arriverait un rendu trop tard et deux exécutions partiraient de front, ce
    que cette file existe précisément pour empêcher.
  */
  const traitementEnCours = useRef(false);

  /*
    Un identifiant par travail. ⚠️ Pas l'index : la file se vide par la tête, et retirer « le
    premier » après un ajout concurrent retirerait le mauvais.
  */
  const compteurTravaux = useRef(1);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterIssueType, setFilterIssueType] = useState(null);
  /*
   * ⚠️ **Le bloc « Problèmes les plus fréquents » est replié par défaut**, et son état n'est
   * pas mémorisé : vingt-quatre pastilles sur sept rangées séparaient les tuiles de la liste.
   * C'est un instrument de tri, pas un résumé — les quatre tuiles tiennent ce rôle.
   *
   * ⚠️ Il **ne se rouvre pas** quand un filtre est posé : on y arrive en cliquant une
   * pastille, donc il est déjà ouvert, et le forcer ferait resurgir sept rangées à chaque
   * clic. C'est le résumé qui porte le filtre actif, lisible sans ouvrir.
   */
  const [problemesOuverts, setProblemesOuverts] = useState(false);
  const [filterSuggestion, setFilterSuggestion] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  /*
    Les filtres de segment — marque, catégorie, type de produit, libellés.

    ⚠️ **Un seul état pour les deux onglets.** Analyse et Suggestions montrent le même
    catalogue sous deux angles : repartir d'un filtre vide en changeant d'onglet obligerait à
    le reposer, et deux états auraient divergé au premier axe ajouté.

    Une valeur par axe, `''` valant « tous ». Voir `backend/src/lib/segmentsProduit.js`, qui
    déclare les axes et décide lesquels valent la peine d'être rendus.
  */
  const [filtresSegment, setFiltresSegment] = useState({});

  // Pagination des produits
  const [productsCurrentPage, setProductsCurrentPage] = useState(1);
  /*
    ⚠️ **Cinquante, et non cent.** Une page de cent lignes fait près de six mille pixels de
    défilement : on perd l'en-tête des colonnes, la barre de filtres et le décompte dès la
    vingtième ligne. Cinquante tient en trois écrans, et le pied de liste reste atteignable.

    C'est aussi la taille du lot que la route synchrone accepte : cocher une page entière
    reste une demande que l'écran sait servir d'un coup.
  */
  const PRODUCTS_PER_PAGE = 50;

  /**
   * Le tri de la liste des produits.
   *
   * La liste suivait l'ordre de l'API, qui n'en est pas un, et n'offrait aucun tri — c'est le
   * reproche qui avait fait convertir les Sites, les Clients et les Comptes en lignes. Le défaut
   * est le score croissant : ce qu'on ouvre l'outil pour voir, c'est ce qui va le plus mal.
   */
  const [triProduits, setTriProduits] = useState({ cle: 'score', sens: 'asc' });

  // États pour le sélecteur de catégorie Google
  const [categorySearch, setCategorySearch] = useState('');
  const [categoryResults, setCategoryResults] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  // Chemin du niveau parcouru dans l'arborescence (null = catégories racines)
  const [categoryPath, setCategoryPath] = useState(null);
  // Vrai quand une recherche a renvoyé plus de résultats que la limite
  const [categoryTruncated, setCategoryTruncated] = useState(false);
  const [categoryNamesCache, setCategoryNamesCache] = useState({});

  /**
   * Les valeurs éditables d'un produit, sous leur nom d'attribut.
   *
   * Le produit porte ses champs en camelCase (`googleProductCategory`), l'édition et l'API
   * les nomment en snake_case (`google_product_category`). La table porte les deux, c'est
   * elle qui fait le passage — ces quatre correspondances étaient recopiées à la main, et
   * c'est le genre d'endroit où un attribut se perd sans bruit.
   */
  const valeursEditables = (product) => Object.fromEntries(
    attributs
      .filter(a => a.editable)
/*
 * ⚠️ **Le flux enrichi part du texte nettoyé, la colonne « Flux source » du brut.**
 *
 * Les descriptions des flux traversent souvent un back-office qui les a stockées en HTML,
 * puis un export qui n'a décodé qu'à moitié : 131 descriptions du parc portent « &nbsp »,
 * 1 691 des espaces multiples. Rien de cela ne demande un modèle, et le marchand n'a pas à
 * corriger un résidu d'encodage à la main dans le champ.
 *
 * `cleProduitNettoyee` n'est posée que là où un nettoyage existe — la description aujourd'hui.
 * Partout ailleurs la valeur brute est la bonne, et le `??` enchaîné le dit sans condition.
 */
      .map(a => [a.nom, product?.[a.cleProduitNettoyee] ?? product?.[a.cleProduit] ?? valeurVide(a)])
  );

  /** Ce qu'on envoie au serveur : les champs éditables, et eux seuls. */
  const attributsAEnvoyer = (champs) => Object.fromEntries(
    attributs.filter(a => a.editable).map(a => [a.nom, champs[a.nom]])
  );

  // Charger la table d'attributs (indépendante du compte : elle décrit l'outil, pas un flux)
  useEffect(() => {
    if (!token) return;
    let annule = false;

    axios.get(`${API_URL}/api/tools/feed-optimizer/attributs`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => { if (!annule && r.data?.success) setAttributs(r.data.attributs || []); })
      .catch(err => console.error('Erreur chargement table d\'attributs:', err));

    return () => { annule = true; };
  }, [token]);

  // Charger les comptes Merchant Center
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        setLoadingAccounts(true);
        const response = await axios.get(`${API_URL}/api/tools/feed-optimizer/accounts`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          // Trier les comptes par ordre alphabétique
          const sortedAccounts = [...(response.data.accounts || [])].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
          );
          setAccounts(sortedAccounts);
          // Ne pas auto-sélectionner le premier compte
        }
      } catch (err) {
        console.error('Erreur chargement comptes:', err);
        setError('Impossible de charger les comptes Merchant Center');
      } finally {
        setLoadingAccounts(false);
      }
    };

    if (token) {
      loadAccounts();
    }
  }, [token]);

  // Réinitialiser le compte sélectionné si le client change et que le compte n'est plus dans la liste filtrée
  // Auto-sélectionner si un seul compte disponible
  useEffect(() => {
    if (selectedClientId) {
      const clientAccounts = accounts.filter(acc => acc.clientId === selectedClientId);

      // Si un seul compte disponible, le sélectionner automatiquement
      if (clientAccounts.length === 1 && selectedAccount !== clientAccounts[0].id) {
        setSelectedAccount(clientAccounts[0].id);
      }
      // Si le compte sélectionné n'est plus valide, réinitialiser
      else if (selectedAccount && !clientAccounts.some(acc => acc.id === selectedAccount)) {
        setSelectedAccount('');
        setFeedLabels([]);
        setSelectedFeedLabel('');
        setAnalysis(null);
        setAnalyzedAt(null);
        setAnalysisSource(null);
        setProducts([]);
        setSuggestions([]);
        setFeeds([]);
      }
    }
  }, [selectedClientId, accounts, selectedAccount]);

  // Charger les feed labels quand un compte est sélectionné
  useEffect(() => {
    const loadFeedLabelsForAccount = async () => {
      if (!selectedAccount) {
        setFeedLabels([]);
        setSelectedFeedLabel('');
        return;
      }

      try {
        setLoadingFeedLabels(true);
        setError(null);
        // Réinitialiser les données précédentes
        setSelectedFeedLabel('');
        setAnalysis(null);
        setAnalyzedAt(null);
        setAnalysisSource(null);
        setProducts([]);
        setSuggestions([]);
        setFeeds([]);
        setFilterIssueType(null);

        console.log('🏷️ [FEED-LABELS] Chargement pour compte:', selectedAccount);

        const response = await axios.get(
          `${API_URL}/api/tools/feed-optimizer/feed-labels/${selectedAccount}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log('🏷️ [FEED-LABELS] Réponse:', response.data);

        if (response.data.success) {
          const availableLabels = response.data.feedLabels || [];
          setFeedLabels(availableLabels);
          console.log('🏷️ [FEED-LABELS] Labels trouvés:', availableLabels);
          // Auto-sélectionner FR si disponible, sinon le premier
          if (availableLabels.length > 0) {
            const defaultFeed = availableLabels.includes('FR') ? 'FR' : availableLabels[0];
            setSelectedFeedLabel(defaultFeed);
          } else {
            console.warn('🏷️ [FEED-LABELS] Aucun label trouvé');
          }
        } else {
          console.error('🏷️ [FEED-LABELS] Erreur API:', response.data.error);
          setError(messageErreur({ response: { data: response.data } }, 'Erreur lors du chargement des flux'));
        }
      } catch (err) {
        console.error('🏷️ [FEED-LABELS] Erreur:', err);
        /*
          ⚠️ Passé par la porte lui aussi : interpolé dans un gabarit, un objet n'aurait pas
          blanchi l'écran — il aurait écrit « [object Object] », ce qui est la version muette
          du même défaut. Et la forme `data?.error ||` se recopie : n'en laisser aucune.
        */
        setError(messageErreur(err, 'Erreur lors du chargement des flux du compte'));
        setFeedLabels([]);
      } finally {
        setLoadingFeedLabels(false);
      }
    };

    loadFeedLabelsForAccount();
  }, [selectedAccount, token]);

  // Analyser les produits
  const analyzeProducts = useCallback(async () => {
    if (readOnly) return;
    if (!selectedAccount) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `${API_URL}/api/tools/feed-optimizer/analyze`,
        {
          merchantId: selectedAccount,
          feedLabel: selectedFeedLabel
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAnalysis(response.data.analysis);
        setProducts(response.data.products || []);
        setAnalyzedAt(response.data.analyzedAt || null);
        setAnalysisSource(response.data.source || 'MANUEL');
        setSuccess(`${response.data.analysis.totalProducts} produits analysés`);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Erreur analyse:', err);
      setError('Erreur lors de l\'analyse des produits');
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, selectedFeedLabel, token]);

  /**
   * Exécuter un traitement : les produits désignés, par lots, l'un après l'autre.
   *
   * ⚠️ **Elle reçoit ses identifiants, elle ne lit plus la sélection.** La sélection est
   * vidée dès la mise en file — pour que l'utilisateur puisse en composer une autre — et s'y
   * fier ferait traiter la nouvelle à la place de l'ancienne.
   */
  const traiterUnLot = useCallback(async (offerIdsDemandes) => {
    if (readOnly) return;
    /*
      ⚠️ **Les compteurs vivent hors du `try`.** Déclarés dedans, le `catch` ne les voyait
      pas — et c'est pourtant là qu'ils comptent : une coupure au septième lot doit pouvoir
      annoncer les six premiers, enregistrés en base avant la panne.
    */
    let generees = 0;
    let echouees = 0;
    let ecartees = 0;

    if (!offerIdsDemandes?.length) return;

    try {
      setError(null);

      /*
        ⚠️ **On renvoie la SOURCE, jamais l'état enrichi.**

        Les produits reçus de `GET /analysis` portent, pour les lignes publiées, le contenu
        que le flux XML sert — c'est ce qui fait que la liste affiche la vraie note. Les
        renvoyer tels quels ferait repartir au modèle **sa propre sortie précédente** en
        guise de flux du marchand : le titre dériverait à chaque régénération sans jamais
        revenir à ce que le marchand a écrit, et `productTitle` — l'archive du titre
        d'origine — serait réécrit avec l'enrichi.

        ⚠️ `fluxSource` est posé dès qu'une suggestion a du contenu — **en attente comme
        publiée** —, et non sur les seules lignes publiées comme l'affirmait ce commentaire.
        C'est la régénération d'une ligne en attente qui est le cas courant, et c'est celui que
        la fiche manquait : voir `sourceDuMarchand`.
      */
      /*
        ⚠️ **Résolus au moment du traitement, pas de la mise en file.** Une analyse peut être
        rechargée entre-temps : un produit disparu du flux n'a plus rien à générer, et le
        garder en mémoire ferait envoyer au modèle une copie périmée.
      */
      const demandes = new Set(offerIdsDemandes.map(String));
      const productsToOptimize = products
        .filter(p => demandes.has(String(p.offerId)))
        .map(sourceDuMarchand);

      if (productsToOptimize.length === 0) {
        setError('Ces produits ne figurent plus dans l\'analyse : relancez-la avant de générer.');
        return;
      }

      /*
        ⚠️ **Découpé en lots, et séquentiellement.**

        En parallèle, dix requêtes de cinquante produits lanceraient cinq cents appels au
        modèle en même temps : le plafond de débit d'Anthropic se compte par organisation, et
        on fabriquerait les 429 que le service passe son temps à éviter. La durée totale est
        la même de toute façon — c'est le modèle qui la fixe, pas le transport.
      */
      let response = null;

      for (let i = 0; i < productsToOptimize.length; i += LOT_GENERATION) {
        const lot = productsToOptimize.slice(i, i + LOT_GENERATION);
        /*
          ⚠️ **Seul le lot qui voyage est « en cours ».** Les suivants attendent, et les
          griser de la même façon ferait croire que cent appels au modèle tournent de front.
        */
        setOfferIdsEnVol(lot.map(p => String(p.offerId)));
        /*
          ⚠️ **Toujours posé, même sous un lot.** Il ne l'était qu'au-delà de dix produits —
          le bouton suffisait à montrer qu'on travaillait. Il ne suffit plus : la sélection
          est vidée dès la mise en file, le bloc d'actions disparaît avec elle, et ce témoin
          devient le seul. Une génération d'un produit sans aucun signe à l'écran serait le
          reproche d'origine en plus petit.
        */
        setAvancementGeneration({ faits: i, total: productsToOptimize.length });

        response = await axios.post(
          `${API_URL}/api/tools/feed-optimizer/generate-suggestions`,
          {
            merchantId: selectedAccount,
            feedLabel: selectedFeedLabel,
            products: lot,
            brandPosition,
            titreGabarit,
            // ⚠️ `null` quand rien n'est coché : la route le lit comme « tous », et remplace
            // alors les attributs en bloc au lieu de fusionner. Voir `attributsAGenerer`.
            attributs: attributsAGenerer,
            // Choisis dans la barre du flux : ils valent pour les générations suivantes.
            setAsDefault: true
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.data.success) break;
        generees += response.data.generated || 0;
        echouees += response.data.failed || 0;
        ecartees += response.data.pairesEcartees || 0;
      }

      if (response && response.data.success) {
        /*
          ⚠️ **Les paires de questions/réponses écartées sont dites.**

          Chaque paire sort du modèle avec une citation du flux, vérifiée mécaniquement ; ce
          qui ne s'y retrouve pas est écarté. Un produit sans questions se lirait sinon comme
          un produit que le modèle n'a pas su servir — alors que c'est le contrôle qui a fait
          son travail, et c'est lui qui empêche une réponse inventée de partir sous la
          signature du marchand.
        */
        setSuccess(`${generees} suggestions générées`
          + (echouees ? ` · ${echouees} en échec` : '')
          + (ecartees ? ` · ${ecartees} question(s) écartée(s), faute d'être dans le flux` : ''));
        /*
          ⚠️ **La génération ne touche pas à la vue.** Elle posait le filtre « En attente » et
          revenait en page 1 — pour montrer ce qui venait d'être généré. C'était une bonne
          intention et un mauvais geste : on ne bloque plus l'écran *précisément* pour que
          l'utilisateur continue à travailler ailleurs, et lui déplacer sa liste sous les yeux
          à la fin d'un traitement qu'il avait oublié annule tout le bénéfice. Signalé :
          « à la fin du traitement tu filtres sur "en attente", or je ne veux pas que tu
          filtres ».

          Ce qui vient d'être généré se voit sans rien déplacer : la colonne « Suggestion »
          des lignes concernées passe à « En attente », le message dit combien, et la pastille
          de l'en-tête — elle, cliquable — propose le filtre à qui le veut. **On offre, on
          n'impose pas.**
        */
        // Voir `appliquerChangement` : les lignes générées montrent leur perspective aussitôt.
        appliquerChangement(response.data);
        loadSuggestions();
        setTimeout(() => setSuccess(null), 6000);
      }
    } catch (err) {
      console.error('Erreur génération:', err);
      /*
        ⚠️ **Le message du serveur est repris tel quel.** Il était écrasé par une phrase
        générique, si bien qu'un refus qui nommait sa cause — « Maximum 50 produits par
        requête » — arrivait comme « Erreur lors de la génération ». L'appelant ne pouvait
        pas s'en sortir, et c'est ce que fait déjà la recherche d'images d'ambiance.
      */
      /*
        ⚠️ **Ce qui est déjà enregistré se dit.** Chaque lot écrit ses suggestions avant de
        répondre : une coupure au septième lot laisse les six premiers en base. Annoncer un
        échec sec ferait tout relancer — et repayer ce qui était déjà fait.
      */
      setError(messageErreur(err, 'Erreur lors de la génération des suggestions')
        + (generees > 0
          ? ` · ${generees} suggestion(s) déjà enregistrée(s) : relancez sur le reste.`
          : ''));
      /*
        Les suggestions des lots aboutis existent : la liste doit les porter. Sans toucher
        à la vue, pour la raison dite au chemin de succès — à plus forte raison ici, où
        l'utilisateur vient de lire un message d'erreur et n'a pas besoin que sa liste bouge
        en même temps.
      */
      if (generees > 0) loadSuggestions();
    } finally {
      setAvancementGeneration(null);
      setOfferIdsEnVol([]);
    }
    /*
      `brandPosition` fait partie des dépendances : sans elle, la fonction garderait la
      position telle qu'elle était au dernier rendu mémoïsé et enverrait un réglage périmé.

      ⚠️ **`attributsAGenerer` pour la même raison**, et le compilateur l'a signalé : cocher
      trois attributs puis lancer aurait envoyé la sélection d'avant — le plus souvent `null`,
      c'est-à-dire « tous ». On aurait payé les seize attributs en croyant en demander trois,
      et écrasé le titre qu'on voulait garder.
    */
  }, [products, selectedAccount, selectedFeedLabel, token, brandPosition,
    attributsAGenerer]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Les produits qu'un traitement tient déjà — en vol ou en attente.
   *
   * ⚠️ **En vol ET en attente.** Ne verrouiller que le lot qui voyage laisserait resélectionner
   * les quatre-vingt-dix produits qui suivent : ils partiraient une seconde fois, la seconde
   * génération écrasant la première par `upsert`. Des jetons payés deux fois, et un résultat
   * que rien n'explique.
   */
  const offerIdsVerrouilles = useMemo(() => {
    const tenus = new Set(offerIdsEnVol.map(String));
    for (const travail of fileGeneration) {
      for (const id of travail.offerIds) tenus.add(String(id));
    }
    /*
      ⚠️ **Et ceux qu'un lot asynchrone tient.** Ce verrou-là vient du serveur : il survit au
      rechargement, là où la file locale disparaît. Sans lui, revenir sur l'écran pendant un
      lot de cinq mille produits les rendrait tous resélectionnables — et une seconde
      soumission écraserait la première par `upsert`, pour cent vingt dollars payés deux fois.
    */
    for (const id of offerIdsEnLot) tenus.add(String(id));
    return tenus;
  }, [fileGeneration, offerIdsEnVol, offerIdsEnLot]);

  /**
   * Mettre la sélection en file, et rendre la main tout de suite.
   *
   * ⚠️ **La sélection est vidée ici**, et c'est ce qui rend l'écran utilisable : sans cela on
   * composerait la suivante par-dessus l'ancienne, et le bouton compterait des produits déjà
   * partis. Les lignes verrouillées le disent, elles n'ont plus besoin d'être cochées.
   */
  const mettreEnFile = useCallback((offerIds) => {
    const aTraiter = (offerIds || []).map(String).filter(id => !offerIdsVerrouilles.has(id));
    if (aTraiter.length === 0) {
      setError('Ces produits sont déjà en cours de traitement.');
      return;
    }
    setFileGeneration(file => [...file, { id: compteurTravaux.current++, offerIds: aTraiter }]);
    setSelectedProducts(sel => sel.filter(id => !aTraiter.includes(String(id))));
  }, [offerIdsVerrouilles]);

  /*
    La file se vide d'elle-même, un traitement à la fois.

    ⚠️ **Le verrou est un `ref`, et l'effet se redéclenche à chaque changement de file** —
    y compris celui que la fin d'un traitement provoque en retirant son travail. Sans le
    `ref`, deux exécutions partiraient de front au prochain ajout, ce que cette file existe
    pour empêcher.
  */
  useEffect(() => {
    if (traitementEnCours.current || fileGeneration.length === 0) return;
    const travail = fileGeneration[0];
    traitementEnCours.current = true;
    (async () => {
      try {
        await traiterUnLot(travail.offerIds);
      } finally {
        traitementEnCours.current = false;
        // Retiré par identité : un autre travail a pu être ajouté entre-temps.
        setFileGeneration(file => file.filter(t => t.id !== travail.id));
      }
    })();
  }, [fileGeneration, traiterUnLot]);

  // Charger les suggestions
  /**
   * L'état des lots asynchrones, et ce qu'ils verrouillent.
   *
   * ⚠️ **La route reprend les lots terminés au passage.** Un cron les reprend aussi toutes
   * les dix minutes, mais quelqu'un qui ouvre l'écran juste après la fin d'un lot ne doit pas
   * attendre le prochain balayage pour voir ses suggestions. La reprise est idempotente.
   */
  const chargerLots = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      const reponse = await axios.get(
        `${API_URL}/api/tools/feed-optimizer/batches/${selectedAccount}/${selectedFeedLabel || ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!reponse.data.success) return;
      setLotsIA(reponse.data.lots || []);
      setOfferIdsEnLot(reponse.data.offerIdsEnLot || []);
    } catch (err) {
      // Un échec de lecture des lots ne doit pas priver l'écran du reste.
      console.error('Erreur lecture lots:', err);
    }
  }, [selectedAccount, selectedFeedLabel, token]);

  /*
    ⚠️ **Un lot en cours se sonde, et seulement lui.** Sans la condition, l'écran
    interrogerait le serveur toutes les trente secondes pour toujours, sur les onze douzièmes
    des flux qui n'ont aucun lot — c'est le défaut que le sondage de l'inventaire GTM a déjà
    rencontré.
  */
  useEffect(() => {
    if (!lotsIA.some(l => l.statut === 'EN_COURS')) return undefined;
    const minuterie = setInterval(() => { chargerLots(); loadSuggestions(); }, 30000);
    return () => clearInterval(minuterie);
  }, [lotsIA, chargerLots]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSuggestions = useCallback(async () => {
    if (!selectedAccount) return;

    try {
      /*
        ⚠️ **On boucle sur les pages.**

        La route paginait à 50 et l'écran ne demandait aucune page : un flux dont plus de
        cinquante produits avaient été générés perdait les suivants **en silence** — BZB en
        avait 43, sept d'une coupe invisible. Le plafond est monté à 1 000, mais s'y fier
        sans boucler ne ferait que déplacer le silence : un flux entièrement généré fait
        plusieurs milliers de suggestions.

        ⚠️ Et la boucle est **séquentielle** : les pages se demandent l'une après l'autre
        parce que `totalPages` n'est connu qu'après la première réponse.
      */
      const PAGE = 1000;
      let page = 1;
      let totalPages = 1;
      let toutes = [];
      let response = null;

      do {
        response = await axios.get(
          `${API_URL}/api/tools/feed-optimizer/suggestions/${selectedAccount}/${selectedFeedLabel || ''}`,
          {
            params: { page, limit: PAGE },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        if (!response.data.success) break;
        toutes = toutes.concat(response.data.suggestions || []);
        totalPages = response.data.totalPages || 1;
        page += 1;
      } while (page <= totalPages);

      if (response?.data.success) {
        setSuggestions(toutes);
        setSuggestionsChargees(true);
        if (response.data.brandPosition) {
          setBrandPosition(response.data.brandPosition);
        }
        setBrandOverride(response.data.brandOverride || '');
        setTitreGabarit(response.data.titreGabarit || null);
      }
    } catch (err) {
      console.error('Erreur chargement suggestions:', err);
    }
  }, [selectedAccount, selectedFeedLabel, token]);

  // Charger les flux XML
  const loadFeeds = useCallback(async () => {
    if (!selectedAccount) return;

    try {
      const response = await axios.get(
        `${API_URL}/api/tools/feed-optimizer/feeds/${selectedAccount}/${selectedFeedLabel || ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setFeeds(response.data.feeds || []);
      }
    } catch (err) {
      console.error('Erreur chargement flux:', err);
    }
  }, [selectedAccount, selectedFeedLabel, token]);

  /**
   * L'analyse déjà en base pour ce flux, s'il y en a une.
   *
   * Lecture seule et instantanée : elle évite de refaire dix secondes de lecture Merchant à
   * chaque passage dans l'outil, et sert ce que le cron de 6 h 30 a préparé pendant la nuit.
   */
  const loadStoredAnalysis = useCallback(async () => {
    if (!selectedAccount) return null;

    try {
      const response = await axios.get(
        `${API_URL}/api/tools/feed-optimizer/analysis/${selectedAccount}/${selectedFeedLabel || ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.data.success || !response.data.analysis) return null;

      setAnalysis(response.data.analysis);
      setProducts(response.data.products || []);
      setAnalyzedAt(response.data.analyzedAt || null);
      setAnalysisSource(response.data.source || null);
      return response.data;

    } catch (err) {
      // Une analyse absente n'est pas une panne : l'écran enchaîne sur la sienne.
      console.error('Erreur lecture analyse enregistrée:', err);
      return null;
    }
  }, [selectedAccount, selectedFeedLabel, token]);

  /*
   * À l'arrivée sur un flux : servir ce qu'on a, analyser sinon.
   *
   * On ne relance pas l'analyse quand une version enregistrée existe, même ancienne : sa date est
   * affichée, et c'est au lecteur de juger s'il veut la rafraîchir. Relancer d'office ferait
   * attendre dix secondes et dépenser onze appels Merchant à chaque va-et-vient entre les onglets.
   */
  useEffect(() => {
    if (!selectedAccount || !selectedFeedLabel) return undefined;

    let abandonne = false;
    // Tant que la lecture en base n'a pas répondu, l'écran ne peut rien conclure : sans ce
    // drapeau, « Ce flux n'a pas encore été analysé » s'affichait le temps d'un aller-retour,
    // y compris sur un flux qui avait bien une analyse enregistrée.
    setAnalyseCherchee(false);
    (async () => {
      /*
        ⚠️ **Les suggestions se chargent avec l'analyse, et non à l'ouverture d'un onglet.**

        Elles ne l'étaient que sur l'onglet Suggestions : la colonne « Suggestion » de la
        liste affichait donc « — » sur chaque ligne tant qu'on n'était pas allé voir ailleurs,
        et le tri par cette colonne ne triait rien. Les deux listes ayant fusionné, il n'y a
        plus d'onglet où accrocher ce chargement — et il n'y en avait pas besoin : les
        suggestions se comptent en dizaines, pas en milliers.
      */
      /*
        ⚠️ **Les suggestions sont vidées avant de recharger.**

        `products` l'était déjà au changement de flux, pas `suggestions` — parce que l'onglet
        les rechargeait à l'entrée. Or les `offerId` d'un même marchand **se recoupent entre
        flux** : FR et BE servent le même catalogue. Une liste périmée peindrait donc de vrais
        statuts sur les lignes du flux suivant, et un clic sur ✓ validerait la suggestion d'un
        autre flux.
      */
      setSuggestions([]);
      setSuggestionsChargees(false);
      /*
        ⚠️ **Changer de flux vide la file.** Elle porte des `offerId` du flux précédent, et
        les `offerId` d'un même marchand **se recoupent entre flux** — FR et BE servent le
        même catalogue. Les laisser partirait générer les produits du flux d'à côté, et
        écrirait leurs suggestions sous la configuration qu'on vient d'ouvrir.

        ⚠️ Le travail **en vol** n'est pas annulable — la requête voyage, et le serveur
        enregistrera ce qu'il a produit. On le laisse finir : ses suggestions appartiennent
        au flux d'où il est parti, et rien ne les y déplace.
      */
      setFileGeneration(file => file.slice(0, traitementEnCours.current ? 1 : 0));
      setLotsIA([]);
      setOfferIdsEnLot([]);
      const [enBase] = await Promise.all([
        loadStoredAnalysis(), loadSuggestions(), chargerLots()]);
      if (abandonne) return;
      setAnalyseCherchee(true);
      if (!enBase) analyzeProducts();
    })();

    return () => { abandonne = true; };
  }, [selectedAccount, selectedFeedLabel, loadStoredAnalysis, loadSuggestions, chargerLots]); // eslint-disable-line react-hooks/exhaustive-deps

  // Les flux XML restent chargés à l'ouverture de leur onglet : ils ne servent que là.
  useEffect(() => {
    if (activeTab === 'feeds') loadFeeds();
  }, [activeTab, loadFeeds]);

  /**
   * Remplacer dans la liste les produits que le serveur vient de recomposer.
   *
   * ⚠️ **Pourquoi le serveur les rend, et pourquoi on ne recharge pas l'analyse.**
   *
   * La note, les constats et le titre servi sont composés **à la lecture de l'analyse** :
   * valider une suggestion les change, mais l'écran ne rechargeait que la liste des
   * suggestions. Les pastilles de problèmes restaient à l'état d'avant, et il fallait
   * recharger la page — signalé.
   *
   * Recharger l'analyse à chaque clic est exclu : **15,8 Mo pour un flux de 3 568 produits,
   * 22,2 Mo pour 5 603** (mesuré). Le serveur rend donc les lignes touchées, et l'on ne
   * remplace que celles-là.
   *
   * ⚠️ **Remplacé, jamais fusionné.** Un `{ ...ancien, ...nouveau }` garderait les clés que
   * la nouvelle version n'a plus : un constat résolu resterait affiché, ce qui est exactement
   * le défaut qu'on corrige.
   */
  const appliquerChangement = useCallback((reponse) => {
    const lignes = reponse?.produits;
    if (Array.isArray(lignes) && lignes.length > 0) {
      const parOffre = new Map(lignes.map(l => [String(l.offerId), l]));
      setProducts(prev => prev.map(p2 => parOffre.get(String(p2.offerId)) || p2));
    }
    /*
      ⚠️ **Le résumé suit les lignes, sinon les tuiles mentent.**

      `ajusterResume` ne tournait que dans `GET /analysis` : générer ou valider remplaçait les
      lignes et laissait « Score moyen · grade F » sur sa valeur d'avant, et une pastille
      « Couleur manquante · 2 590 » n'ouvrait plus que les lignes qui le sont encore. Il fallait
      recharger la page — « il faut que je recharge la page pour voir les changements ».

      ⚠️ Une **fusion**, pas un remplacement : le résumé d'une mutation ne porte que les champs
      du résumé, là où `analysis` en porte d'autres (`analyzedAt`, `source`). Les écraser à
      `undefined` viderait la ligne « analysé le … » à chaque validation.

      ⚠️ Et `null` ne touche à rien : le serveur le rend quand la recomposition a échoué, sur
      une écriture qui, elle, a bien eu lieu.
    */
    if (reponse?.analysis) {
      setAnalysis(prev => (prev ? { ...prev, ...reponse.analysis } : prev));
    }
  }, []);

  // Valider une suggestion
  const validateSuggestion = async (suggestionId, status = 'VALIDATED') => {
    if (readOnly) return;
    if (statutEnVol[suggestionId]) return;
    setStatutEnVol(v => ({ ...v, [suggestionId]: true }));
    try {
      /*
        ⚠️ **Le statut seul part**, et c'est le bon contrat. Un `editedValues` local
        accompagnait l'appel, nourri par une édition en ligne qui n'avait **aucun
        déclencheur** depuis l'origine : la retouche se fait en fiche, qui écrit ses propres
        champs avant de valider. Garder le mélange laissait croire qu'un geste de ligne
        pouvait emporter des valeurs — alors qu'il n'en portait jamais.
      */
      const data = { status };

      const reponse = await axios.put(
        `${API_URL}/api/tools/feed-optimizer/suggestions/${suggestionId}`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // La ligne recomposée arrive avec la réponse : voir `appliquerChangement`.
      appliquerChangement(reponse.data);
      loadSuggestions();
    } catch (err) {
      console.error('Erreur validation:', err);
      setError('Erreur lors de la validation');
    } finally {
      // Dans un `finally` : un témoin resté posé après un échec désarmerait le bouton pour
      // toute la session, et l'on croirait la ligne figée.
      setStatutEnVol(v => {
        const suite = { ...v };
        delete suite[suggestionId];
        return suite;
      });
    }
  };

  // Valider en masse
  const bulkValidate = async (ids) => {
    if (readOnly) return;
    /*
      ⚠️ **La liste vient de l'appelant**, qui seul connaît le périmètre filtré. Deux gardes :
      celle-ci, et la condition de rendu du bouton — une condition de rendu dérive, pas un
      `return`.
    */
    if (!ids?.length) return;

    /*
      ⚠️ **Au-delà de cinquante, on confirme.** Le geste publie dans un flux en ligne, en un
      clic, sur un périmètre qu'on n'a pas forcément lu en entier — la liste est paginée à
      cent, et le bouton agit sur toutes les pages.
    */
    if (ids.length > 50 && !window.confirm(
      `Valider ${ids.length} suggestions ? Leurs titres et attributs partiront dans le flux XML.`
      + '\nChacune reste reprenable depuis sa ligne.'
    )) return;

    try {
      const reponse = await axios.post(
        `${API_URL}/api/tools/feed-optimizer/bulk-validate`,
        { suggestionIds: ids },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Les lignes recomposées arrivent avec la réponse : voir `appliquerChangement`.
      appliquerChangement(reponse.data);
      loadSuggestions();
      setSuccess(`${ids.length} suggestion(s) validée(s) — flux mis à jour`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Erreur validation en masse:', err);
      setError('Erreur lors de la validation');
    }
  };

  /**
   * Chercher les images d'ambiance dans les galeries du flux.
   *
   * ⚠️ **Le coût est annoncé avant d'être engagé.** Le même catalogue vaut quatre dollars chez
   * un marchand et cinquante-huit chez un autre, pour la seule raison que le second ne sait
   * pas servir de vignettes — un écart que rien à l'écran ne laisse deviner. L'estimation est
   * donc demandée d'abord, à une route qui n'appelle aucun modèle, et la confirmation porte le
   * chiffre.
   *
   * ⚠️ **Et rien n'est posé dans le flux.** L'examen dépose une *proposition* que la modale
   * « image d'ambiance » présente en tête de galerie, avec son niveau de confiance. C'est un
   * geste humain qui la retient — voir l'en-tête de `services/feedOptimizerAmbiance.js`.
   */
  const chercherAmbiances = async () => {
    if (readOnly) return;
    try {
      setChercheAmbiances(true);
      setError(null);

      const estim = await axios.get(
        `${API_URL}/api/tools/feed-optimizer/ambiance/${selectedAccount}/${selectedFeedLabel || ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const e = estim.data || {};

      if (e.analyseAbsente || !e.jeux) {
        setError('Analysez le flux avant de chercher les images d\'ambiance.');
        return;
      }
      if (!e.suggestions) {
        setError('Générez d\'abord des suggestions : la proposition s\'y attache.');
        return;
      }

      /*
       * ⚠️ Le décompte des images en pleine résolution est **nommé** quand il y en a : c'est
       * la seule chose qui explique une facture hors norme, et la cause est la plateforme du
       * marchand, pas le volume du catalogue. Sans elle on cherche du mauvais côté.
       */
      const horsVignette = e.imagesPleineResolution
        ? `\n⚠️ ${e.imagesPleineResolution} image(s) partiront en pleine résolution : `
          + 'ce marchand ne sert pas de vignettes, et elles coûtent une quinzaine de fois plus.'
        : '';
      const dejaVues = e.produitsDejaExamines
        ? `\n${e.produitsDejaExamines} produit(s) ont déjà été examinés et seront laissés de côté.`
        : '';

      const feuVert = window.confirm(
        `Examiner ${e.jeux} galerie(s), soit ${e.images} image(s).\n`
        + `Environ ${Math.round(e.jetons / 1000)} k jetons.${horsVignette}${dejaVues}\n\n`
        + 'Rien ne sera écrit dans le flux : les vues repérées seront proposées dans la fiche.'
      );
      if (!feuVert) return;

      const r = await axios.post(
        `${API_URL}/api/tools/feed-optimizer/ambiance`,
        { merchantId: selectedAccount, feedLabel: selectedFeedLabel || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = r.data || {};

      if (!d.jeuxExamines) {
        setSuccess(d.note || 'Aucune galerie à examiner.');
      } else {
        /*
         * ⚠️ Les trois nombres sont dits, y compris les échecs. « 34 vues repérées » seul
         * laisserait croire que le reste n'en a pas, alors qu'une partie n'a pas pu être
         * regardée — et ces galeries-là restent à reprendre.
         */
        setSuccess(
          `${d.propositions} vue(s) en situation proposée(s) sur ${d.jeuxExamines} galerie(s)`
          + ` · ${d.sansAmbiance} sans ambiance`
          + (d.echecs ? ` · ${d.echecs} non examinée(s), à relancer` : '')
        );
        await loadSuggestions();
      }
      setTimeout(() => setSuccess(null), 8000);
    } catch (err) {
      console.error('Erreur examen des galeries:', err);
      setError(messageErreur(err, 'Erreur lors de l\'examen des galeries'));
    } finally {
      setChercheAmbiances(false);
    }
  };

  // Créer un flux XML
  const createFeed = async () => {
    if (readOnly) return;
    if (!newFeedName.trim()) {
      setError('Nom du flux requis');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/tools/feed-optimizer/create-feed`,
        {
          merchantId: selectedAccount,
          feedLabel: selectedFeedLabel,
          name: newFeedName
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setNewFeedName('');
        loadFeeds();
        setSuccess('Flux créé avec succès');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Erreur création flux:', err);
      setError('Erreur lors de la création du flux');
    }
  };

  // Copier l'URL du flux
  const copyFeedUrl = (url) => {
    navigator.clipboard.writeText(url);
    setSuccess('URL copiée');
    setTimeout(() => setSuccess(null), 2000);
  };

  /**
   * L'orthographe qui s'applique à un produit, par ordre de précision.
   *
   * 1. celle posée sur ce produit — un réglage individuel l'emporte toujours ;
   * 2. celle du flux, qui est précisément un défaut ;
   * 3. la marque telle qu'elle est dans le flux source.
   *
   * Le deuxième maillon manquait : une fiche ouverte sur un flux réglé à « Pébéo » affichait
   * « Pebeo », et le réglage du flux semblait n'avoir servi à rien. Une seule fonction pour les
   * trois points qui en avaient besoin — les deux ouvertures et la détection de changement — car
   * c'est de leur divergence que venait le défaut.
   */
  const graphieDuProduit = (suggestion = productEdits._suggestion, produit = selectedProductDetail) => {
    if (suggestion?.brandLabel) return suggestion.brandLabel;

    const source = suggestion?.productBrand || produit?.brand || '';

    // L'orthographe du flux ne vaut que pour les produits de cette marque-là : c'est la règle
    // qu'applique déjà le serveur (`memeMarque`), et l'ignorer ici afficherait « Pébéo » sur la
    // fiche d'un produit Nike d'un flux multi-marques.
    if (brandOverride && memeMarque(brandOverride, source)) return brandOverride;

    return source;
  };

  // Ouvrir le détail d'un produit
  const openProductDetail = (product) => {
    // Trouver la suggestion existante pour ce produit
    const existingSuggestion = suggestions.find(s => s.offerId === product.offerId);

    // Initialiser les éditions avec les valeurs actuelles
    const editions = {
      // Les champs éditables viennent de la table : voir `attributs` plus haut.
      ...valeursEditables(product),
      // Garder trace des recommandations acceptées/refusées
      _acceptedFields: {},
      _suggestion: existingSuggestion
    };
    setProductEdits(editions);
    // L'état de départ, pour que la barre de la fiche sache si une saisie est en attente.
    empreinteALOuverture.current = empreinteEditions(editions);
    // Changer de produit remet le témoin à zéro : le titre du précédent n'a rien à dire sur
    // celui-ci, et le garder bloquerait la réécriture sur toute la fiche suivante.
    titrePoseParGabarit.current = null;
    gabaritTouche.current = false;
    // Changer de produit referme la retouche : on revient à la lecture.
    setEditionTitre(false);
    setProductTitreGabarit(existingSuggestion?.titreGabarit || titreGabarit);
    // Réinitialiser le sélecteur de catégorie
    setCategorySearch('');
    setCategoryResults([]);
    setShowCategoryDropdown(false);
    setCategoryPath(null);
    setSelectedProductDetail(product);
  };

  // Fermer le détail du produit
  const closeProductDetail = () => {
    setSelectedProductDetail(null);
    setProductEdits({});
    setCategorySearch('');
    setCategoryResults([]);
    setShowCategoryDropdown(false);
    setCategoryPath(null);
  };

  // Ouvrir la modal depuis une suggestion

  // Générer des suggestions pour un seul produit
  const generateSuggestionsForProduct = async (product) => {
    if (readOnly) return;
    try {
      setGeneratingForProduct(true);
      setError(null);

      const response = await axios.post(
        `${API_URL}/api/tools/feed-optimizer/generate-suggestions`,
        {
          merchantId: selectedAccount,
          feedLabel: selectedFeedLabel,
          products: [product],
          // Le même choix qu'en lot : la fiche n'a pas son propre réglage, et en avoir un
          // ferait deux listes à tenir d'accord pour un geste qui est le même.
          attributs: attributsAGenerer,
          /*
            ⚠️ **Le défaut du flux, et non une valeur propre au produit.** Celle-ci venait de
            la suggestion elle-même et s'y reconduisait sans fin : une suggestion marquée
            `DEBUT` par un réglage que l'écran n'offre plus regénérait la marque en tête, quoi
            que dise le flux. La route ne retient ceci comme nouveau défaut que sur
            `setAsDefault`, que cet appel n'envoie pas.
          */
          brandPosition
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess('Suggestions générées');
        // Voir `appliquerChangement` : la perspective « ⟶ N % si validé » apparaît aussitôt.
      appliquerChangement(response.data);
      await loadSuggestions();

        // Recharger la suggestion pour ce produit
        const updatedResponse = await axios.get(
          `${API_URL}/api/tools/feed-optimizer/suggestions/${selectedAccount}/${selectedFeedLabel || ''}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (updatedResponse.data.success) {
          setSuggestions(updatedResponse.data.suggestions || []);
          const newSuggestion = updatedResponse.data.suggestions?.find(s => s.offerId === product.offerId);
          if (newSuggestion) {
            setProductEdits(prev => ({
              ...prev,
              _suggestion: newSuggestion
            }));
          }
        }
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Erreur génération:', err);
      setError('Erreur lors de la génération des suggestions');
    } finally {
      setGeneratingForProduct(false);
    }
  };

  /**
   * Déplace la marque dans les titres déjà suggérés.
   *
   * Sans `suggestionIds`, le flux entier est repris et la position devient son défaut ; avec,
   * seul le produit visé bouge. Aucun appel à l'IA : le serveur réécrit les titres, ce qui rend
   * le geste immédiat sur une sélection de plusieurs centaines et réversible en rebasculant.
   */
  const appliquerPositionMarque = async (position, suggestionIds = null, graphie = undefined) => {
    if (readOnly) return;
    try {
      setApplyingBrandPosition(true);
      setError(null);

      const response = await axios.post(
        `${API_URL}/api/tools/feed-optimizer/brand-position`,
        {
          merchantId: selectedAccount,
          feedLabel: selectedFeedLabel,
          brandPosition: position,
          ...(graphie === undefined ? {} : { brandLabel: graphie }),
          ...(suggestionIds ? { suggestionIds } : { setAsDefault: true })
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.data.success) return null;

      const { modified, skippedWithoutBrand } = response.data;
      // Dire ce qui n'a pas bougé : sans marque connue, un titre reste en l'état, et le silence
      // ferait croire à un réglage sans effet.
      const reecrits = `${modified} titre${modified > 1 ? 's' : ''} réécrit${modified > 1 ? 's' : ''}`;
      setSuccess(
        skippedWithoutBrand > 0
          ? `${reecrits}, ${skippedWithoutBrand} sans marque renseignée`
          : reecrits
      );
      setTimeout(() => setSuccess(null), 4000);

      return response.data;
    } catch (err) {
      console.error('Erreur position de marque:', err);
      setError('Erreur lors du déplacement de la marque dans les titres');
      return null;
    } finally {
      setApplyingBrandPosition(false);
    }
  };

  /**
   * L'orthographe de la marque dans le titre, prise du champ **Marque** de la fiche.
   *
   * ⚠️ **Elle avait son propre champ, et c'était un doublon qui pouvait se contredire.** La
   * fiche porte déjà « Marque [brand] », qui est ce qui part dans `<g:brand>` ; une seconde
   * case « Orthographe » réglait la même chose pour `<g:title>`, et rien n'empêchait d'y
   * écrire deux graphies différentes. Google attend justement que les deux concordent.
   *
   * Le champ de l'attribut fait donc foi : écrire « Pébéo » là réécrit le titre.
   *
   * ⚠️ Appelé au repos du champ, jamais à la frappe — réécrire le titre à la lettre près
   * ferait un aller-retour serveur par caractère tapé. C'est la règle qui valait déjà pour
   * l'ancien champ.
   */
  const validerGraphieDuProduit = () => {
    const saisie = String(productEdits.brand || '').trim();
    if (!saisie || saisie === graphieDuProduit()) return;
    changerPositionMarqueDuProduit(brandPosition, saisie);
  };

  /**
   * Réécrit le titre de la fiche ouverte, sans rappeler l'IA.
   *
   * ⚠️ **Son seul appelant est la correction d'orthographe de la marque.** La *place* ne se
   * règle plus par produit — elle appartient au gabarit de titre, ou au défaut du flux —, et
   * `position` ne sert donc ici qu'à **conserver celle qui s'applique déjà** pendant qu'on
   * change la graphie. Lui passer autre chose déplacerait la marque au détour d'une
   * correction de « Pebeo » en « Pébéo ».
   */
  const changerPositionMarqueDuProduit = async (position, graphie = undefined) => {
    const suggestion = productEdits._suggestion;
    // Sans suggestion, il n'y a pas encore de titre à réécrire : le choix servira de consigne
    // à la prochaine génération.
    if (!suggestion) return;

    const resultat = await appliquerPositionMarque(position, [suggestion.id], graphie);
    if (!resultat) return;

    const rafraichies = await axios.get(
      `${API_URL}/api/tools/feed-optimizer/suggestions/${selectedAccount}/${selectedFeedLabel || ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (rafraichies.data.success) {
      setSuggestions(rafraichies.data.suggestions || []);
      const aJour = rafraichies.data.suggestions?.find(s => s.offerId === suggestion.offerId);
      if (aJour) {
        setProductEdits(prev => ({
          ...prev,
          _suggestion: aJour,
          // Le champ manuel suit le titre réécrit, sauf s'il a été refusé : on ne ressuscite
          // pas une recommandation que l'utilisateur vient d'écarter.
          title: prev._acceptedFields?.title === 'rejected'
            ? prev.title
            : (aJour.editedTitle || aJour.suggestedTitle || prev.title)
        }));
      }
    }
  };

  // Accepter une recommandation pour un champ
  const acceptRecommendation = (field, value) => {
    setProductEdits(prev => ({
      ...prev,
      [field]: value,
      _acceptedFields: {
        ...prev._acceptedFields,
        [field]: 'accepted'
      }
    }));
  };

  // Refuser une recommandation (garder la valeur actuelle)
  const rejectRecommendation = (field) => {
    // Voir `accepterRecommandation` : un verdict referme l'atelier.
    if (field === 'title') setEditionTitre(false);

    setProductEdits(prev => ({
      ...prev,
      /*
        ⚠️ **Refuser rend la valeur du FLUX.** Pour une ligne publiée,
        `selectedProductDetail` porte l'état enrichi : refuser y aurait reposé
        l'enrichissement précédent au lieu de ce que le marchand a écrit — un refus qui ne
        refuse rien.
      */
      [field]: sourceDuMarchand(selectedProductDetail)[field] || '',
      _acceptedFields: {
        ...prev._acceptedFields,
        [field]: 'rejected'
      }
    }));
  };

  // Modifier manuellement un champ
  const updateProductField = (field, value) => {
    setProductEdits(prev => ({
      ...prev,
      [field]: value,
      _acceptedFields: {
        ...prev._acceptedFields,
        [field]: 'manual'
      }
    }));
  };

  // Rechercher des catégories Google.
  // Deux modes : recherche à plat dès 2 caractères, sinon navigation dans l'arborescence
  // (les enfants directs de `parentPath`, ou les 21 catégories racines à la ouverture).
  const searchCategories = useCallback(async (searchValue, parentPath = null) => {
    const hasTerm = searchValue && searchValue.length >= 2;

    try {
      setLoadingCategories(true);
      const response = await axios.get(
        `${API_URL}/api/tools/feed-optimizer/google-taxonomy`,
        {
          params: hasTerm
            ? { search: searchValue, limit: 50 }
            : { ...(parentPath ? { parent: parentPath } : {}) },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setCategoryResults(response.data.categories || []);
        setCategoryTruncated(Boolean(response.data.truncated));
      }
    } catch (err) {
      console.error('Erreur recherche catégories:', err);
      setCategoryResults([]);
      setCategoryTruncated(false);
    } finally {
      setLoadingCategories(false);
    }
  }, [token]);

  // Debounce pour la recherche de catégories.
  // Ne se déclenche qu'une fois la liste ouverte, pour ne pas interroger l'API au montage
  // du composant. En navigation (sans terme saisi), on charge sans délai.
  useEffect(() => {
    if (!showCategoryDropdown) return undefined;

    const hasTerm = categorySearch && categorySearch.length >= 2;
    const timer = setTimeout(
      () => searchCategories(categorySearch, categoryPath),
      hasTerm ? 300 : 0
    );

    return () => clearTimeout(timer);
  }, [categorySearch, searchCategories, showCategoryDropdown, categoryPath]);

  // Descendre d'un niveau dans l'arborescence
  const openCategoryLevel = (category) => {
    // La saisie et la navigation s'excluent : entrer dans un niveau efface la recherche.
    setCategorySearch('');
    setCategoryPath(category.name);
  };

  // Remonter à un niveau donné du fil d'Ariane (null = racine)
  const goToCategoryLevel = (path) => {
    setCategorySearch('');
    setCategoryPath(path);
  };

  // Sélectionner une catégorie
  const selectCategory = (category) => {
    updateProductField('google_product_category', category.id);
    setCategorySearch(category.name);
    setShowCategoryDropdown(false);
    setCategoryResults([]);
    setCategoryPath(null);
    // Mettre en cache le nom de la catégorie
    setCategoryNamesCache(prev => ({ ...prev, [category.id]: category.name }));
  };

  // Récupérer le nom d'une catégorie par son ID
  const fetchCategoryName = async (categoryId) => {
    if (!categoryId) return null;

    try {
      const response = await axios.get(
        `${API_URL}/api/tools/feed-optimizer/google-taxonomy`,
        {
          params: { id: categoryId },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success && response.data.categories?.length > 0) {
        return response.data.categories[0].name;
      }
    } catch (err) {
      console.error('Erreur récupération nom catégorie:', err);
    }
    return null;
  };

  // Afficher le libellé de la catégorie déjà enregistrée à l'ouverture de la fiche.
  // Le champ ne montre que la saisie : sans cela il paraîtrait vide alors qu'une
  // catégorie est bien associée au produit.
  useEffect(() => {
    const categoryId = productEdits.google_product_category;
    if (!selectedProductDetail || !categoryId || categorySearch) return;

    let cancelled = false;
    const cached = categoryNamesCache[categoryId];

    if (cached) {
      setCategorySearch(cached);
    } else {
      fetchCategoryName(categoryId).then(name => {
        if (cancelled || !name) return;
        setCategoryNamesCache(prev => ({ ...prev, [categoryId]: name }));
        setCategorySearch(name);
      });
    }

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductDetail, productEdits.google_product_category]);

  // Charger le nom de la catégorie recommandée quand une suggestion existe
  useEffect(() => {
    const loadRecommendedCategoryName = async () => {
      const recommendedCategoryId = productEdits._suggestion?.suggestedAttributes?.google_product_category;
      if (recommendedCategoryId && !categoryNamesCache[recommendedCategoryId]) {
        const name = await fetchCategoryName(recommendedCategoryId);
        if (name) {
          setCategoryNamesCache(prev => ({ ...prev, [recommendedCategoryId]: name }));
        }
      }
    };
    loadRecommendedCategoryName();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productEdits._suggestion?.suggestedAttributes?.google_product_category]);

  // Sauvegarder les modifications du produit
  const saveProductChanges = async () => {
    if (readOnly) return;
    if (!selectedProductDetail) return;

    try {
      setSavingProduct(true);
      setError(null);

      // Préparer les données à sauvegarder
      const { _acceptedFields, _suggestion, ...fieldsToSave } = productEdits;

      // Si une suggestion existe, la mettre à jour
      let reponse;
      if (_suggestion) {
        reponse = await axios.put(
          `${API_URL}/api/tools/feed-optimizer/suggestions/${_suggestion.id}`,
          {
            status: 'MODIFIED',
            editedTitle: fieldsToSave.title,
            editedDescription: fieldsToSave.description,
            /*
              ⚠️ Le gabarit du produit part avec le reste : il passait par un bouton
              « Appliquer » séparé, qui réécrivait en base un titre que le champ portait déjà,
              et qui ne faisait rien dès que ce titre avait été retouché à la main.

              `null` dit « suit le gabarit du flux » — c'est ce qu'envoie le bouton de retour
              au défaut, et c'est la valeur d'un produit qu'on n'a jamais réglé à part.
            */
            titreGabarit: gabaritTouche.current ? (gabaritEffectif || null) : undefined,
            // Les attributs envoyés viennent de la table, pas d'une liste recopiée : un
            // attribut ajouté côté serveur est enregistré sans modification ici.
            editedAttributes: attributsAEnvoyer(fieldsToSave)
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Créer une nouvelle suggestion avec les modifications manuelles
        reponse = await axios.post(
          `${API_URL}/api/tools/feed-optimizer/create-manual-suggestion`,
          {
            merchantId: selectedAccount,
            feedLabel: selectedFeedLabel,
            offerId: selectedProductDetail.offerId,
            /*
              ⚠️ **L'archive est celle du FLUX**, et le commentaire ci-dessous le disait déjà
              sans que le code puisse le tenir : pour une ligne publiée,
              `selectedProductDetail` porte l'état enrichi. Le point de comparaison serait
              devenu l'enrichissement précédent, et la fiche aurait opposé une proposition à
              elle-même.
            */
            productTitle: sourceDuMarchand(selectedProductDetail).title,
            // Le texte du flux, pas celui qu'on enregistre : `editedDescription` juste en
            // dessous est la correction, celle-ci reste le point de comparaison.
            productDescription:
              sourceDuMarchand(selectedProductDetail).description,
            imageLink: selectedProductDetail.imageLink,
            editedTitle: fieldsToSave.title,
            editedDescription: fieldsToSave.description,
            // Les attributs envoyés viennent de la table, pas d'une liste recopiée : un
            // attribut ajouté côté serveur est enregistré sans modification ici.
            editedAttributes: attributsAEnvoyer(fieldsToSave)
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // La ligne recomposée arrive avec la réponse : voir `appliquerChangement`.
        appliquerChangement(reponse.data);
        await loadSuggestions();
      // Recharger les flux pour mettre à jour la date de dernière modification
      await loadFeeds();

      /*
        La fiche reste ouverte.

        Elle se refermait, et c'était cohérent du temps de la modale : enregistrer était le
        dernier geste. Avec la navigation produit par produit, refermer obligerait à retrouver sa
        ligne parmi cent pour passer au suivant — alors que le bouton « Suivant » est juste à côté
        de « Enregistrer ».

        La suggestion portée par la fiche est réalignée sur celle que le serveur vient de créer ou
        de mettre à jour : sans cela, le bouton continuerait d'annoncer « Générer » sur un produit
        qui a désormais une suggestion, et le statut resterait absent de l'en-tête.
      */
      const enregistree = reponse?.data?.suggestion;
      if (enregistree) {
        setProductEdits(prev => ({ ...prev, _suggestion: enregistree }));
      }
      // Ce qui vient d'être enregistré n'est plus « en attente ».
      empreinteALOuverture.current = empreinteEditions(productEdits);

      setSuccess('Modifications enregistrées — flux mis à jour');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSavingProduct(false);
    }
  };

  // Obtenir la recommandation pour un champ
  /**
   * Une proposition identique à la source n'est pas une proposition.
   *
   * ⚠️ **Jumeau de `backend/src/lib/memeValeur.js`**, et les deux doivent rester d'accord —
   * même parti que `segmentsProduit`, pour la même raison : l'écran ne peut pas charger un
   * module CommonJS du serveur. `backend/scripts/test-proposition-identique.js` confronte les
   * deux implémentations sur les mêmes jeux.
   *
   * Deux subtilités, et chacune décide du résultat :
   *
   * 1. **La casse ne se replie que sur une liste fermée** (`widget: 'choix'`). Partout
   *    ailleurs la casse **est** la proposition — « CERAMIC 45 ML » → « Ceramic 45 ml » est
   *    précisément le travail de cet outil, et replier la casse le refuserait en silence.
   * 2. **Les clés d'un objet se trient avant comparaison** : la valeur relue vient d'une
   *    colonne `jsonb`, où Postgres réordonne les clés par longueur puis octet.
   *
   * ⚠️ **L'ordre d'un tableau, lui, compte** : `product_type` déclare une hiérarchie dont le
   * premier chemin est le principal. Une proposition qui ne fait que réordonner en est une.
   */
  const formeComparable = (valeur, replierCasse) => {
    if (valeur === undefined || valeur === null) return null;
    if (typeof valeur === 'string') {
      const t = valeur.trim();
      return replierCasse ? t.toLowerCase() : t;
    }
    const vide = (v) => v === undefined || v === null
      || (typeof v === 'string' && v.trim() === '')
      || (Array.isArray(v) && v.length === 0);
    if (Array.isArray(valeur)) {
      return valeur.filter(v => !vide(v)).map(v => formeComparable(v, replierCasse));
    }
    if (typeof valeur === 'object') {
      const trie = {};
      for (const cle of Object.keys(valeur).sort()) {
        if (vide(valeur[cle])) continue;
        trie[cle] = formeComparable(valeur[cle], replierCasse);
      }
      return trie;
    }
    return valeur;
  };

  const memeValeurQueLaSource = (proposition, source, listeFermee) => {
    const vide = (v) => v === undefined || v === null
      || (typeof v === 'string' && v.trim() === '')
      || (Array.isArray(v) && v.length === 0);
    if (vide(source) || vide(proposition)) return false;
    return JSON.stringify(formeComparable(proposition, Boolean(listeFermee)))
      === JSON.stringify(formeComparable(source, Boolean(listeFermee)));
  };

  /**
   * La proposition du modèle pour un champ — ou `null` si elle ne propose rien.
   *
   * ⚠️ **Le filtre de génération ne suffit pas, et c'est la leçon de cet incident.** Une
   * recommandation est une **comparaison**, et ses deux côtés sont lus à des moments
   * différents : la génération compare à ce que la source disait **alors**, l'écran à ce
   * qu'elle dit **maintenant**.
   *
   * Mesuré : 14 des 23 `age_group` proposés recopient aujourd'hui la source, et **tous**
   * viennent de suggestions antérieures à l'analyse courante — le flux a changé depuis, ou
   * c'est notre propre normalisation des énumérations qui a rapproché les deux
   * (`ADULT` → `adult`). Aucun filtre posé à la génération ne pouvait le savoir.
   *
   * C'est donc ici que la garantie tient, et c'est ici qu'elle couvre les suggestions déjà
   * enregistrées.
   */
  const getRecommendationForField = (field) => {
    const suggestion = productEdits._suggestion;
    if (!suggestion) return null;

    const proposition = field === 'title' ? suggestion.suggestedTitle
      : field === 'description' ? suggestion.suggestedDescription
        : suggestion.suggestedAttributes?.[field];

    if (proposition === undefined || proposition === null) return null;

    /*
     * ⚠️ **La comparaison porte sur le produit affiché**, c'est-à-dire sur ce que la colonne
     * « Flux source » montre — et non sur l'archive de la suggestion. Comparer à l'archive
     * reproduirait exactement le défaut : elle fige la source au jour de la génération.
     */
    const attribut = attributs.find(a => a.nom === field);
    /*
     * ⚠️ **La comparaison porte sur le FLUX**, et `selectedProductDetail` ne l'est plus pour
     * une ligne publiée : il porte l'état enrichi. S'y fier ferait écarter une proposition
     * parce qu'elle est identique… à l'enrichissement précédent, c'est-à-dire à elle-même.
     */
    const duFlux = sourceDuMarchand(selectedProductDetail);
    const source = field === 'title' ? duFlux?.title
      : field === 'description' ? duFlux?.description
        : duFlux?.[attribut?.cleProduit];

    if (memeValeurQueLaSource(proposition, source, attribut?.widget === 'choix')) return null;

    return proposition;
  };

  // Vérifier si un champ a un problème
  const getIssueForField = (field) => {
    if (!selectedProductDetail) return null;
    return selectedProductDetail.issues?.find(i => i.attribute === field);
  };

  /*
    ⚠️ **Déclarés AVANT `filteredProducts`, qui les appelle.**

    Ils vivaient plus bas, avec les autres fonctions de rendu : `const` place une déclaration
    dans sa zone morte temporelle jusqu'à son initialisation, et `filteredProducts` s'évalue
    au corps du composant — donc avant. L'écran tombait sur « Cannot access 'passeLesFiltres'
    before initialization », et **ni le build ni les contrôles ne le voyaient** : le premier
    n'a pas `no-use-before-define`, les seconds lisent le texte du fichier, pas son ordre
    d'exécution.
  */
  /*
    ⚠️ **Jumeaux de `backend/src/lib/segmentsProduit.js`**, et les deux doivent rester
    d'accord — même parti que `vignetteDe` et `lireMesureSaisie`, pour la même raison :
    l'écran ne peut pas charger un module CommonJS du serveur, et filtrer côté serveur
    imposerait un aller-retour par clic. `backend/scripts/test-filtres-segments.js` confronte
    les deux implémentations sur les mêmes jeux.
  */
  const AXES_SEGMENT = [
    { cle: 'brand', libelle: 'Marque', multiple: false, hierarchique: false },
    { cle: 'productTypes', libelle: 'Catégorie', multiple: true, hierarchique: true },
    { cle: 'customLabels', libelle: 'Libellés', multiple: true, hierarchique: false }
  ];
  const SEPARATEUR_SEGMENT = ' > ';
  const niveauxSegment = (chemin) =>
    String(chemin || '').split('>').map(x => x.trim()).filter(Boolean);

  const valeurSegment = (brut) => {
    if (brut === null || brut === undefined) return null;
    const t = String(brut).trim();
    return (!t || t === 'null' || t === 'undefined') ? null : t;
  };
  const listeSegment = (brut) => {
    const tab = Array.isArray(brut) ? brut : (brut === null || brut === undefined ? [] : [brut]);
    return [...new Set(tab.map(valeurSegment).filter(Boolean))];
  };

  /** Les segments d'un produit d'analyse. Les deux graphies de l'API sont acceptées. */
  const segmentsDuProduit = (produit) => (!produit ? null : {
    brand: valeurSegment(produit.brand),
    productTypes: listeSegment(produit.productTypes || produit.product_types || produit.productType),
    customLabels: listeSegment([0, 1, 2, 3, 4].map(i =>
      produit[`customLabel${i}`] ?? produit[`custom_label_${i}`]))
  });

  /*
    ⚠️ Rend `null` quand la suggestion ne porte aucun segment — les lignes produites avant la
    colonne `productSegments`. Elles ne doivent jamais être masquées : voir `passeLesFiltres`.
  */
  const segmentsDeLaSuggestion = (sugg) => {
    if (!sugg) return null;
    const r = sugg.productSegments;
    if (!r && !sugg.productBrand) return null;
    return {
      brand: valeurSegment(sugg.productBrand) || valeurSegment(r?.brand),
        productTypes: listeSegment(r?.productTypes),
      customLabels: listeSegment(r?.customLabels)
    };
  };

  /*
    ⚠️ **Une ligne qu'on ne sait pas classer est retenue, jamais masquée.** Les suggestions
    antérieures à `productSegments` n'en portent aucun : les écarter viderait l'onglet au
    premier filtre posé, et l'on conclurait que le catalogue est vide.
  */
  const passeLesFiltres = (segments) => {
    const actifs = AXES_SEGMENT.filter(a => filtresSegment[a.cle]);
    if (actifs.length === 0) return true;
    if (!segments) return true;
    return actifs.every(a => {
      const attendu = filtresSegment[a.cle];
      /*
        ⚠️ **Sur un axe hiérarchique, on retient aussi ce qui est dessous**, et la comparaison
        se fait **au séparateur** : « Femme » ne doit pas retenir « Femmes enceintes », ni
        « Art » retenir « Arts Graphiques ».
      */
      const correspond = a.hierarchique
        ? (x) => x === attendu || String(x).startsWith(attendu + SEPARATEUR_SEGMENT)
        : (x) => x === attendu;
      const v = segments[a.cle];
      return a.multiple
        ? (v || []).some(correspond)
        : (v !== null && v !== undefined && correspond(v));
    });
  };

  /*
    ⚠️ **Un axe à une seule valeur n'est pas rendu.** Il occuperait une case sans jamais
    retirer une ligne — mesuré : la marque vaut « Pebeo » sur les 3 568 produits de ce flux,
    et « BZB » sur les 5 584 de l'autre.
  */
  /*
    ⚠️ **Une taxonomie se propose par niveaux, jamais en chemins entiers.**

    Mesuré : 242 chemins sur un flux, dont un de **310 caractères**. Une liste déroulante
    s'élargit à sa plus longue entrée — elle débordait l'écran, et il fallait lire la phrase
    entière pour distinguer deux voisines. Chaque préfixe devient une entrée et n'affiche que
    son dernier niveau, décalé de sa profondeur : 76 caractères au pire.
  */
  const optionsDeFiltre = (segmentsListe) => AXES_SEGMENT
    .map(a => {
      const vues = new Set();
      for (const s2 of segmentsListe) {
        if (!s2) continue;
        if (a.multiple) (s2[a.cle] || []).forEach(v => vues.add(v));
        else if (s2[a.cle]) vues.add(s2[a.cle]);
      }

      const entrees = new Map();
      for (const brute of vues) {
        const parts = niveauxSegment(brute);
        if (!a.hierarchique || parts.length <= 1) {
          entrees.set(brute, { valeur: brute, libelle: brute, profondeur: 0 });
          continue;
        }
        for (let i = 1; i <= parts.length; i += 1) {
          const prefixe = parts.slice(0, i).join(SEPARATEUR_SEGMENT);
          if (!entrees.has(prefixe)) {
            entrees.set(prefixe, { valeur: prefixe, libelle: parts[i - 1], profondeur: i - 1 });
          }
        }
      }
      /*
        Le tri porte sur le chemin complet : trier sur le dernier niveau mêlerait les branches
        et l'arbre ne se lirait plus. `localeCompare` parce que « Écharpe » se range près de
        « Echarpe », pas après « Z ».
      */
      return { ...a, valeurs: [...entrees.values()].sort((x, y) => x.valeur.localeCompare(y.valeur, 'fr')) };
    })
    .filter(a => a.valeurs.length > 1);

  /**
   * Les suggestions indexées par identifiant d'offre.
   *
   * Chaque ligne cherchait la sienne par un `find` sur le tableau entier. Sur un flux de mille
   * produits, cent lignes affichées, cela fait cent mille comparaisons à chaque frappe dans le
   * champ de recherche — et le tri par statut en demanderait autant.
   */
  const suggestionsParProduit = useMemo(() => {
    const index = new Map();
    suggestions.forEach(s => index.set(s.offerId, s));
    return index;
  }, [suggestions]);

  /**
   * La liste que l'écran montre : les produits analysés, **plus** les suggestions dont le
   * produit n'est plus au flux.
   *
   * ## ⚠️ Pourquoi l'union, et non les seuls produits
   *
   * Mesuré sur le parc : **19 suggestions sur 82 — 23 %** n'ont plus de produit dans l'analyse
   * courante, réparties sur six flux. Dont une `VALIDATED`, donc présente dans le flux XML
   * **en ligne**. L'ancien onglet Suggestions les montrait — il listait des suggestions, pas
   * des produits. Une liste pilotée par les seuls `products` les ferait disparaître, et avec
   * elles la seule façon de les rouvrir ou de dépublier celle qui est servie.
   *
   * ⚠️ Une suggestion validée orpheline **reste dans le flux XML** : la requête du flux filtre
   * par configuration et statut, jamais par appartenance à l'analyse. Masquer sa ligne « parce
   * que le produit n'existe plus » la laisserait publiée sans recours.
   *
   * ⚠️ **Conséquence heureuse** : l'orpheline étant dans la liste, `indexProduitOuvert` la
   * trouve, et sa fiche retrouve Précédent/Suivant — que l'ancienne porte d'entrée
   * `openSuggestionDetail` lui refusait, son produit reconstitué n'étant nulle part.
   */
  /**
   * Les segments d'une ligne, quelle que soit son origine.
   *
   * ⚠️ **Une orpheline tire ses segments de sa suggestion**, jamais du produit : le produit
   * reconstitué par `produitDeLaFiche` n'a pas les clés d'un produit d'analyse, et
   * `segmentsDuProduit` rendrait vide. Une ligne sans segment n'est pas masquée (voir
   * `passeLesFiltres`), mais aucun filtre posé ne la retiendrait — elle paraîtrait
   * disparaître, ce qui se lit comme un filtre qui fonctionne.
   *
   * ⚠️ **Une seule fonction décide**, et c'est le point : les options de la barre et le
   * filtrage lisent la même source. Deux lectures divergentes proposeraient une marque que
   * le filtre ne saurait pas retenir — une option qui ne filtre rien, et rien ne le dirait.
   *
   * ⚠️ **Une fonction simple, pas un `useCallback`.** Les deux aides qu'elle enveloppe sont
   * recréées à chaque rendu : la mémoriser ne mémoriserait rien, et déclarer ses dépendances
   * exactes l'invaliderait à chaque fois de toute façon. Un `useCallback` qui tait ses
   * dépendances pour paraître stable est le genre de mensonge qui se paie plus tard, sur une
   * valeur figée au premier rendu.
   */
  const segmentsDeLaLigne = (ligne) => (
    ligne?._horsAnalyse
      ? segmentsDeLaSuggestion(suggestionsParProduit.get(ligne.offerId))
      : segmentsDuProduit(ligne)
  );

  const produitsEtOrphelines = useMemo(() => {
    const connus = new Set(products.map(p2 => String(p2.offerId)));
    const orphelines = suggestions
      .filter(s2 => !connus.has(String(s2.offerId)))
      .map(s2 => ({
        ...produitDeLaFiche(s2, attributs, undefined),
        /*
          ⚠️ **Le drapeau, et pas une absence de score.** La ligne n'a ni note ni gravités —
          l'analyse ne l'a pas regardée. Lui laisser afficher « Conforme » dirait d'un produit
          non mesuré qu'il va bien, ce que `analyseCherchee` existe déjà pour empêcher ailleurs.
        */
        _horsAnalyse: true,
        issues: [],
        score: undefined,
        grade: undefined
      }));
    return orphelines.length ? [...products, ...orphelines] : products;
  }, [products, suggestions, attributs]);

  // Filtrer les produits
  const filteredProducts = produitsEtOrphelines.filter(product => {
    // Filtre par sévérité
    if (filterSeverity !== 'all') {
      const hasSeverity = product.issues?.some(i => i.severity === filterSeverity);
      if (!hasSeverity) return false;
    }

    // Filtre par type de problème
    if (filterIssueType) {
      const hasIssueType = product.issues?.some(i => i.type === filterIssueType);
      if (!hasIssueType) return false;
    }

    /*
      Filtre par état de suggestion.

      ⚠️ **Inerte tant que les suggestions ne sont pas arrivées.** Sans la garde, « En attente »
      posé avant la réponse vide la liste — et une liste vide se lit « rien n'est en attente »,
      c'est-à-dire l'inverse de ce qu'on venait vérifier.
    */
    if (suggestionsChargees && filterSuggestion !== 'all') {
      const etatVise = ETATS_SUGGESTION.find(e => e.cle === filterSuggestion);
      const statut = suggestionsParProduit.get(product.offerId)?.status;
      // Un prédicat l'emporte : il lit la ligne, là où `statuts` ne lit que la suggestion.
      if (etatVise?.predicat) {
        if (!etatVise.predicat(product)) return false;
      } else if (etatVise?.statuts ? !etatVise.statuts.includes(statut) : statut !== undefined) {
        return false;
      }
    }

    // Filtres de segment — marque, catégorie, libellés. Voir `segmentsDeLaLigne`.
    if (!passeLesFiltres(segmentsDeLaLigne(product))) return false;

    // Filtre par recherche
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        product.offerId?.toLowerCase().includes(search) ||
        product.title?.toLowerCase().includes(search)
      );
    }

    return true;
  });

  /**
   * Ce que « Valider » enverra : les suggestions en attente du périmètre **filtré**.
   *
   * ⚠️ **Toutes pages confondues**, et non la page affichée. Filtrer puis valider est le geste
   * utile — cerner une catégorie, tout accepter — et le borner à cent lignes le rendrait
   * surprenant dans l'autre sens.
   *
   * ⚠️ Calcul simple et non `useMemo` : `filteredProducts` est un tableau recréé à chaque
   * rendu, un memo qui en dépend recalculerait de toute façon. Un memo mentirait sur sa
   * nature pour ne rien économiser.
   */
  /*
    ⚠️ **Une publiée régénérée est à valider elle aussi.** Le décompte ne retenait que
    `PENDING` : le bouton affichait « 0 » alors que des lignes portaient « À valider », et
    rien ne permettait de les publier en masse. Une proposition qui diffère de ce qui est
    servi attend un geste, quel que soit le statut qu'elle traîne.
  */
  const aValider = filteredProducts.reduce((ids, p2) => {
    const s2 = suggestionsParProduit.get(p2.offerId);
    if (!s2) return ids;
    if (s2.status === 'PENDING' || aRevalider(p2)) ids.push(s2.id);
    return ids;
  }, []);

  /** Un filtre est-il posé ? C'est ce qui décide du libellé du bouton. */
  const filtresActifs = filterSeverity !== 'all' || Boolean(filterIssueType)
    || Boolean(searchTerm) || filterSuggestion !== 'all'
    || AXES_SEGMENT.some(a2 => filtresSegment[a2.cle]);

  /*
    ⚠️ **Déclarés ici, et non plus haut.** `selectionnablesFiltres` lit `filteredProducts` et
    `soumettreLotIA` déclare `chargerLots` en dépendance : tous deux s'évaluent **au corps du
    composant**, donc avant leurs sources si on les pose en tête. L'écran tombait sur
    « Cannot access … before initialization » — le même défaut que `passeLesFiltres` en son
    temps, et le compilateur l'a signalé cette fois (`no-use-before-define`).
  */
  /**
   * Les produits que « tout sélectionner » prendrait : le périmètre **filtré**, toutes pages
   * confondues, moins ce qu'un traitement tient déjà.
   *
   * ⚠️ **Filtré, et non « tout le flux ».** C'est le même parti que « Tout valider » : filtrer
   * puis tout prendre est le geste utile — cerner une catégorie, la traiter en entier. Prendre
   * le flux entier quoi qu'affiche l'écran serait une surprise à cent vingt dollars.
   */
  const selectionnablesFiltres = useMemo(() => filteredProducts
    .filter(p => !p._horsAnalyse && !offerIdsVerrouilles.has(String(p.offerId)))
    .map(p => p.offerId), [filteredProducts, offerIdsVerrouilles]);

  /**
   * Soumettre un lot asynchrone.
   *
   * ⚠️ **On n'envoie que des identifiants.** Cinq mille produits feraient plusieurs
   * mégaoctets de corps, et surtout la soumission doit porter ce que le flux dit
   * **maintenant** : le serveur les relit de l'analyse. Un lot vit jusqu'à vingt-quatre
   * heures, partir d'une copie vieille de la session serait partir faux.
   */
  const soumettreLotIA = useCallback(async (offerIds) => {
    if (readOnly) return;
    if (!offerIds?.length) return;
    try {
      setError(null);
      const reponse = await axios.post(
        `${API_URL}/api/tools/feed-optimizer/generate-batch`,
        {
          merchantId: selectedAccount,
          feedLabel: selectedFeedLabel,
          offerIds,
          brandPosition,
          titreGabarit,
          attributs: attributsAGenerer
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!reponse.data.success) return;

      const { lot, ecartes } = reponse.data;
      /*
        ⚠️ **Ce qui a été écarté se dit.** « 5 000 demandés, 4 980 soumis » se perdrait
        sinon, et l'on croirait le catalogue couvert.
      */
      setSuccess(`Lot de ${lot.demandes} produit(s) soumis — résultats sous une heure en`
        + ' général, 24 h au plus. Vous pouvez fermer l\'écran.'
        + (ecartes?.dejaEnLot ? ` · ${ecartes.dejaEnLot} déjà dans un lot` : '')
        + (ecartes?.absentsDeLAnalyse
          ? ` · ${ecartes.absentsDeLAnalyse} absent(s) de l'analyse` : ''));
      setTimeout(() => setSuccess(null), 10000);
      setSelectedProducts([]);
      chargerLots();
    } catch (err) {
      setError(messageErreur(err, 'Erreur lors de la soumission du lot'));
    }
  }, [selectedAccount, selectedFeedLabel, token, brandPosition, titreGabarit,
    attributsAGenerer, chargerLots]);


  /**
   * Les attributs que l'analyse affichée ne porte pas **du tout**.
   *
   * ## Le défaut que cela corrige
   *
   * La fiche affichait « Non renseigné » sur le prix et la disponibilité de flux qui les
   * renseignent tous les deux. Ce n'est pas le marchand qui les laissait vides : c'est la photo
   * qui ne les portait pas. Les analyses en base datent du 2 octobre, prises par la version
   * d'avant la table d'attributs (`fe839ea`, 9 octobre), dont la projection ne gardait que dix
   * champs choisis à la main — `price`, `availability`, `mpn`, `productTypes`, `ageGroup`,
   * `googleProductCategory` et `additionalImageLinks` n'y figuraient pas. Vérifié sur les cinq
   * comptes : aucun n'en porte un seul.
   *
   * Dire « Non renseigné » d'un champ qu'on n'a pas regardé est un mensonge, et un mensonge
   * actionnable : il envoie corriger un flux source qui n'a rien à corriger. C'est la discipline
   * du Diagnostic SEO — « pas mesuré » n'est pas « mesuré, rien à signaler ».
   *
   * ## Ni la valeur seule, ni le lot seul : il faut les deux, plus les constats
   *
   * Une analyse est enregistrée en JSON, qui perd les clés `undefined`. Sur un produit,
   * « absent » et « vide » sont donc indiscernables. Sur le lot entier non plus : la matière
   * manque sur les 2 612 produits de ce flux, et sa clé est absente partout — pourtant elle a
   * bien été relevée, et les 2 612 constats « Matière manquante » le prouvent.
   *
   * **C'est le constat qui tranche.** Un attribut contrôlé que l'analyse a trouvé vide porte un
   * « manquant » ; s'il est renseigné, il porte sa valeur. N'avoir ni l'un ni l'autre sur aucun
   * produit du lot ne laisse qu'une explication : la photo ne l'a pas relevé.
   *
   * Vérifié contre les données du flux Pébéo : la règle isole exactement `price`,
   * `availability`, `mpn`, `product_type` et `product_highlight` — les cinq que la projection
   * d'avant `fe839ea` ne gardait pas — et laisse « relevés » la matière, le genre, la tranche
   * d'âge et la catégorie Google, vides mais constatés.
   */
  /*
   * Deux questions, et les confondre était le défaut : « cette analyse connaît-elle
   * l'attribut ? » porte sur l'analyse entière, « ce produit a-t-il été constaté ? » porte
   * sur un produit. Voir `ficheProduit.js` pour la mesure qui a tranché.
   */
  const inconnusDeLAnalyse = useMemo(
    () => attributsInconnusDeLAnalyse(products, attributs),
    [products, attributs]
  );

  const attributsNonReleves = useMemo(
    () => attributsNonRelevesPour(selectedProductDetail, inconnusDeLAnalyse),
    [selectedProductDetail, inconnusDeLAnalyse]
  );

  /**
   * Ceux que le tableau comparatif montre, parmi les non relevés.
   *
   * L'avertissement de tête les nomme, et ne compte donc que ce qu'il annonce : les quatre
   * attributs de l'en-tête disent eux-mêmes leur « non relevé », voir `repereCatalogue`.
   */
  const nonRelevesAffiches = attributs.filter(a => (
    attributsNonReleves.has(a.nom) && !ATTRIBUTS_EN_ENTETE.includes(a.nom)
  ));

  /**
   * La liste triée, puis paginée.
   *
   * Le tri porte sur les produits **filtrés** et non sur la page : trier page par page
   * n'ordonnerait que les cent lignes sous les yeux, en laissant croire au contraire.
   */
  const sortedProducts = useMemo(() => {
    const sens = triProduits.sens === 'asc' ? 1 : -1;
    const valeur = (produit) => {
      if (triProduits.cle === 'score') return produit.score ?? 0;
      if (triProduits.cle === 'problemes') return produit.issues?.length || 0;
      if (triProduits.cle === 'suggestion') {
        const statut = suggestionsParProduit.get(produit.offerId)?.status;
        // Sans suggestion, le produit passe après les quatre statuts : c'est l'état le plus
        // fréquent, et le remonter noierait les lignes qui demandent une décision.
        return statut === undefined ? 9 : (RANG_STATUT[statut] ?? 8);
      }
      return null;
    };

    return [...filteredProducts].sort((a, b) => {
      const va = valeur(a);
      if (va === null) {
        return sens * String(a.title || '').localeCompare(String(b.title || ''), 'fr');
      }
      return sens * (va - valeur(b));
    });
  }, [filteredProducts, triProduits, suggestionsParProduit]);

  const totalProductsPages = Math.ceil(sortedProducts.length / PRODUCTS_PER_PAGE);
  const paginatedProducts = sortedProducts.slice(
    (productsCurrentPage - 1) * PRODUCTS_PER_PAGE,
    productsCurrentPage * PRODUCTS_PER_PAGE
  );

  /**
   * La liste, pour y revenir en changeant de page.
   *
   * Cent lignes font six mille pixels : les boutons de pagination sont donc tout en bas, et
   * cliquer « Suivant » laissait le lecteur au pied de la page suivante, à cent lignes de son
   * premier produit.
   */
  const listeProduitsRef = useRef(null);
  const ficheRef = useRef(null);
  /* La racine de l'outil : elle survit à toutes les bascules, et sert de point d'appui quand
     ni la liste ni la fiche ne sont montées — au retour d'une fiche ouverte depuis l'onglet
     Suggestions, par exemple, où il n'y a pas de liste de produits. */
  const outilRef = useRef(null);

  /**
   * Le conteneur qui défile au-dessus d'un élément.
   *
   * ⚠️ **`scrollIntoView` est inopérant dans cette application** — vérifié dans le navigateur :
   * ni avec `behavior: 'smooth'`, ni sans. Le conteneur qui défile est `.main-content`, et deux
   * de ses ancêtres (`#root` et `html`) portent `overflow: hidden` ; la remontée de chaîne du
   * navigateur n'y trouve pas de cible. On pose donc `scrollTop` nous-mêmes, sur le premier
   * ancêtre réellement défilant — le chercher évite de coder en dur le nom d'une classe du
   * châssis de l'application.
   */
  const conteneurDefilant = (element) => {
    let parent = element?.parentElement;
    while (parent) {
      const { overflowY } = window.getComputedStyle(parent);
      const peutDefiler = (overflowY === 'auto' || overflowY === 'scroll')
        && parent.scrollHeight > parent.clientHeight;
      if (peutDefiler) return parent;
      parent = parent.parentElement;
    }
    return null;
  };

  /**
   * Amener le haut d'un élément sous le bord haut de la zone visible.
   *
   * Défilement instantané, et non adouci : `behavior: 'smooth'` est ignoré ici, et faire défiler
   * six mille pixels sous les yeux n'apporterait rien de plus que d'être arrivé.
   */
  const amenerEnVue = (element, marge = 16) => {
    const defilant = conteneurDefilant(element);
    if (!defilant) return;

    const haut = element.getBoundingClientRect().top
      - defilant.getBoundingClientRect().top
      + defilant.scrollTop;
    defilant.scrollTop = Math.max(haut - marge, 0);
  };

  /**
   * Changer de page, et revenir en tête de liste.
   *
   * Cent lignes font six mille pixels : les boutons de pagination sont donc tout en bas, et
   * cliquer « Suivant » laissait le lecteur au pied de la page suivante, à cent lignes de son
   * premier produit.
   */
  const allerPageProduits = (page) => {
    setProductsCurrentPage(Math.min(Math.max(page, 1), totalProductsPages));
    amenerEnVue(listeProduitsRef.current);
  };

  /** Remet les trois filtres à zéro — celui de gravité, celui de type, et la recherche. */
  const reinitialiserFiltres = () => {
    setFilterSeverity('all');
    setFilterIssueType(null);
    setSearchTerm('');
    /*
      ⚠️ **Les segments et l'état y étaient oubliés.** Le bouton ne remettait que la gravité,
      le type et la recherche : sur un flux filtré par marque, « Réinitialiser les filtres »
      laissait la liste vide et ne tenait pas sa promesse. Le défaut existait déjà ; la fusion
      en fait l'unique sortie d'une liste vide, puisqu'il y a désormais cinq axes au lieu de
      trois.
    */
    setFiltresSegment({});
    setFilterSuggestion('all');
    setProductsCurrentPage(1);
  };

  /* -------------------------------------------------------------------------
     La fiche produit : dans la page, et d'un produit au suivant

     Elle était une modale. Trop petite pour ce qu'elle porte — deux champs de texte long,
     dix-sept attributs, les recommandations de l'IA, dix points forts — et surtout refermée
     sur un seul produit : revoir le suivant demandait de fermer, retrouver sa ligne parmi
     cent, et rouvrir.
     ------------------------------------------------------------------------- */

  /** Une fiche ouverte occupe la page : la barre de sélection, les onglets et la liste s'effacent. */
  const ficheOuverte = Boolean(selectedProductDetail);

  /**
   * Le rang du produit ouvert dans la liste **triée et filtrée**.
   *
   * C'est bien celle-là, et non `products` : la navigation doit suivre l'ordre qu'on a sous les
   * yeux, et ne pas ramener un produit que le filtre en cours écarte.
   *
   * Vaut -1 quand la fiche vient de l'onglet Suggestions : le produit y est reconstitué à partir
   * d'une suggestion et n'appartient à aucune liste. La barre masque alors sa navigation.
   */
  const indexProduitOuvert = selectedProductDetail
    ? sortedProducts.findIndex(p => p.offerId === selectedProductDetail.offerId)
    : -1;

  /**
   * L'empreinte des saisies, pour savoir si quelque chose a bougé depuis l'ouverture.
   *
   * Comparer aux valeurs du produit ne conviendrait pas : une fiche ouverte depuis l'onglet
   * Suggestions est pré-remplie avec les propositions de l'IA, qui diffèrent du produit par
   * construction — elle s'annoncerait « non enregistrée » avant qu'on y touche.
   *
   * Les clés sont triées : accepter une recommandation réécrit l'objet, et l'ordre d'insertion
   * ne survit pas à toutes ces réécritures.
   */
  const empreinteEditions = (editions) => {
    const { _acceptedFields, _suggestion, ...champs } = editions || {};
    return JSON.stringify(Object.keys(champs).sort().map(cle => [cle, champs[cle] ?? '']));
  };

  const empreinteALOuverture = useRef('');
  const editionsEnCours = ficheOuverte
    && empreinteALOuverture.current !== empreinteEditions(productEdits);

  /**
   * Un repère de catalogue dans l'en-tête de la fiche : le prix, la disponibilité.
   *
   * Trois états, et c'est pour les tenir ensemble que cette fonction existe plutôt qu'un bloc
   * de JSX par attribut :
   *
   * 1. **une valeur** — elle s'affiche, le prix en chiffres, la disponibilité en pastille ;
   * 2. **un constat** — « Prix manquant » : le flux ne le porte pas, et c'est une alerte ;
   * 3. **ni l'un ni l'autre** — la photo ne l'a pas relevé, voir `attributsNonReleves`.
   *
   * Confondre les deux derniers est précisément ce que l'écran faisait avant, et ce qu'il ne
   * doit plus faire : « Prix manquant » envoie corriger le flux source, « non relevé » envoie
   * relancer l'analyse.
   *
   * La note de la table — « Le prix vient du catalogue du marchand » — passe en infobulle :
   * elle explique l'absence de champ de saisie, et cette explication n'a plus de carte où vivre.
   */
  const repereCatalogue = (nom) => {
    const attribut = attributs.find(a => a.nom === nom);
    if (!attribut || !selectedProductDetail) return null;

    const texte = texteAttribut(selectedProductDetail[attribut.cleProduit], nom);

    if (texte) {
      if (nom === 'availability') {
        const ton = TON_DISPONIBILITE[normaliserEnum(selectedProductDetail.availability)] || 'inactif';
        return <span key={nom} className={`kc-etat ${ton}`} title={attribut.note}>{texte}</span>;
      }
      return <span key={nom} className="fo-fiche-prix" title={attribut.note}>{texte}</span>;
    }

    const constat = getIssueForField(nom);
    if (constat) {
      return (
        <span key={nom} className={`field-issue ${constat.severity}`} title={attribut.note}>
          {getSeverityIcon(constat.severity)}
          {constat.message}
        </span>
      );
    }

    if (attributsNonReleves.has(nom)) {
      return (
        /*
          ⚠️ **Le même badge que dans le tableau, et pour la même raison.**

          L'en-tête porte déjà `field-issue` juste au-dessus pour les constats : garder ici un
          texte orange italique faisait deux langages sur une même rangée, dont l'un promettait
          une gravité qu'il n'a pas — l'analyse n'a pas regardé cet attribut, ce n'est pas un
          avertissement sur le flux.

          L'infobulle reste : c'est elle qui dit le geste — relancer l'analyse —, et un badge de
          trois mots ne peut pas le porter.
        */
        <span
          key={nom}
          className="field-issue info fo-badge-non-releve"
          title={`${attribut.libelle} n’a pas été relevé par cette analyse. Relancez l’analyse du flux pour l’obtenir.`}
        >
          {getSeverityIcon('info')}
          {MENTION_NON_RELEVE[nom] || `${attribut.libelle} non relevé`}
        </span>
      );
    }

    return null;
  };

  /**
   * Les champs du tableau comparatif, dans l'ordre de la table servie par le serveur.
   *
   * Tout ce que la table déclare, sauf ce que l'en-tête porte déjà. Le titre et la description
   * y reviennent : du temps où ils avaient leur propre carte en haut de la fiche ils en étaient
   * écartés, mais ce sont désormais les deux premières lignes du tableau — et elles le sont
   * parce qu'elles ouvrent la table, pas parce qu'on les y a placées.
   */
  /*
    ⚠️ **L'ordre de la fiche est celui du catalogue, sans retouche.**

    Les éditeurs en bloc étaient repoussés en fin de fiche par un tri local, pour éviter qu'un
    `liste` de dix champs posé en troisième ligne ne repousse les onze attributs suivants de
    trois cents pixels. Le remède était bon, l'endroit non : le catalogue déclare lui-même
    « l'ordre est celui de la déclaration, c'est lui qui fixe l'ordre des champs dans la fiche
    produit », et un tri d'écran le contredisait en silence.

    Les attributs d'enrichissement sont maintenant groupés en fin de catalogue, ce qui règle le
    cas à sa source. Et le tri avait cessé d'être inoffensif : `product_type` étant devenu une
    liste de chemins, il l'aurait arraché à la catégorisation pour l'exiler en bas.
  */
  /*
   * Le lien vers la fiche Merchant Center, calculé par le serveur et posé à la lecture.
   *
   * ⚠️ **Il n'est pas refabriqué ici**, alors que les quatre paramètres traînent dans le
   * composant. L'URL était déjà recopiée à six endroits du projet, chacun avec ses propres
   * replis — dont un « fr » en dur qui ouvrait la fiche française d'un produit allemand.
   * `lib/lienMerchantCenter.js` est la seule implémentation, et elle rend `null` plutôt qu'un
   * lien incomplet.
   *
   * Deux origines parce que la fiche s'ouvre de deux endroits : depuis l'onglet Analyse, le
   * produit porte le lien ; depuis Suggestions, c'est la suggestion.
   */
  const lienMerchant = selectedProductDetail?.merchantCenterUrl
    || productEdits._suggestion?.merchantCenterUrl
    || null;

  const champsDeLaFiche = attributs.filter(a => !ATTRIBUTS_EN_ENTETE.includes(a.nom));

  /**
   * Le nom d'une catégorie Google recommandée.
   *
   * Le modèle rend tantôt un identifiant numérique, tantôt un chemin (« Vêtements > Chemises »).
   * Le premier ne dit rien à personne : on lui substitue son nom quand le cache l'a résolu, en
   * gardant l'identifiant entre parenthèses — c'est lui qui part dans le flux.
   */
  const libelleCategorie = (valeur) => {
    if (!/^\d+$/.test(String(valeur))) return String(valeur);
    const nom = categoryNamesCache[valeur];
    return nom ? `${nom} (${valeur})` : String(valeur);
  };

  /**
   * Accepter une recommandation.
   *
   * La catégorie Google demande deux gestes de plus : la recherche affichée doit suivre la
   * valeur acceptée, sinon le champ continue d'afficher autre chose que ce qui sera écrit. Un
   * chemin, lui, n'est pas une valeur valide — on ouvre la liste sur son dernier niveau pour
   * que la sélection se fasse dans l'arborescence.
   */
  const accepterRecommandation = (attribut, valeur) => {
    /*
     * ⚠️ **Un verdict referme l'atelier.** Il s'y rend, et une fois rendu l'atelier n'a plus
     * d'objet : le laisser ouvert ferait coexister un titre tranché et un panneau qui invite
     * à le recomposer. « Modifier » le rouvre, lui, à tout moment.
     */
    if (attribut.nom === 'title') setEditionTitre(false);

    if (attribut.nom !== 'google_product_category') {
      acceptRecommendation(attribut.nom, valeur);
      return;
    }

    if (/^\d+$/.test(String(valeur))) {
      acceptRecommendation('google_product_category', valeur);
      const nom = categoryNamesCache[valeur];
      if (nom) setCategorySearch(nom);
      return;
    }

    setCategorySearch(String(valeur).split('>').pop().trim());
    setShowCategoryDropdown(true);
  };

  /**
   * Le choix d'une catégorie Google : un champ de recherche, et l'arborescence sous lui.
   *
   * Sorti en fonction depuis que la fiche est un tableau : c'est le seul widget qui ne tienne
   * pas en trois lignes de JSX, et le laisser en ligne dans la boucle aurait noyé les douze
   * autres champs.
   */
  const rendreChoixCategorie = () => (
    <div className="fo-categorie">
      <div className="fo-categorie-saisie">
        <Search size={14} className="fo-categorie-loupe" />
        <input
          type="text"
          // Le champ n'affiche que la saisie. Il ne se rabat plus sur « ID: 780 » quand la
          // saisie est vide, sinon effacer le texte faisait réapparaître la valeur enregistrée
          // et le champ ne pouvait jamais être vidé.
          value={categorySearch}
          onChange={(e) => {
            setCategorySearch(e.target.value);
            setShowCategoryDropdown(true);
            setCategoryPath(null);
            // Modifier le texte invalide la sélection : tant qu'une catégorie n'a pas été
            // choisie dans la liste, il ne doit rester aucun identifiant enregistré.
            if (productEdits.google_product_category) {
              updateProductField('google_product_category', '');
            }
          }}
          onFocus={() => setShowCategoryDropdown(true)}
          // Délai pour permettre le clic sur un élément de la liste.
          onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 200)}
          placeholder="Rechercher une catégorie…"
        />
        {/* Le chevron dit que le champ ouvre une liste ; il cède la place au sablier pendant
            que la taxonomie se charge. */}
        {loadingCategories
          ? <Loader2 size={14} className="animate-spin fo-categorie-indicateur" />
          : (
            <ChevronDown
              size={14}
              className={`fo-categorie-indicateur${showCategoryDropdown ? ' ouvert' : ''}`}
            />
          )}
      </div>

      {showCategoryDropdown && categoryResults.length > 0 && (
        <div
          className="fo-categorie-liste"
          // Empêche l'input de perdre le focus : sans cela, le onBlur refermerait la liste
          // 200 ms après un clic, et descendre d'un niveau rouvrirait puis refermerait aussitôt.
          onMouseDown={(e) => e.preventDefault()}
        >
          {!(categorySearch && categorySearch.length >= 2) && (
            <div className="fo-categorie-fil">
              {/* Fil d'Ariane : chaque niveau parcouru reste cliquable pour remonter. */}
              <span
                role="button"
                tabIndex={-1}
                onClick={() => goToCategoryLevel(null)}
                style={{ textDecoration: categoryPath ? 'underline' : 'none' }}
              >
                Catégories principales
              </span>
              {categoryPath && categoryPath.split(' > ').map((segment, index, segments) => {
                const chemin = segments.slice(0, index + 1).join(' > ');
                const courant = index === segments.length - 1;
                return (
                  <span key={chemin}>
                    {' › '}
                    <span
                      role={courant ? undefined : 'button'}
                      tabIndex={courant ? undefined : -1}
                      onClick={() => !courant && goToCategoryLevel(chemin)}
                      style={courant ? { color: '#374151', fontWeight: 600 } : undefined}
                    >
                      {segment}
                    </span>
                  </span>
                );
              })}
              {!categoryPath && ' — saisissez au moins 2 caractères pour chercher'}
            </div>
          )}

          {categoryTruncated && (
            <div className="fo-categorie-troncature">
              Trop de résultats : seuls les 50 premiers sont affichés, affinez la recherche
            </div>
          )}

          {categoryResults.map((cat) => {
            // Un nœud se parcourt, une feuille se sélectionne. Le bouton « Choisir » reste
            // disponible sur les nœuds : une catégorie intermédiaire est une valeur valide.
            const noeud = cat.hasChildren && !(categorySearch && categorySearch.length >= 2);
            return (
              <div
                key={cat.id}
                className="fo-categorie-item"
                onClick={() => (noeud ? openCategoryLevel(cat) : selectCategory(cat))}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="fo-categorie-nom">{cat.shortName}</div>
                  <div className="fo-categorie-chemin">
                    {cat.name} <span style={{ color: '#6b7280' }}>({cat.id})</span>
                  </div>
                </div>
                {noeud && (
                  <>
                    <button
                      type="button"
                      className="fo-categorie-choisir"
                      onClick={(e) => { e.stopPropagation(); selectCategory(cat); }}
                    >
                      Choisir
                    </button>
                    <ChevronRight size={14} style={{ color: '#9ca3af', flexShrink: 0 }} />
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCategoryDropdown && categorySearch && categorySearch.length >= 2
        && categoryResults.length === 0 && !loadingCategories && (
        <div className="fo-categorie-vide">Aucune catégorie trouvée</div>
      )}

      {productEdits.google_product_category && (
        <div className="fo-categorie-retenue">
          Identifiant retenu : <strong>{productEdits.google_product_category}</strong>
          <button
            type="button"
            className="fo-categorie-effacer"
            onClick={() => {
              updateProductField('google_product_category', '');
              setCategorySearch('');
            }}
          >
            Effacer
          </button>
        </div>
      )}
    </div>
  );

  /**
   * Une valeur de liste s'affiche en liste.
   *
   * `texteAttribut` joint les éléments par des virgules — ce qu'il faut pour une infobulle ou
   * une ligne de liste, pas pour une cellule qui a la place de les empiler. Six points forts
   * mis bout à bout donnaient un paragraphe où plus aucun argument ne se distinguait ; trois
   * URL d'images, une ligne où l'on ne voyait plus où commençait la deuxième.
   */
  const rendreValeurListe = (valeurs) => (
    <ul className="fo-liste-valeurs">
      {valeurs.map((element, rang) => (
        <li key={rang}>
          {element && typeof element === 'object' && 'attributeName' in element ? (
            /*
              Une caractéristique se lit en deux temps : ce qui est mesuré, puis la mesure.
              `texteAttribut` les joint par « : », ce qu'il faut pour une infobulle ; dans une
              puce il y a la place de distinguer le nom de sa valeur, et c'est ce qui permet de
              parcourir dix caractéristiques du regard sans les lire une à une.
            */
            <span className="fo-caracteristique-lue">
              {element.sectionName && (
                <span className="fo-caracteristique-section">{element.sectionName}</span>
              )}
              <strong>{element.attributeName}</strong>
              <span>{texteAttribut(element.attributeValue)}</span>
            </span>
          ) : estAdresse(element) ? (
            /*
              Une image supplémentaire se vérifie en la regardant, pas en lisant son adresse.
              Nouvel onglet, comme le lien du produit : la fiche garde ses saisies en cours.
            */
            <a
              className="fo-lien-fichier"
              href={element}
              target="_blank"
              rel="noopener noreferrer"
              title={`Ouvrir ${element} dans un nouvel onglet`}
            >
              <ExternalLink size={12} />
              <span>{nomDeFichier(element)}</span>
            </a>
          ) : texteAttribut(element)}
        </li>
      ))}
    </ul>
  );

  /**
   * Un éditeur en bloc : une rangée de saisie par élément, numérotée, plafonnée.
   *
   * Sert les points forts **et** les images d'ambiance. ⚠️ **Ses quatre libellés viennent de
   * la table d'attributs**, et non du composant : ils y étaient écrits en dur — « Ajouter un
   * point fort », « Retirer ce point fort » —, ce qui allait tant que le widget `liste` ne
   * servait qu'à un attribut. Le second aurait proposé d'ajouter un point fort à la place
   * d'une image, sans qu'aucune erreur ne paraisse.
   *
   * La table les refuse d'ailleurs au chargement si un attribut `liste` ne les déclare pas.
   */
  const rendreListe = (attribut) => {
    // Même garde que `rendreGroupe` : voir `enTableau`.
    const elements = enTableau(productEdits[attribut.nom]);
    const maximum = attribut.maxElements || 10;
    const mots = attribut.libelleElement || {};

    return (
      <div className="fo-points-forts">
        {elements.map((element, rang) => (
          <div key={rang} className="fo-point-fort">
            <span className="fo-point-fort-rang">{rang + 1}</span>
            <input
              type="text"
              value={element}
              maxLength={attribut.maxLongueur || undefined}
              placeholder={`${mots.unite} ${rang + 1}`}
              onChange={(e) => {
                const suivants = [...elements];
                suivants[rang] = e.target.value;
                updateProductField(attribut.nom, suivants);
              }}
            />
            <button
              type="button"
              className="fo-retirer"
              title={mots.retirer}
              onClick={() => updateProductField(attribut.nom, elements.filter((_, i) => i !== rang))}
            >
              <X size={14} />
            </button>
          </div>
        ))}

        {elements.length < maximum && (
          <button
            type="button"
            className="fo-ajouter"
            onClick={() => (attribut.choixDepuis
              /*
                ⚠️ **Un attribut qui déclare où puiser ouvre une modale, les autres ajoutent une
                ligne vide.** Une image d'ambiance ne s'invente pas : la coller à la main suppose
                d'aller la chercher ailleurs, alors que la galerie du produit est déjà dans le
                flux et contient presque toujours une vue portée. Le choix est déclaratif
                (`choixDepuis`, au catalogue) : un autre attribut l'obtiendrait sans toucher ici.
              */
              ? setChoixVisuel(attribut)
              : updateProductField(attribut.nom, [...elements, '']))}
          >
            <Plus size={14} />
            {mots.ajouter}
          </button>
        )}

        {/*
          La borne de longueur n'est mentionnée que si l'attribut en déclare une : une adresse
          d'image n'est pas « de 2 000 caractères au plus », et l'annoncer n'apprendrait rien.
        */}
        <p className="fo-aide">
          {maximum} {mots.pluriel} au plus
          {attribut.maxLongueur ? `, de ${attribut.maxLongueur} caractères chacun` : ''}.
        </p>
      </div>
    );
  };

  /**
   * Choisir une image d'ambiance parmi celles du produit, ou en coller une.
   *
   * ⚠️ **Rendue par un portail**, comme toutes les modales du projet : la fiche vit dans des
   * conteneurs à `overflow: hidden` — ceux qui arrondissent les coins des listes — et une
   * modale posée dans l'arbre y serait rognée.
   *
   * ## Pourquoi proposer la galerie plutôt qu'un champ vide
   *
   * Google décrit l'image d'ambiance comme « le produit dans un cadre réel, comme un mannequin
   * portant le vêtement ». Elle existe presque toujours : l'audit du flux BZB relevait
   * « aucune image lifestyle déclarée alors que chaque galerie a une vue portée ». La
   * déclarer, c'est choisir la bonne vue parmi celles déjà envoyées — pas trouver une adresse.
   *
   * ⚠️ **Rien n'est posé d'office pour autant.** L'écran peut *désigner* — voir la proposition
   * ci-dessous —, il ne retient jamais seul. Une vue portée ne se distingue d'un packshot que
   * par le regard, et se tromper enverrait un plan produit là où Google attend une scène :
   * un défaut invisible dans le flux, que personne ne relit.
   *
   * ## La proposition, quand la galerie a été examinée
   *
   * `ambianceProposee` vient de `POST /ambiance`, qui a regardé les images. Trois états, et
   * l'écran les distingue tous les trois :
   *
   * - **absente** — la galerie n'a pas été examinée. Rien n'est dit : promettre « aucune
   *   ambiance » sur ce qui n'a pas été regardé serait faux.
   * - **`url: null`** — examinée, aucune scène. Le motif est affiché, parce que c'est une
   *   réponse : sans lui, l'absence de proposition se lirait comme un examen manquant, et on
   *   relancerait un calcul déjà payé.
   * - **`url`** — la vue est remontée en tête de galerie et porte son niveau de confiance.
   *
   * ⚠️ **Le niveau de confiance est affiché, pas filtré.** La frontière est réellement floue
   * sur une partie des cas — un à-plat stylisé sur fond sableux, mesuré, est défendable dans
   * les deux sens. Masquer les « probable » priverait d'une bonne réponse une fois sur deux ;
   * les présenter comme les « sure » ferait valider sans regarder.
   */
  /**
   * Choisir les attributs à (re)générer, puis lancer.
   *
   * ⚠️ **Rendue par un portail**, comme toutes les modales du projet : la fiche vit dans des
   * conteneurs à `overflow: hidden` — ceux qui arrondissent les coins des listes — et une
   * modale posée dans l'arbre y serait rognée.
   *
   * ## Pourquoi au clic, et non à côté du bouton
   *
   * Le choix vivait d'abord dans un panneau replié, à côté du bouton. On le voyait sans savoir
   * qu'il commandait le clic suivant, et il fallait l'avoir ouvert **avant** de lancer. Au
   * clic, le choix est là où la décision se prend — et la modale peut dire ce qu'on s'apprête
   * à payer, ce qu'un panneau posé à côté ne pouvait pas.
   *
   * ⚠️ **Une seule modale pour les deux gestes.** `generationADemander` porte `{ produit }`
   * depuis la fiche et `{ lot: true }` depuis la liste : c'est elle qui dit quoi confirmer.
   * Deux modales auraient divergé au premier attribut ajouté.
   */
  /*
    Les axes qui n'ont rien à filtrer, et pourquoi.

    ⚠️ **Sans cette ligne, on cherche un filtre absent.** Un axe à une seule valeur n'est pas
    rendu — c'est voulu, il ne retirerait jamais une ligne — mais rien ne le disait : « je ne
    vois pas les filtres marque et libellés », alors que la marque vaut « Pebeo » sur les
    3 568 produits et qu'aucun libellé n'est renseigné. Le dire coûte une phrase ; le taire
    coûte une recherche.
  */
  /*
    ⚠️ **« Pas mesuré » n'est pas « mesuré, rien à signaler ».**

    Les libellés personnalisés ne sont projetés dans l'analyse que depuis leur entrée au
    catalogue : toute analyse antérieure n'en porte **aucune clé**, et l'axe paraît vide.
    Annoncer « non renseigné » enverrait corriger un flux qui n'a rien à corriger — preuve :
    5 553 produits d'un compte du parc portent une popularité déduite de `customLabel1`, donc
    les libellés y sont bel et bien.

    On regarde donc la **présence de la clé**, jamais sa valeur, et le message dit quoi faire :
    relancer l'analyse. C'est la discipline du Diagnostic SEO, tenue ici.
  */
  const axeMesure = (brutes, cle) => {
    /*
      ⚠️ **Une suggestion ne porte pas les clés d'un produit.** Ses axes vivent dans
      `productSegments`, écrit à la génération, et sa marque dans `productBrand` — jamais
      `brand`. Sonder les seules clés du produit rendait donc la marque toujours « pas
      mesurée » sur l'onglet Suggestions, et l'écran disait « regénérez les suggestions pour
      le lire » d'une marque qu'il affichait par ailleurs. Relevé en production.
    */
    const sondes = {
      brand: ['brand', 'productBrand'],
      productTypes: ['productTypes', 'product_types', 'productType'],
      customLabels: [0, 1, 2, 3, 4].flatMap(i => [`customLabel${i}`, `custom_label_${i}`])
    }[cle] || [];
    return (brutes || []).some(b => b && typeof b === 'object'
      && (sondes.some(k => k in b) || (cle !== 'brand' && b.productSegments)));
  };

  const rendreAxesSansFiltre = (brutes, versSegments) => {
    const segmentsListe = brutes.map(versSegments);
    const rendus = new Set(optionsDeFiltre(segmentsListe).map(a => a.cle));
    const muets = AXES_SEGMENT.filter(a => !rendus.has(a.cle)).map(a => {
      if (!axeMesure(brutes, a.cle)) {
        /*
          ⚠️ Le geste nommé dépend de l'onglet : une analyse se relance, une suggestion se
          regénère. Dire « relancez l'analyse » sous Suggestions enverrait faire un geste qui
          n'y change rien.
        */
        return `${a.libelle} : ${versSegments === segmentsDuProduit
          ? 'relancez l\'analyse pour le lire'
          : 'regénérez les suggestions pour le lire'}`;
      }
      const vues = new Set();
      for (const s2 of segmentsListe) {
        if (!s2) continue;
        if (a.multiple) (s2[a.cle] || []).forEach(v => vues.add(v));
        else if (s2[a.cle]) vues.add(s2[a.cle]);
      }
      // Une seule valeur se nomme : c'est elle qui explique l'absence du filtre.
      return vues.size === 1
        ? `${a.libelle} : ${[...vues][0]}`
        : `${a.libelle} : non renseigné`;
    });
    if (muets.length === 0) return null;
    return <span className="fo-filtres-muets">{muets.join(' · ')}</span>;
  };

  /** Les sélecteurs, rendus pour les seuls axes qui ont de quoi filtrer. */
  const rendreFiltresSegment = (segmentsListe, apresChangement) => {
    const axes = optionsDeFiltre(segmentsListe);
    if (axes.length === 0) return null;
    return axes.map(a => (
      <select
        key={a.cle}
        className="fo-select"
        value={filtresSegment[a.cle] || ''}
        onChange={(e) => {
          setFiltresSegment(f2 => ({ ...f2, [a.cle]: e.target.value }));
          if (apresChangement) apresChangement();
        }}
      >
        <option value="">{a.libelle}</option>
        {/*
          ⚠️ **Le chemin entier, et rien d'autre.**

          Trois formes l'ont précédé, et deux tenaient du bricolage : le dernier niveau indenté
          d'espaces insécables — invisibles —, puis le même avec un chevron — visible, et
          toujours illisible, parce qu'un même libellé revient sous plusieurs parents
          (« Coffrets - Aquarelle » trois fois). Un champ de recherche a suivi, qui réglait le
          problème au prix d'un contrôle que personne n'attend dans une barre de filtres.

          Le chemin entier lève l'ambiguïté par lui-même, et les entrées sont triées **par
          niveau d'abord** : les choix larges se présentent en tête, ce qui est l'ordre dans
          lequel on filtre.

          ⚠️ La casse est celle du marchand, jamais forcée : aucun des cinq flux du parc n'a
          de taxonomie en minuscules, et la réécrire donnerait une catégorie que son auteur ne
          reconnaîtrait pas dans son propre back-office.
        */}
        {a.valeurs.map(v => (
          <option key={v.valeur} value={v.valeur}>{v.valeur}</option>
        ))}
      </select>
    ));
  };

  const rendreModaleGeneration = () => {
    if (!generationADemander) return null;

    const { produit, lot } = generationADemander;
    const suggerables = attributs.filter(a => a.suggerable);
    const choisis = attributsAGenerer || suggerables.map(a => a.nom);
    const combien = lot ? selectedProducts.length : 1;

    const fermer = () => setGenerationADemander(null);
    const lancer = () => {
      fermer();
      if (lot) mettreEnFile(selectedProducts);
      /*
        ⚠️ **La source du marchand, jamais la ligne enrichie.** C'est ici que le défaut vivait :
        « regénérer une fiche déjà générée ne fonctionne pas ». Voir `sourceDuMarchand`.
      */
      else generateSuggestionsForProduct(sourceDuMarchand(produit));
    };

    const lancerEnLot = () => {
      fermer();
      soumettreLotIA(selectedProducts);
    };

    const basculer = (nom) => setAttributsAGenerer(() => {
      /*
        ⚠️ Décocher depuis l'état « tous » part de la liste complète : sans cela, le premier
        clic laisserait une sélection d'un seul attribut — l'inverse du geste.
      */
      const base = attributsAGenerer || suggerables.map(x => x.nom);
      return base.includes(nom) ? base.filter(n => n !== nom) : [...base, nom];
    });

    return ReactDOM.createPortal(
      <div className="fo-modale-fond" onClick={fermer}>
        <div className="fo-modale" onClick={(e) => e.stopPropagation()}>
          <div className="fo-modale-entete">
            <div>
              <h3>{lot ? `Générer ${combien} produit${combien > 1 ? 's' : ''}` : 'Générer ce produit'}</h3>
              <p>
                Seuls les attributs cochés seront (re)générés ; les autres sont conservés tels
                quels.
              </p>
            </div>
            <button type="button" className="fo-modale-fermer" onClick={fermer} title="Fermer">
              <X size={18} />
            </button>
          </div>

          <div className="fo-choix-attributs-gestes">
            {/*
              ⚠️ « Tout » remet `null`, pas la liste complète. La route lit la différence :
              sans sélection elle **remplace** les attributs en bloc — ce qui fait disparaître
              ceux que le modèle a retirés —, avec sélection elle **fusionne**. Cocher les
              seize cases ne vaut donc pas « tout », et c'est voulu.
            */}
            <button type="button" onClick={() => setAttributsAGenerer(null)}>Tout</button>
            <button type="button" onClick={() => setAttributsAGenerer([])}>Aucun</button>
            <span>{choisis.length} sur {suggerables.length}</span>
          </div>

          <div className="fo-choix-attributs-liste">
            {suggerables.map(a => {
              // `null` vaut « tous » : toutes les cases paraissent cochées.
              const coche = !attributsAGenerer || attributsAGenerer.includes(a.nom);
              return (
                <label key={a.nom} className="fo-choix-attribut">
                  <input type="checkbox" checked={coche} onChange={() => basculer(a.nom)} />
                  <span>{a.libelle}</span>
                </label>
              );
            })}
          </div>

          {/*
            ⚠️ **Le coût est annoncé avant d'être engagé**, comme pour l'examen des galeries.
            Une génération complète d'un flux entier se chiffre en dizaines de dollars, et rien
            dans l'écran ne le laisse deviner. Les chiffres viennent de relevés réels — ~5 100
            jetons d'entrée et ~1 300 de sortie par produit, tous attributs —, et la part
            retenue est proportionnelle aux attributs cochés.
          */}
          <p className="fo-modale-cout">
            {choisis.length === 0
              ? 'Aucun attribut coché : rien ne sera généré.'
              : `≈ ${Math.round(combien * (900 + 4200 * choisis.length / suggerables.length) / 1000)} k `
                + `jetons d'entrée et ${Math.round(combien * 1300 * choisis.length / suggerables.length / 1000)} k `
                + `de sortie, soit environ ${(combien * (900 + 4200 * choisis.length / suggerables.length) / 1e6 * 3
                  + combien * 1300 * choisis.length / suggerables.length / 1e6 * 15).toFixed(2)} $.`}
          </p>

          {/*
            ⚠️ **Deux voies, et le seuil les sépare.**

            En deçà de `SEUIL_LOT`, le chemin synchrone : les suggestions arrivent en une ou
            deux minutes, et l'on travaille dessus tout de suite. Au-delà, il devient
            intenable — cinq produits de front côté serveur, soit 714 tours pour un catalogue,
            et des heures d'onglet ouvert.

            Le lot asynchrone est **deux fois moins cher** et rend la main aussitôt. Il n'est
            pas gratuit pour autant : les résultats arrivent en général sous l'heure, au plus
            tard sous 24 h. C'est ce qui le rend mauvais pour trois produits, et seul tenable
            pour trois mille.
          */}
          <div className="fo-modale-gestes">
            <button type="button" className="btn-neutre" onClick={fermer}>Annuler</button>
            {lot && combien >= SEUIL_LOT ? (
              <button
                type="button"
                className="btn-ia"
                onClick={lancerEnLot}
                disabled={choisis.length === 0}
                title={'Soumis à Anthropic en traitement asynchrone : moitié prix, résultats '
                  + 'sous une heure en général. Vous pouvez fermer l\'écran.'}
              >
                <Layers size={16} />
                Soumettre un lot de {combien}
              </button>
            ) : (
              <button
                type="button"
                className="btn-ia"
                onClick={lancer}
                disabled={choisis.length === 0}
              >
                <Sparkles size={16} />
                {lot ? `Générer (${combien})` : 'Générer'}
              </button>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const rendreChoixVisuel = () => {
    if (!choixVisuel || !selectedProductDetail) return null;

    const dejaChoisies = enTableau(productEdits[choixVisuel.nom]).filter(Boolean);
    /* Les sources viennent du catalogue : une clé de liste ou une clé unique, indifféremment. */
    const brutes = [...new Set((choixVisuel.choixDepuis || [])
      .flatMap(cle => {
        const v = selectedProductDetail[cle];
        return Array.isArray(v) ? v : [v];
      })
      .filter(u => typeof u === 'string' && u.trim()))];

    /*
     * ⚠️ La proposition ne vaut que pour cet attribut-ci. `choixVisuel` sert tout attribut
     * déclarant `choixDepuis` ; en poser une sur un autre afficherait un verdict rendu sur
     * une question qui n'était pas la sienne.
     */
    const proposition = choixVisuel.nom === 'lifestyle_image_links'
      ? (productEdits._suggestion?.ambianceProposee || null)
      : null;
    const urlProposee = proposition?.url && brutes.includes(proposition.url)
      ? proposition.url
      : null;

    /* La vue désignée passe en tête : c'est elle qu'on vient voir. */
    const candidates = urlProposee
      ? [urlProposee, ...brutes.filter(u => u !== urlProposee)]
      : brutes;

    const fermer = () => setChoixVisuel(null);
    const retenir = (url) => {
      const propre = String(url).trim();
      if (!propre || dejaChoisies.includes(propre)) return fermer();
      const maximum = choixVisuel.maxElements || 10;
      updateProductField(choixVisuel.nom, [...dejaChoisies, propre].slice(0, maximum));
      fermer();
    };

    return ReactDOM.createPortal(
      <div className="fo-modale-fond" onClick={fermer}>
        <div className="fo-modale" onClick={(e) => e.stopPropagation()}>
          <div className="fo-modale-entete">
            <div>
              <h3>{choixVisuel.libelle}</h3>
              <p>Choisissez une vue déjà envoyée, ou collez l'adresse d'un visuel.</p>
            </div>
            <button type="button" className="fo-modale-fermer" onClick={fermer} title="Fermer">
              <X size={18} />
            </button>
          </div>

          {/*
            ⚠️ Le verdict est annoncé **avant** la galerie, pas posé sur une vignette seule :
            « aucune ambiance » n'a aucune vignette où s'écrire, et c'est précisément le cas
            qu'il ne faut pas taire — sinon on relance un examen déjà payé.
          */}
          {proposition && (
            <p className={`fo-ambiance-verdict${urlProposee ? ` ${proposition.confiance || 'incertaine'}` : ' aucune'}`}>
              {urlProposee ? (
                <>
                  <Sparkles size={14} />
                  <strong>Vue en situation repérée</strong>
                  <em>{LIBELLE_CONFIANCE[proposition.confiance] || 'à vérifier'}</em>
                  {proposition.motif && <span>— {proposition.motif}</span>}
                </>
              ) : (
                <>
                  <Search size={14} />
                  <strong>Galerie examinée, aucune vue en situation</strong>
                  {proposition.motif && <span>— {proposition.motif}</span>}
                </>
              )}
            </p>
          )}

          {candidates.length > 0 ? (
            <div className="fo-modale-galerie">
              {candidates.map((url) => {
                const prise = dejaChoisies.includes(url);
                const designee = url === urlProposee;
                return (
                  <button
                    type="button"
                    key={url}
                    className={`fo-vignette${prise ? ' prise' : ''}${designee ? ' designee' : ''}`}
                    onClick={() => retenir(url)}
                    disabled={prise}
                    title={prise ? 'Déjà retenue' : url}
                  >
                    {designee && <span className="fo-vignette-marque">Proposée</span>}
                    {/*
                      ⚠️ Les images viennent du site du marchand : une adresse morte ne doit pas
                      laisser un cadre vide sans explication. `onError` bascule sur le nom du
                      fichier, qui reste cliquable et dit au moins de quoi il s'agit.
                    */}
                    {/*
                      ⚠️ `vignetteDe` ne sert que l'affichage : la valeur retenue reste
                      l'adresse du marchand. Les galeries sont servies en 2 560 px pour des
                      vignettes de 150 — le downscale de 17× paraît pixellisé, et cinq
                      vignettes pèsent 1,7 Mo. Voir `texteAttribut.js`.
                    */}
                    <img
                      src={vignetteDe(url)}
                      alt=""
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <span>{nomDeFichier(url)}</span>
                    {prise && <em>déjà retenue</em>}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="fo-modale-vide">
              Ce produit n'envoie aucune autre vue : collez l'adresse ci-dessous.
            </p>
          )}

          {/*
            Le champ libre reste, et ce n'est pas un repli : une vraie scène lifestyle est
            parfois absente de la galerie du flux tout en existant sur la fiche du site.
          */}
          <form
            className="fo-modale-lien"
            onSubmit={(e) => { e.preventDefault(); retenir(new FormData(e.target).get('url')); }}
          >
            <input
              name="url"
              type="url"
              placeholder="https://… adresse d'un visuel en situation"
              autoComplete="off"
            />
            <button type="submit">Ajouter</button>
          </form>
        </div>
      </div>,
      document.body
    );
  };

  /**
   * Un groupe de sous-champs liés : une rangée par élément, dix au plus.
   *
   * Le seul widget de la fiche qui saisisse une valeur **structurée**. Il sert les
   * caractéristiques techniques, les questions-réponses, les produits apparentés et les
   * options de variante — deux ou trois sous-champs selon l'attribut, lus dans `sousChamps`.
   *
   * ⚠️ Il s'est appelé `triplet` tant qu'il ne servait qu'à `product_detail`. Les paires
   * `question / réponse` et `option / valeur` ont montré que le nom mentait.
   *
   * Trois décisions, et chacune se voit à l'écran :
   *
   * 1. **Les trois champs viennent de `sousChamps`**, servis par le serveur. Un quatrième
   *    sous-champ entrerait sans qu'on touche à cette fonction — c'est le même parti que le
   *    reste de la table d'attributs.
   * 2. **Un sous-champ obligatoire le dit**, par son astérisque et son placeholder. Sans quoi
   *    une rangée s'abandonne à moitié remplie, est écartée en silence à l'écriture du flux,
   *    et l'utilisateur croit avoir saisi une caractéristique qui n'y est pas.
   * 3. **Un sous-champ facultatif est plus étroit** : la section d'une caractéristique
   *    (« Dimensions », « Entretien ») est un intertitre que plusieurs rangées se partagent,
   *    pas une donnée au même rang que les deux autres. À largeur égale, on la remplissait à
   *    chaque ligne. La règle porte sur `obligatoire`, pas sur le nom du sous-champ : les
   *    autres attributs n'ont que des sous-champs exigés, et gardent donc trois tiers égaux.
   */
  const rendreGroupe = (attribut) => {
    const rangees = enTableau(productEdits[attribut.nom], attribut.sousChamps);
    const maximum = attribut.maxElements || 10;
    const sousChamps = attribut.sousChamps || [];

    const modifier = (rang, cle, texte) => {
      const suivantes = rangees.map((rangee, i) => (
        i === rang ? { ...rangee, [cle]: texte } : rangee
      ));
      updateProductField(attribut.nom, suivantes);
    };

    return (
      <div className="fo-caracteristiques">
        {rangees.map((rangee, rang) => (
          <div key={rang} className="fo-caracteristique">
            <span className="fo-point-fort-rang">{rang + 1}</span>
            <div className="fo-caracteristique-champs">
              {sousChamps.map(sc => (
                <input
                  key={sc.cle}
                  type="text"
                  className={sc.obligatoire ? '' : 'fo-caracteristique-section-saisie'}
                  value={rangee?.[sc.cle] || ''}
                  maxLength={sc.maxLongueur}
                  placeholder={sc.obligatoire ? `${sc.libelle} *` : `${sc.libelle} (facultatif)`}
                  onChange={(e) => modifier(rang, sc.cle, e.target.value)}
                />
              ))}
            </div>
            <button
              type="button"
              className="fo-retirer"
              title={attribut.libelleElement?.retirer || 'Retirer cette rangée'}
              onClick={() => updateProductField(
                attribut.nom,
                rangees.filter((_, i) => i !== rang)
              )}
            >
              <X size={14} />
            </button>
          </div>
        ))}

        {rangees.length < maximum && (
          <button
            type="button"
            className="fo-ajouter"
            /* Une rangée naît vide — et non préremplie de la section de la précédente : deux
               caractéristiques voisines n'appartiennent pas toujours à la même section, et une
               valeur posée d'office est une valeur que personne n'a choisie.

               ⚠️ **Vide ne veut pas dire sans clés.** `{}` paraissait le choix naturel, mais
               `enTableau` écarte toute rangée qui ne porte aucun des sous-champs attendus —
               règle écrite pour chasser les provenances de déduction prises pour des rangées.
               La rangée neuve disparaissait donc à l'instant où elle naissait, et le bouton
               semblait inopérant. Déclarer les champs à vide la rend reconnaissable sans rien
               préremplir. */
            onClick={() => updateProductField(attribut.nom, [
              ...rangees,
              Object.fromEntries(sousChamps.map(sc => [sc.cle, '']))
            ])}
          >
            <Plus size={14} />
            {/* Le libellé vient du catalogue : « Ajouter une caractéristique » s'affichait sur
                les questions-réponses et les produits apparentés, qui n'en sont pas. */}
            {attribut.libelleElement?.ajouter || 'Ajouter une rangée'}
          </button>
        )}

        <p className="fo-aide">
          {/* Comme le bouton : « caractéristiques » s'affichait sous les questions-réponses. */}
          {maximum} {attribut.libelleElement?.pluriel || 'rangées'} au plus. Le nom et la valeur
          sont exigés par Google — une rangée incomplète n’est pas écrite dans le flux.
        </p>
      </div>
    );
  };

  /**
   * Le gabarit réellement en vigueur pour le produit ouvert.
   *
   * ⚠️ **Une seule source, parce que deux divergeaient.** Le panneau affichait le gabarit
   * déduit du titre — sept briques, leurs valeurs, leurs couleurs — pendant que l'aperçu
   * envoyait `productTitreGabarit`, resté `null` faute d'avoir été touché. Le serveur
   * composait donc sans gabarit et répondait « aucun élément renseigné », sous une rangée de
   * cinq pastilles bien remplies.
   *
   * Tant que personne n'a choisi, le gabarit **est** ce que le titre porte : c'est ce que
   * l'écran montre, et c'est donc ce qu'il faut envoyer.
   */
  const gabaritEffectif = useMemo(() => {
    if (Array.isArray(productTitreGabarit)) return productTitreGabarit;
    const composants = productEdits._suggestion?.titreComposants;
    if (!composants) return null;
    return gabaritDepuisLeTitre(productEdits.title, composants, productEdits.brand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTitreGabarit, productEdits._suggestion, productEdits.title, productEdits.brand]);

  /**
   * L'aperçu du titre que produirait le gabarit en cours de composition.
   *
   * ⚠️ **Demandé au serveur, et non calculé ici.** Composer un titre demande la table des
   * briques, leurs liaisons, la borne de longueur, le refus de perte et la pose de la
   * marque : recopier tout cela ferait deux implémentations à tenir d'accord, et un aperçu
   * qui diverge du résultat est pire que pas d'aperçu — il promet un titre qu'on n'obtiendra
   * pas.
   *
   * Après une courte pause : chaque flèche et chaque ajout changent le gabarit, et un appel
   * par clic serait une requête pour un état qu'on traverse.
   */
  useEffect(() => {
    const suggestion = productEdits._suggestion;
    if (!suggestion?.id) { setApercuTitre(null); return undefined; }

    const minuteur = setTimeout(async () => {
      try {
        const reponse = await axios.post(
          `${API_URL}/api/tools/feed-optimizer/titre-apercu`,
          {
            suggestionId: suggestion.id,
            titreGabarit: gabaritEffectif?.length ? gabaritEffectif : null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!reponse.data.success) return;
        setApercuTitre(reponse.data);

        /*
         * ⚠️ **Le titre change sous les yeux, et c'est tout l'intérêt** : une liste de briques
         * ne dit rien tant qu'on n'a pas vu la phrase qu'elle produit, et la lire dans un
         * encart en bas du panneau oblige à faire l'aller-retour avec le champ.
         *
         * Rien n'est enregistré pour autant : le champ est la zone d'édition, « Appliquer »
         * est ce qui écrit en base. C'est le même partage que partout dans cette fiche.
         */
        /*
         * ⚠️ On écrit `titreCompose`, pas `titre` : la composition est rendue même quand le
         * garde-fou la refuse. Refuser protège le lot ; devant un produit, on montre le
         * résultat et son coût, et c'est la personne qui regarde qui décide. Voir la route.
         *
         * `gabaritTouche` reste la condition : on ne réécrit pas un titre que personne n'a
         * demandé à changer.
         */
        const compose = reponse.data.titreCompose;
        if (!compose || !gabaritTouche.current) return;

        setProductEdits(prev => {
          const actuel = prev.title || '';
          // On ne touche pas à ce que quelqu'un a tapé : voir `titrePoseParGabarit`.
          const aNous = titrePoseParGabarit.current === null
            || titrePoseParGabarit.current === actuel;
          if (!aNous || actuel === compose) return prev;

          titrePoseParGabarit.current = compose;
          return {
            ...prev,
            title: compose,
            _acceptedFields: { ...prev._acceptedFields, title: 'manual' }
          };
        });
      } catch {
        // Un aperçu qui échoue ne doit rien casser : la rangée reprend son texte d'aide.
        setApercuTitre(null);
      }
    }, 300);

    return () => clearTimeout(minuteur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gabaritEffectif, productEdits._suggestion?.id, token]);

  /**
   * Recompose les titres à partir des morceaux déjà relevés.
   *
   * Jumelle de `appliquerPositionMarque` : sans `suggestionIds`, le flux entier est repris et
   * le gabarit devient son défaut ; avec, seul le produit visé bouge. **Aucun appel à l'IA** —
   * les morceaux ont été relevés une fois, à la génération.
   */
  const appliquerGabaritTitre = async (gabarit, suggestionIds = null) => {
    if (readOnly) return;
    try {
      setApplyingTitreGabarit(true);
      setError(null);

      const reponse = await axios.post(
        `${API_URL}/api/tools/feed-optimizer/titre-gabarit`,
        {
          merchantId: selectedAccount,
          feedLabel: selectedFeedLabel,
          titreGabarit: gabarit,
          ...(suggestionIds ? { suggestionIds } : { setAsDefault: true })
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!reponse.data.success) return null;

      if (!suggestionIds) {
        setTitreGabarit(gabarit);
        // Les titres viennent de changer en base : sans rechargement, la liste resterait
        // celle d'avant et le réglage paraîtrait sans effet.
        await loadSuggestions();
      }
      return reponse.data;

    } catch (e) {
      setError(messageErreur(e, 'Erreur lors de la recomposition des titres'));
      return null;
    } finally {
      setApplyingTitreGabarit(false);
    }
  };

  /**
   * Le panneau de composition du titre, frère de celui de la marque.
   *
   * Deux listes plutôt qu'un glisser-déposer : les briques retenues, dans l'ordre, avec leurs
   * flèches ; puis celles qui restent disponibles. Le glisser-déposer demanderait une
   * dépendance et ne serait pas plus clair sur huit éléments — et il se teste mal.
   *
   * ⚠️ **Rien n'est appliqué tant qu'on n'a pas cliqué.** Composer un ordre est un geste à
   * plusieurs temps ; recomposer tout un flux à chaque flèche serait une requête par clic.
   */
  /**
   * Le glissement en cours : d'où part la brique, et au-dessus de laquelle on survole.
   *
   * ⚠️ **Un état, et non un `ref`** — c'était un `ref` pour éviter un rendu par événement,
   * mais c'est précisément du rendu qu'on a besoin : sans lui, rien ne bouge pendant qu'on
   * déplace, et on lâche la pastille sans savoir où elle atterrira.
   *
   * `onDragOver` se déclenche plusieurs fois par seconde sur la même cible : on n'écrit donc
   * que si le survol change de pastille.
   */
  const [glissement, setGlissement] = useState({ depart: null, survol: null });

  /**
   * Ouvrir la retouche du titre, sur la proposition de l'IA.
   *
   * ⚠️ **C'est ici que le titre de référence se fixe.** Le champ partait du titre du **flux**
   * tant que la proposition n'était pas acceptée : les éléments décrivaient donc le flux
   * pendant qu'on lisait la proposition juste au-dessus, et leur ordre paraissait faux.
   *
   * Retoucher une proposition, c'est partir d'elle. Le champ la reçoit, le panneau d'éléments
   * la lit, et « Revenir à la proposition IA » y ramène.
   */
  const ouvrirEditionTitre = (titrePropose) => {
    const suggestion = productEdits._suggestion;
    const depart = suggestion?.editedTitle || titrePropose || suggestion?.suggestedTitle;

    if (depart && depart !== productEdits.title) {
      titrePoseParGabarit.current = depart;
      /*
       * ⚠️ **Ouvrir l'atelier ne rend PAS de verdict.**
       *
       * On y posait `title: 'accepted'`, et le bloc des trois boutons n'est rendu que tant
       * qu'aucun verdict ne l'est : cliquer « Modifier » faisait donc disparaître « Accepter »,
       * « Refuser » **et** « Modifier » lui-même. L'atelier s'ouvrait et ne se refermait plus —
       * c'est le défaut signalé.
       *
       * Et le silence était la moitié du problème : « Modifier » se lit « laisse-moi regarder »,
       * pas « j'accepte ». On repartait avec une proposition acceptée sans l'avoir décidé.
       *
       * Poser le titre dans le champ reste nécessaire — c'est le point de départ de la
       * retouche, et le panneau d'éléments décrit **ce** titre. Mais c'est un point de départ,
       * pas une décision : le verdict se rend dans l'atelier, il ne le précède pas.
       */
      setProductEdits(prev => ({ ...prev, title: depart }));
    }
    gabaritTouche.current = false;
    setEditionTitre(true);
  };

  /**
   * Rendre au champ Titre la proposition de l'IA, et au gabarit sa composition.
   *
   * ⚠️ **Le pendant indispensable de l'écriture en direct.** Le gabarit réécrit le champ à
   * chaque brique ajoutée : sans retour en arrière, essayer un élément coûterait la
   * proposition du modèle — et l'on n'oserait donc pas essayer.
   *
   * Rien n'est enregistré ici, pas plus qu'à l'aller : on repose l'état de départ, et c'est
   * « Appliquer » qui écrit en base. Une suggestion déjà enregistrée reste donc intacte tant
   * qu'on n'a pas appliqué.
   */
  const revenirALaPropositionIA = () => {
    const suggestion = productEdits._suggestion;
    if (!suggestion) return;

    const titreIA = suggestion.editedTitle || suggestion.suggestedTitle || '';
    gabaritTouche.current = false;
    titrePoseParGabarit.current = null;

    // `null` plutôt qu'un gabarit recalculé : le panneau le relira du titre qu'on repose.
    setProductTitreGabarit(suggestion.titreGabarit || null);
    setProductEdits(prev => ({ ...prev, title: titreIA }));
  };

  /**
   * Le panneau de composition du titre.
   *
   * ## ⚠️ Montrer la valeur, pas seulement le nom
   *
   * Il listait « Coupe », « Matière », « Motif » — sans dire ce que **ce produit** y met. On
   * choisissait donc à l'aveugle : rien ne distinguait une brique qui apporterait « Fluide »
   * d'une brique vide qui ne changerait rien au titre. Chaque pastille porte maintenant sa
   * valeur, et celle qui n'en a pas le dit et ne se laisse pas ajouter.
   *
   * Les valeurs viennent de `titreComposants`, relevé à la génération. Au niveau du flux il
   * n'y a pas de produit ouvert : les pastilles n'y portent que leur nom, et c'est honnête —
   * un réglage de flux ne peut pas montrer la valeur de quatre mille produits.
   *
   * ## Le glisser-déposer
   *
   * `draggable` et les événements HTML5, sans dépendance. Les flèches restent : elles sont la
   * seule voie au clavier, et le glissement échoue sur un écran tactile.
   */
  const rendreGabaritTitre = (gabaritCourant, poser, suggestionIds = null) => {
    /*
     * ⚠️ Les composants **complétés** par le serveur, s'il les a déjà rendus.
     *
     * Ceux de la suggestion n'ont pas toujours de nom de produit : le modèle l'oublie, et
     * l'écran affichait alors « Nom du produit — » en grisé sur un titre qui en a bien un.
     * La route d'aperçu attribue le reste du titre à cette brique ; on adopte sa version dès
     * qu'elle arrive, sans la recalculer ici.
     */
    const composants = apercuTitre?.composants
      || productEdits._suggestion?.titreComposants
      || null;
    // Le niveau produit seul connaît les valeurs — voir l'en-tête.
    const auProduit = Boolean(suggestionIds && composants);

    /*
     * ⚠️ **Le gabarit de départ se lit sur le titre AFFICHÉ, au moment du rendu.**
     *
     * Il était déduit à l'ouverture de la fiche, depuis le titre de la *suggestion* — alors
     * que les pastilles colorent leur état contre `productEdits.title`, c'est-à-dire le
     * **champ**. Les deux divergent dès qu'on ouvre une fiche depuis l'onglet Analyse, où le
     * champ porte le titre du flux et non la proposition : le panneau annonçait « aucun
     * élément » pendant que cinq pastilles s'affichaient en vert.
     *
     * Une seule source, et au plus près de l'affichage : il ne peut plus y avoir d'écart.
     */
    // Au produit, `gabaritEffectif` porte déjà le déduit — voir sa déclaration. Au flux, il
    // n'y a pas de titre à lire : seul un gabarit choisi vaut.
    const actives = Array.isArray(gabaritCourant)
      ? gabaritCourant
      : (auProduit ? (gabaritEffectif || []) : []);

    /*
     * ⚠️ **Trois états, et c'est la distinction qu'on venait chercher.** La liste ne disait
     * que « Coupe » ou « Matière » : rien ne séparait ce que le titre de l'IA porte déjà de
     * ce qu'on pourrait y ajouter, ni de ce que ce produit ne renseigne pas du tout.
     *
     *   · « déjà au titre » — la valeur y figure, l'ajouter ne changerait rien
     *   · « à ajouter »     — la valeur existe et manque au titre : c'est le gain
     *   · « non relevé »    — ce produit n'a pas cette donnée
     */
    const etatDe = (cle, valeur) => {
      if (!auProduit) return null;
      if (!valeur) return 'absente';
      return dejaDansLeTitre(productEdits.title, valeur) ? 'presente' : 'manquante';
    };

    /** Ce que la brique mettrait dans le titre de ce produit, ou `null`. */
    const valeurDe = (cle) => {
      if (!auProduit) return null;
      if (cle === 'marque') return String(productEdits.brand || '').trim() || null;
      const brute = composants[cle];
      if (Array.isArray(brute)) return brute.filter(Boolean).join(' ') || null;
      return String(brute || '').trim() || null;
    };

    const deplacer = (rang, pas) => {
      const suivant = [...actives];
      const cible = rang + pas;
      if (cible < 0 || cible >= suivant.length) return;
      [suivant[rang], suivant[cible]] = [suivant[cible], suivant[rang]];
      poser(suivant);
    };

    const deposer = (rangCible) => {
      const { depart } = glissement;
      setGlissement({ depart: null, survol: null });
      if (depart === null || depart === rangCible) return;
      const suivant = [...actives];
      const [brique] = suivant.splice(depart, 1);
      suivant.splice(rangCible, 0, brique);
      poser(suivant);
    };

    return (
      <div className="brand-position-row fo-gabarit-row">
        <span className="brand-position-label">
          <Tag size={14} /> Éléments du titre
        </span>

        <div className="fo-gabarit-colonnes">
          <div className="fo-gabarit-groupe">
            <span className="fo-gabarit-titre-groupe">
              Dans le titre en cours
              {actives.length > 1 && <em> · glissez pour réordonner</em>}
            </span>
            <div
              className="fo-gabarit-actives"
              onDragOver={(e) => e.preventDefault()}
            >
              {actives.length === 0 && (
                /*
                  ⚠️ Ce cas ne veut plus dire « on n'a rien lu » : le gabarit part désormais de
                  la composition réelle du titre (voir `gabaritDepuisLeTitre`). Vide, il dit
                  donc que le titre ne porte **aucune** des valeurs relevées — ou qu'il n'y a
                  pas de morceaux relevés du tout, ce que l'aperçu précise juste en dessous.
                */
                <span className="fo-gabarit-vide">
                  {auProduit
                    ? 'Aucun élément relevé ne figure dans ce titre.'
                    : 'Aucun élément : les titres restent ceux de l’IA.'}
                </span>
              )}
              {actives.map((cle, rang) => {
                const valeur = valeurDe(cle);
                const etat = etatDe(cle, valeur);
                return (
                  <span
                    key={cle}
                    /*
                      Trois états visuels pendant un glissement : la pastille qu'on déplace
                      s'efface, et celle qu'on survole s'écarte — du côté d'où vient la
                      brique, pour que l'espace qui s'ouvre soit celui qu'elle occupera.
                    */
                    className={[
                      'fo-gabarit-brique',
                      etat ? `etat-${etat}` : '',
                      glissement.depart === rang ? 'en-glissement' : '',
                      (glissement.depart !== null && glissement.depart !== rang
                        && glissement.survol === rang)
                        ? (glissement.depart < rang ? 'inserer-apres' : 'inserer-avant')
                        : ''
                    ].filter(Boolean).join(' ')}
                    title={MOTS_ETAT_BRIQUE[etat]}
                    draggable
                    onDragStart={() => setGlissement({ depart: rang, survol: rang })}
                    onDragOver={(e) => {
                      e.preventDefault();
                      // Plusieurs fois par seconde sur la même cible : on n'écrit qu'au
                      // changement, sinon c'est un rendu par battement de souris.
                      if (glissement.survol !== rang) {
                        setGlissement(prev => ({ ...prev, survol: rang }));
                      }
                    }}
                    onDrop={() => deposer(rang)}
                    onDragEnd={() => setGlissement({ depart: null, survol: null })}
                  >
                    <button type="button" title="Vers la gauche"
                      disabled={rang === 0} onClick={() => deplacer(rang, -1)}>‹</button>
                    <span className="fo-gabarit-corps">
                      <span className="fo-gabarit-nom">{libelleBrique(cle)}</span>
                      {auProduit && (
                        <span className="fo-gabarit-valeur">
                          {valeur || 'non relevé'}
                        </span>
                      )}
                    </span>
                    <button type="button" title="Vers la droite"
                      disabled={rang === actives.length - 1} onClick={() => deplacer(rang, 1)}>›</button>
                    <button type="button" className="fo-gabarit-retirer" title="Retirer du titre"
                      onClick={() => poser(actives.filter(c => c !== cle))}>
                      <X size={12} />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>

          {/*
            ⚠️ **Les gestes suivent la dernière brique, dans le même flux.**

            « Appliquer » occupait une ligne à lui seul, aligné à droite sous un rang de
            pastilles : on le cherchait. À la suite des briques, il est là où le regard
            termine de les parcourir — et il revient à la ligne avec elles quand la largeur
            manque, au lieu de rester seul en bas.

            ⚠️ Le groupe est rendu **même quand rien ne reste à ajouter** : les gestes y
            vivent, et les huit briques posées faisaient disparaître le bouton avec le titre
            « À ajouter ».
          */}
          <div className="fo-gabarit-groupe">
            {BRIQUES_TITRE.some(b => !actives.includes(b.cle)) && (
              <span className="fo-gabarit-titre-groupe">À ajouter</span>
            )}
            <div className="fo-gabarit-disponibles">
                {BRIQUES_TITRE.filter(b => !actives.includes(b.cle)).map(b => {
                  const valeur = valeurDe(b.cle);
                  const etat = etatDe(b.cle, valeur);
                  // ⚠️ Une brique sans valeur n'est pas proposée : l'ajouter ne changerait
                  // rien au titre, et laisser cliquer sur un geste sans effet est pire que
                  // de l'interdire en disant pourquoi.
                  const inerte = etat === 'absente';
                  return (
                    <button
                      key={b.cle}
                      type="button"
                      className={`fo-gabarit-ajouter ${etat ? `etat-${etat}` : ''}`}
                      disabled={inerte}
                      title={MOTS_ETAT_BRIQUE[etat]}
                      onClick={() => poser([...actives, b.cle])}
                    >
                      <Plus size={11} />
                      <span className="fo-gabarit-nom">{b.libelle}</span>
                      {auProduit && <span className="fo-gabarit-valeur">{valeur || '—'}</span>}
                    </button>
                  );
                })}
              <div className="fo-gabarit-gestes">
                {/*
                  ⚠️ Revenir au défaut du flux : le seul geste qui défait une exception. Il ne paraît
                  que s'il y en a une — sinon il promettrait d'annuler ce qui n'existe pas.
                */}
                {suggestionIds && productEdits._suggestion?.titreGabarit && (
                  <button
                    type="button"
                    className="fo-gabarit-annuler"
                    onClick={async () => {
                      /* `null` dit « suit le flux » : on repose donc le gabarit du flux, et la
                         suggestion cesse de porter le sien. */
                      await appliquerGabaritTitre(titreGabarit, suggestionIds);
                      setProductTitreGabarit(titreGabarit || null);
                    }}
                  >
                    <RotateCcw size={13} /> Revenir au gabarit du flux
                  </button>
                )}

                {/*
                  ⚠️ Le retour en arrière, et il n'est pas facultatif : le gabarit **réécrit** le
                  champ Titre à chaque brique ajoutée. Sans moyen de revenir, essayer un élément
                  coûterait la proposition de l'IA — on n'oserait donc pas essayer.

                  Il ne paraît qu'une fois le gabarit touché : avant, il n'y a rien à annuler.
                */}
                {suggestionIds && gabaritTouche.current && (
                  <button
                    type="button"
                    className="fo-gabarit-annuler"
                    onClick={() => revenirALaPropositionIA()}
                  >
                    <RotateCcw size={13} /> Revenir à la proposition IA
                  </button>
                )}
                {/*
                  ⚠️ **Pas d'« Appliquer » sur une fiche : « Enregistrer » suffit.**

                  Le champ Titre se met déjà à jour à chaque brique déplacée, et « Enregistrer » sauve
                  ce qu'on voit — gabarit compris. Le second bouton réécrivait en base un titre que le
                  champ portait déjà, et ne faisait **rien** dès qu'on avait retouché ce titre à la
                  main : on cliquait, on recliquait, et rien ne le disait.

                  Il garde tout son sens dans la barre du flux, où il recompose un lot sans ouvrir
                  chaque fiche — et où préserver les retouches reste la bonne règle.
                */}
                {!suggestionIds && (
                  <button
                    type="button"
                    className="brand-position-btn active"
                    disabled={applyingTitreGabarit}
                    onClick={() => appliquerGabaritTitre(actives.length > 0 ? actives : null, null)}
                  >
                    {applyingTitreGabarit ? 'Recomposition…' : 'Appliquer'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>


        {suggestionIds && apercuTitre && (
          <div className="fo-gabarit-apercu">
            {apercuTitre.sansComposants ? (
              <span className="fo-gabarit-apercu-refus">
                Ce produit n’a pas de morceaux relevés : il a été généré avant ce réglage.
                Régénérez-le pour pouvoir composer son titre.
              </span>
            ) : apercuTitre.titreCompose ? (
              <>
                <span className="fo-gabarit-apercu-mesure">
                  Titre recomposé · {apercuTitre.titreCompose.length} caractères
                  {/*
                    ⚠️ **« Détails ne tient pas sous 150 » à côté de « 47 caractères » ne se
                    comprend pas.** Le lecteur voit 47, en conclut qu'il reste de la place, et
                    cherche la panne ailleurs — c'est ce qui est arrivé. Le message nomme donc
                    la longueur qu'aurait le titre complet, qui est le chiffre dont il parle.

                    Et une liste posée pour partie n'est pas une liste absente : « 2 des 3
                    détails » dit qu'il y a bien des détails dans le titre, et que le reste
                    n'entrait pas.
                  */}
                  {apercuTitre.partielles?.length > 0
                    && ` · ${apercuTitre.partielles
                      .map(p => `${p.poses} des ${p.total} ${libelleBrique(p.cle).toLowerCase()}`)
                      .join(', ')} — le reste ne tenait pas`}
                  {apercuTitre.omises.length > 0
                    && ` · ${apercuTitre.omises.map(libelleBrique).join(', ')} : il ne reste pas
                      assez de place sous les 150 caractères du titre`.replace(/\s+/g, ' ')}
                </span>
                {/*
                  ⚠️ Ce que la recomposition coûte, dit sans l'empêcher. Le titre est déjà dans
                  le champ : on annonce ce qui en a disparu pour qu'on puisse le remettre à la
                  main, ou revenir à la proposition de l'IA.
                */}
                {apercuTitre.perdus.length > 0 && (
                  <span className="fo-gabarit-apercu-refus">
                    Le titre précédent portait aussi «&nbsp;{apercuTitre.perdus.join(' », « ')}
                    &nbsp;». En lot, ces produits seront laissés intacts.
                  </span>
                )}
              </>
            ) : (
              <span className="fo-gabarit-apercu-refus">
                Aucun élément du gabarit n’est renseigné pour ce produit.
              </span>
            )}
          </div>
        )}

        {/*
          ⚠️ **Deux gabarits coexistent, et il faut dire lequel agit.**

          Le flux porte un gabarit par défaut (`FeedOptimizerConfig.titreGabarit`), un produit
          peut en avoir un propre (`FeedOptimizerSuggestion.titreGabarit`). Rien ne disait
          lequel s'appliquait : on réglait une fiche sans savoir si on venait d'en faire une
          exception, et sans moyen de revenir.

          ⚠️ **`null` sur la suggestion veut dire « suit le flux »** — et c'est pourquoi la
          route ne l'écrit qu'au réglage produit par produit. L'écrire en lot, comme elle le
          faisait, donnait à 5 605 fiches le même réglage que la configuration : plus rien ne
          distinguait celles qui en déviaient.
        */}
        {suggestionIds && (
          <p className="fo-gabarit-portee">
            {productEdits._suggestion?.titreGabarit
              ? <>Gabarit propre à ce produit — il ne suit plus celui du flux.</>
              : <>Suit le gabarit du flux{gabaritPose(titreGabarit) ? '' : ', qui n’en a aucun'}.</>}
          </p>
        )}


        {/*
          ⚠️ **Le même panneau règle deux portées, et il doit le dire.**

          Sans `suggestionIds`, il pose le **défaut du flux** — il recompose tous les titres et
          vaut pour les générations suivantes. Sur une fiche, il ne règle que ce produit. Rien
          ne distinguait les deux : la fiche annonce sa portée depuis peu, la barre du flux
          restait muette, et on pouvait croire ne régler qu'un produit en en réglant 5 605.
        */}
        <span className="brand-position-hint">
          {suggestionIds
            ? 'Recompose le titre sans rappeler l’IA, à partir des morceaux relevés à la génération.'
            : `Gabarit par défaut du flux : il recompose ${suggestions.length > 1 ? `les ${suggestions.length} titres suggérés` : 'le titre suggéré'} `
              + 'et vaut pour les générations suivantes, sans rappeler l’IA. Une fiche peut s’en écarter.'}
        </span>
      </div>
    );
  };


  /** Prévenir avant de perdre une saisie — c'est la navigation qui crée ce risque. */
  const confirmerAbandon = () => (
    !editionsEnCours
    || window.confirm('Des modifications n’ont pas été enregistrées sur cette fiche. Les abandonner ?')
  );

  /**
   * La ligne qu'on vient de quitter, pour la retrouver au retour.
   *
   * Sans elle, revenir à la liste rendait cent lignes à parcourir depuis le début pour retomber
   * sur le produit qu'on venait de corriger. Vide quand la fiche vient de l'onglet Suggestions :
   * le produit n'y appartient à aucune liste.
   */
  const ligneARetrouver = useRef(null);

  const fermerFiche = () => {
    if (!confirmerAbandon()) return;
    ligneARetrouver.current = indexProduitOuvert >= 0 ? selectedProductDetail.offerId : null;
    closeProductDetail();
  };

  /*
   * Le défilement suit la bascule liste ↔ fiche.
   *
   * C'est le défaut qu'a introduit le passage de la modale à la page. Une modale se posait au
   * centre de l'écran quel que soit le défilement ; une fiche prend la place de la liste. En
   * ouvrant un produit situé à trois mille pixels du haut, on restait à trois mille pixels du
   * haut d'une page qui n'en fait plus que mille huit cents — c'est-à-dire **devant une page
   * vide**, la barre collante pour seul repère.
   *
   * L'effet plutôt que le gestionnaire de clic : la fiche n'est pas encore rendue au moment où
   * on la demande, et ce même effet sert l'ouverture comme le passage au produit suivant.
   */
  useEffect(() => {
    if (!selectedProductDetail) return;
    // Jusqu'au bord : la fiche est seule dans la page, et le titre de l'outil tient au-dessus.
    const defilant = conteneurDefilant(ficheRef.current);
    if (defilant) defilant.scrollTop = 0;
  }, [selectedProductDetail?.offerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedProductDetail) return;

    const offre = ligneARetrouver.current;
    ligneARetrouver.current = null;

    const ligne = offre && listeProduitsRef.current
      ? listeProduitsRef.current.querySelector(`[data-offre="${CSS.escape(offre)}"]`)
      : null;

    if (ligne) {
      // Une ligne en `display: contents` n'a pas de boîte à mesurer : c'est sa première cellule
      // qui porte la position. La marge laisse la barre de filtres au-dessus.
      amenerEnVue(ligne.firstElementChild, 120);
    } else {
      const defilant = conteneurDefilant(outilRef.current);
      if (defilant) defilant.scrollTop = 0;
    }
  }, [selectedProductDetail]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Ouvrir le produit de rang `rang`.
   *
   * La page de la liste suit le rang : en revenant, on retrouve le produit à sa place et non la
   * première page — c'est ce qui rend la navigation utilisable au-delà des cent premières lignes.
   */
  const ouvrirProduitAuRang = (rang) => {
    const produit = sortedProducts[rang];
    /*
      ⚠️ **Les flèches n'ouvrent pas ce que le clic refuse.** Les deux boutons de la ligne sont
      désarmés sur un produit qu'un traitement tient ; sans cette garde, « Suivant » y entrerait
      quand même, et la fiche s'ouvrirait sur une version que le modèle est en train de
      remplacer.

      ⚠️ Elle SAUTE plutôt qu'elle n'arrête : s'arrêter là enfermerait le lecteur devant une
      ligne verrouillée, sans autre issue qu'Échap. Un produit tenu se passe, comme s'il
      n'était pas là.
    */
    if (produit && offerIdsVerrouilles.has(String(produit.offerId))) {
      const sens = rang > indexProduitOuvert ? 1 : -1;
      const suivant = sortedProducts.findIndex((p2, i) => (sens > 0 ? i > rang : i < rang)
        && !offerIdsVerrouilles.has(String(p2.offerId)));
      // `findIndex` lit en avant : en arrière, il faut le dernier qui précède.
      const cible = sens > 0 ? suivant : sortedProducts.reduce(
        (garde, p2, i) => (i < rang && !offerIdsVerrouilles.has(String(p2.offerId)) ? i : garde), -1);
      if (cible >= 0) ouvrirProduitAuRang(cible);
      return;
    }
    if (!produit || !confirmerAbandon()) return;
    setProductsCurrentPage(Math.floor(rang / PRODUCTS_PER_PAGE) + 1);
    openProductDetail(produit);
  };

  /**
   * Les flèches du clavier passent d'un produit à l'autre, Échap revient à la liste.
   *
   * L'écouteur se retire dès qu'un champ a le focus : voler la flèche gauche à quelqu'un qui
   * corrige un titre serait pire que de ne rien offrir.
   */
  useEffect(() => {
    if (!ficheOuverte) return undefined;

    const auClavier = (evenement) => {
      if (evenement.metaKey || evenement.ctrlKey || evenement.altKey || evenement.shiftKey) return;
      if (evenement.target.closest?.('input, textarea, select, [contenteditable="true"]')) return;

      if (evenement.key === 'ArrowLeft') ouvrirProduitAuRang(indexProduitOuvert - 1);
      else if (evenement.key === 'ArrowRight') ouvrirProduitAuRang(indexProduitOuvert + 1);
      else if (evenement.key === 'Escape') fermerFiche();
    };

    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [ficheOuverte, indexProduitOuvert, sortedProducts, editionsEnCours]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Un clic sur un en-tête : même colonne, on inverse ; colonne neuve, on prend son sens. */
  const basculerTriProduits = (colonne) => {
    setProductsCurrentPage(1);
    setTriProduits(actuel => (
      actuel.cle === colonne.cle
        ? { cle: colonne.cle, sens: actuel.sens === 'asc' ? 'desc' : 'asc' }
        : { cle: colonne.cle, sens: colonne.sens }
    ));
  };

  /**
   * Cocher toute la page, ou la décocher.
   *
   * La page, et non le flux entier : le bouton « Générer suggestions IA » agit sur la sélection,
   * et un « tout cocher » portant sur neuf cent soixante-six produits mettrait un appel à l'IA
   * par produit derrière un seul clic. Cent, c'est ce que l'écran montre.
   */
  /*
    ⚠️ **Les orphelines sortent de la sélection en masse**, pour la raison dite sur la case
    d'une ligne. Et l'indicateur se compte sur le **même** périmètre que le geste : les
    inclure dans le décompte et les exclure du clic rendrait une case qui ne se coche jamais
    sur une page qui en contient une — on cliquerait sans fin.
  */
  const selectionnablesDeLaPage = paginatedProducts.filter(
    p => !p._horsAnalyse && !offerIdsVerrouilles.has(String(p.offerId)));

  const pageEntiereCochee = selectionnablesDeLaPage.length > 0
    && selectionnablesDeLaPage.every(p => selectedProducts.includes(p.offerId));

  const basculerPageEntiere = () => {
    const identifiants = selectionnablesDeLaPage.map(p => p.offerId);
    setSelectedProducts(prev => (
      pageEntiereCochee
        ? prev.filter(id => !identifiants.includes(id))
        : [...new Set([...prev, ...identifiants])]
    ));
  };

  /**
   * L'icône d'une gravité.
   *
   * Elle portait `className="w-4 h-4 text-red-500"`, c'est-à-dire des classes Tailwind — et
   * **Tailwind n'est pas installé dans ce projet**. Sans `w-4`, Lucide rendait ses 24 pixels par
   * défaut dans une pastille de texte à 12px : c'est ce qui portait les étiquettes de problème à
   * 32 pixels de haut. Quant à `.text-blue-500`, `ToolsView.css` la redéfinit à #1a1a1a : la
   * gravité « information » s'affichait en noir, donc sans gravité lisible.
   *
   * La taille est un paramètre, la couleur un ton de la charte.
   */
  const getSeverityIcon = (severity, taille = 13) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle size={taille} style={{ color: '#b91c1c', flexShrink: 0 }} />;
      case 'warning':
        return <AlertTriangle size={taille} style={{ color: '#b45309', flexShrink: 0 }} />;
      case 'info':
        return <Info size={taille} style={{ color: '#6b7280', flexShrink: 0 }} />;
      /*
       * ⚠️ `optionnel` se constate sans se reprocher : zéro point au score, et une icône qui
       * ne ressemble à aucune alerte. Le point d'interrogation dit « vous pourriez », là où
       * le ⓘ des autres dit « vous devriez ».
       */
      case 'optionnel':
        // Même gris que l'icône de `info` : c'est la forme — « ? » plutôt que « ⓘ » — qui
        // distingue, pas une couleur plus pâle. Voir la note de `.field-issue.optionnel`.
        return <HelpCircle size={taille} style={{ color: '#6b7280', flexShrink: 0 }} />;
      default:
        return null;
    }
  };

  /**
   * Le décompte des problèmes d'un produit, par gravité, du plus grave au moins grave.
   *
   * Les messages sont joints en infobulle : ce sont des phrases de quatre-vingts caractères, et
   * les afficher toutes dans une cellule interdisait la liste en lignes.
   */
  const resumerGravites = (issues) => GRAVITES
    .map(gravite => {
      const concernes = (issues || []).filter(i => i.severity === gravite.cle);
      if (concernes.length === 0) return null;
      return {
        ...gravite,
        nombre: concernes.length,
        messages: concernes.map(i => i.message).join('\n')
      };
    })
    .filter(Boolean);

  /**
   * Le type de problème sur lequel la liste est filtrée, en toutes lettres.
   *
   * ⚠️ **Replié, le bloc des problèmes ne montre plus ses pastilles** : sans ce libellé au
   * résumé, la liste resterait filtrée sans que rien n'en donne la raison. Le repli vaut le
   * nom.
   *
   * ⚠️ Repli sur le type brut plutôt que sur rien : un `topIssues` recomposé pendant qu'un
   * filtre est posé peut ne plus porter ce type — « filtré sur « title_all_caps » » se
   * comprend, « filtré sur «  » » non.
   */
  const libelleDuFiltreProbleme = filterIssueType
    ? ((analysis?.topIssues || []).find(i => i.type === filterIssueType)?.message || filterIssueType)
    : null;

  /**
   * Le gain d'un produit, à côté de sa note.
   *
   * ## ⚠️ Deux choses qui ne doivent jamais se confondre
   *
   * | | Ce que c'est | Quand |
   * |---|---|---|
   * | **le gain** | ce que l'enrichissement a **déjà** rapporté | la suggestion est dans le flux |
   * | **la perspective** | ce que le flux **deviendrait** si l'on validait | la proposition n'y est pas encore |
   *
   * La note affichée à gauche est, dans les deux cas, **celle du flux tel qu'il est servi** :
   * enrichie si la suggestion est publiée, source sinon. Une suggestion en attente n'est pas
   * chez Google, et afficher sa note comme celle du produit serait le défaut qu'on vient de
   * corriger, retourné — annoncer un état qui n'est pas celui du flux.
   *
   * ⚠️ **La flèche descendante n'est pas un détail d'affichage.** Un enrichissement peut
   * *dégrader* un produit : mesuré sur le parc, un titre réécrit en « 100 % Métal » déclenche
   * `title_promo_chars` et coûte sept points. L'écran d'avant ne pouvait pas le dire, puisqu'il
   * n'affichait que la source. Le montrer est la moitié de l'intérêt de ce calcul.
   */
  const rendreGainDuProduit = (produit) => {
    if (!produit || produit._horsAnalyse) return null;

    const avant = produit.avantEnrichissement;
    const perspective = produit.siValide;

    /*
      ⚠️ **Acquis ou proposé : ce sont deux choses, et la forme les sépare.**

      « ↑ +34 » dit que le flux XML sert déjà cette note — c'est fait, et c'est vert.
      « ↗ depuis F 11 % » dit que la ligne montre une **proposition** : le gain est à portée,
      mais Google reçoit encore l'autre. Les peindre pareil ferait croire un catalogue
      amélioré alors que rien n'est validé.
    */
    /*
      ⚠️ **Le gain et la perspective COEXISTENT, et c'est tout le sujet.**

      Ces deux blocs étaient une chaîne de `return` : sur une ligne publiée, le gain sortait
      le premier et la perspective devenait **inatteignable** — c'est-à-dire précisément dans
      le seul cas où elle sert. Régénérer un produit en ligne ne changeait donc rien à l'écran
      (le flux sert encore l'ancien instantané, par construction), et rien ne disait qu'une
      nouvelle version attendait. Signalé tel quel : « j'ai régénéré 3 fiches mais les titres
      et les problèmes ne se mettent pas à jour ».

      Les deux phrases sont vraies en même temps sur une ligne publiée puis régénérée :
      « ↑ +15 » dit ce que Google reçoit **déjà**, « ⟶ 61 % » ce qu'il recevrait si l'on
      validait. En taire une revient à dire que la régénération n'a rien fait.
    */
    let gain = null;
    if (avant && typeof avant.score === 'number' && avant.score !== produit.score) {
      const delta = produit.score - avant.score;

      if (!produit.enLigne) {
        gain = (
          <span
            className="fo-gain propose"
            title={`Cette ligne montre la suggestion en attente. Le flux sert encore `
              + `${avant.grade} ${avant.score} % ; validez-la pour que ${produit.grade} `
              + `${produit.score} % parte chez Google.`}
          >
            <ArrowUpRight size={11} />
            depuis {avant.grade} {avant.score} %
          </span>
        );
      } else {
        gain = (
          <span
            className={`fo-gain ${delta > 0 ? 'positif' : 'negatif'}`}
            title={`Le flux servait ${avant.grade} ${avant.score} %, il sert ${produit.grade} `
              + `${produit.score} % depuis l'enrichissement.`
              + (delta < 0 ? '\nCette suggestion dégrade le produit : la reprendre est possible.' : '')}
          >
            {delta > 0 ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
            {delta > 0 ? `+${delta}` : delta}
          </span>
        );
      }
    }

    /*
     * ⚠️ **La perspective ne subsiste que là où la ligne ne la montre pas déjà** : une
     * suggestion publiée puis régénérée, dont le flux sert encore l'ancien instantané. Sur
     * une ligne en attente, l'affiché *est* la proposition — l'annoncer deux fois ferait
     * douter du premier chiffre.
     */
    let aVenir = null;
    if (perspective && typeof perspective.score === 'number' && perspective.score !== produit.score) {
      aVenir = (
        <span
          className="fo-gain perspective"
          title={`La version proposée n'est pas celle du flux. Validée, le produit passerait `
            + `de ${produit.grade} ${produit.score} % à ${perspective.grade} ${perspective.score} %.`}
        >
          ⟶ {perspective.score} %
        </span>
      );
    }

    if (!gain && !aVenir) return null;
    return <>{gain}{aVenir}</>;
  };

  // Rendu si pas de connexion Merchant Center
  if (loadingAccounts) {
    return (
      <div className="feed-optimizer-tool">
        <div className="fo-surface">
          <div className="kc-vide">
            <Loader2 size={32} className="animate-spin" />
            <p className="kc-vide-titre">Chargement des comptes Merchant Center…</p>
          </div>
        </div>
      </div>
    );
  }

  // Connecter Google Merchant Center API
  const handleConnectMerchant = () => {
    window.location.href = `${API_URL}/api/auth/google-merchant-api?token=${token}`;
  };

  /*
    Les deux écrans d'accueil, à la charte.

    Ils étaient écrits en classes Tailwind — `p-6 text-center`, `w-12 h-12 mx-auto mb-4`,
    `text-lg font-medium mb-2` — qui n'existent nulle part dans ce projet : le titre s'affichait à
    la taille du texte courant, l'icône en 24px collée au bord gauche, et rien n'était centré. Ils
    reprennent `.kc-vide`, l'état vide de la charte.

    Le bouton passe du bleu au noir : connecter un compte n'est pas une action IA, et le bleu
    n'est réservé qu'à celles-là.
  */
  if (accounts.length === 0) {
    return (
      <div className="feed-optimizer-tool">
        <div className="fo-surface">
          <div className="kc-vide">
            <Store size={40} />
            <p className="kc-vide-titre">Connexion Merchant Center requise</p>
            <p className="kc-vide-texte">
              L'optimiseur lit les produits d'un compte Google Merchant Center, réécrit leurs
              titres et leurs attributs, puis les ressert en flux XML. Connectez un compte pour
              commencer.
            </p>
            <button onClick={handleConnectMerchant} className="fo-action">
              <ExternalLink size={16} />
              Connecter Google Merchant Center
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (selectedClientId && filteredAccounts.length === 0) {
    return (
      <div className="feed-optimizer-tool">
        <div className="fo-surface">
          <div className="kc-vide">
            <Store size={40} />
            <p className="kc-vide-titre">Aucun compte Merchant Center pour ce client</p>
            <p className="kc-vide-texte">
              Associez un compte Merchant Center à ce client depuis les Sites, ou connectez-en
              un nouveau.
            </p>
            <button onClick={handleConnectMerchant} className="fo-action">
              <ExternalLink size={16} />
              Connecter Google Merchant Center
            </button>
          </div>
        </div>
      </div>
    );
  }

  const suggestionsEnAttente = suggestions.filter(s => s.status === 'PENDING').length;

  return (
    <div className="feed-optimizer-tool" ref={outilRef}>
      {/*
        En-tête.

        Le titre vivait dans une carte blanche à ombre portée, et répétait mot pour mot le
        dernier maillon du fil d'Ariane — « Outils › SEA › Shopping › Optimiseur de Flux », puis
        « Optimiseur de Flux » en 24px juste dessous. Il reste, avec sa phrase d'explication,
        mais sans la carte : c'est la forme qu'a prise Backlinks, le dernier outil repris.
      */}
      <div className="fo-entete">
        <h2 className="fo-titre">
          <Sparkles size={20} />
          Optimiseur de flux
        </h2>
        {products.length > 0 && (
          <span className="kok-compteur">
            {filteredProducts.length < produitsEtOrphelines.length
              ? `${filteredProducts.length} sur ${produitsEtOrphelines.length}`
              : `${produitsEtOrphelines.length} produit${produitsEtOrphelines.length > 1 ? 's' : ''}`}
          </span>
        )}

        {/*
          La pastille des suggestions en attente : elle **pose et retire** le filtre.

          ⚠️ Poser un filtre sans pouvoir le retirer d'où on l'a posé est le piège que les
          pastilles de type de problème ont déjà résolu — même parti, même `aria-pressed`.

          ⚠️ **`!ficheOuverte` n'est pas cosmétique.** L'en-tête est rendu hors de la garde de
          la fiche : cliquer ici sous une fiche changerait `filteredProducts`, donc
          `sortedProducts`, donc `indexProduitOuvert` — « Produit 42 sur 966 » deviendrait
          « Produit 7 sur 20 », et « Suivant » sauterait sur un autre produit.
        */}
        {!ficheOuverte && suggestionsEnAttente > 0 && (
          <button
            type="button"
            className="fo-pastille-attente"
            aria-pressed={filterSuggestion === 'PENDING'}
            title={filterSuggestion === 'PENDING'
              ? 'Retirer le filtre'
              : 'Ne garder que les produits dont la suggestion attend une décision'}
            onClick={() => {
              setFilterSuggestion(filterSuggestion === 'PENDING' ? 'all' : 'PENDING');
              setProductsCurrentPage(1);
            }}
          >
            {suggestionsEnAttente} en attente
          </button>
        )}
        <p className="fo-sous-titre">
          Réécrit titres, descriptions et attributs d'un flux Merchant Center, puis les ressert
          en XML.
        </p>
      </div>

      {/*
        ⚠️ **Montée une fois, au niveau du composant.** Elle sert les deux gestes — la liste et
        la fiche —, et un portail n'a pas besoin d'être monté là où son déclencheur vit. La
        poser dans la fiche l'aurait rendue indisponible depuis la liste, qui n'est pas dans
        le même sous-arbre.
      */}
      {rendreModaleGeneration()}

      {error && (
        <div className="fo-bandeau erreur">
          <AlertCircle size={17} />
          {/*
            ⚠️ **Le filet, et non la ceinture seule.** `messageErreur` couvre les quatre
            appelants qui lisent une réponse ; celui-ci couvre les vingt-trois autres, dont
            un seul oubli suffit à blanchir l'écran — React refuse un objet comme enfant et
            démonte l'arbre entier. C'est ainsi qu'un 502 a effacé jusqu'au message qui
            l'expliquait.
          */}
          <span>{texteAffichable(error)}</span>
          <button className="fo-bandeau-fermer" onClick={() => setError(null)} title="Masquer">
            <X size={14} />
          </button>
        </div>
      )}

      {success && (
        <div className="fo-bandeau succes">
          <CheckCircle size={17} />
          <span>{success}</span>
        </div>
      )}

      {/*
        La barre de sélection.

        Elle portait un intertitre « Compte Google Merchant Center » puis un libellé
        « Sélectionner : » — deux lignes pour nommer un menu déroulant qui se nomme lui-même, et
        un libellé qui ne dit pas ce qu'on sélectionne. Chaque menu porte le sien.
      */}
      {/* La barre de sélection s'efface pendant qu'une fiche est ouverte : changer de compte
          sous une fiche en cours d'édition n'a pas de sens, et la laisser inviterait à le faire. */}
      {!ficheOuverte && (
      <div className="fo-barre">
        <label className="fo-champ">
          <span>Compte</span>
          <select
            className="fo-select"
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
          >
            <option value="">Choisir un compte…</option>
            {filteredAccounts.map(account => (
              <option key={account.id} value={account.id}>
                {account.displayName || account.name || account.id}
              </option>
            ))}
          </select>
        </label>

        {selectedAccount && (
          <label className="fo-champ">
            <span>Flux</span>
            <select
              className="fo-select"
              value={selectedFeedLabel}
              onChange={(e) => {
                setSelectedFeedLabel(e.target.value);
                // Réinitialiser les données quand on change de flux
                setAnalysis(null);
                setProducts([]);
                /*
                  ⚠️ **Ici aussi, et pas seulement dans l'effet.** L'effet ne tourne qu'après
                  le rendu : sans cette ligne, une frame entière afficherait les statuts du
                  flux précédent sur les lignes du nouveau — et les `offerId` se recoupent
                  entre flux d'un même marchand. Une frame de statuts faux est une frame de
                  trop sur un écran qui publie.
                */
                setSuggestions([]);
                setSuggestionsChargees(false);
                setFilterIssueType(null);
                setError(null);
              }}
              disabled={loadingFeedLabels}
            >
              <option value="">
                {loadingFeedLabels ? 'Chargement…' : 'Choisir un flux…'}
              </option>
              {feedLabels.map(label => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
          </label>
        )}

        {/*
          L'analyse se lance depuis la ligne du flux, comme dans le Diagnostic de flux :
          c'est le même geste — on choisit un flux, on l'analyse — et le séparer de son
          menu obligeait à le chercher un cran plus bas, dans un onglet.
        */}
        {selectedAccount && selectedFeedLabel && !readOnly && (
          <>
            <button
              className="fo-action"
              onClick={analyzeProducts}
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              {analysis ? 'Actualiser l’analyse' : 'Analyser le flux'}
            </button>

            {/* L'âge du relevé : voir le commentaire de `analyzedAt`. */}
            {analyzedAt && (
              <span className="analysis-freshness" title={new Date(analyzedAt).toLocaleString('fr-FR')}>
                <Clock size={13} />
                {decrireFraicheur(analyzedAt)}
                {analysisSource === 'CRON' && ' · analyse automatique'}
              </span>
            )}
          </>
        )}
      </div>

      )}

      {/*
        Les onglets.

        C'étaient des pastilles bleues pleines, dans une carte à ombre portée. Deux raisons de
        changer : le bleu marque l'IA dans cet écran et nulle part ailleurs, et un onglet retenu
        n'est pas une action. La forme reprend le groupe segmenté du consolidé.
      */}
      {!ficheOuverte && selectedAccount && selectedFeedLabel && (
        /*
          ⚠️ **Le bouton est à côté du groupe segmenté, jamais dedans.**

          « Tout valider » occupait une rangée à lui seul, sous les onglets : une bande vide sur
          toute la largeur pour un bouton, et un geste qui paraissait détaché de l'onglet auquel
          il appartient. Il est maintenant sur la rangée des onglets, à l'autre bout.

          ⚠️ **Il reste hors du `role="tablist"`.** Un lecteur d'écran énumère les enfants d'une
          tablist comme des onglets : l'y glisser annoncerait « onglet 4 sur 4 » sur une action
          qui n'en est pas une, et le laisserait dans le parcours des flèches gauche/droite.
          D'où la rangée qui enveloppe les deux, et le groupe segmenté laissé intact.

          ⚠️ **Les gestes restent hors de la `tablist`.** Ce ne sont pas des onglets : les y
          glisser les annoncerait comme tels aux technologies d'assistance, et le passage au
          clavier dans un `tablist` est une **sélection** — on changerait d'onglet en
          cherchant le bouton.

          Ils suivent désormais l'existence de suggestions, et non plus un onglet : « Tout
          valider » et la recherche d'images n'ont de sens que s'il y a quelque chose à
          valider ou à illustrer, ce qui ne dépend pas de l'écran regardé.
        */
        <div className="fo-onglets-rangee">
        <div className="fo-onglets" role="tablist">
          {[
            { id: 'analysis', label: 'Analyse', icon: Search },
            { id: 'feeds', label: 'Flux XML', icon: FileText }
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`fo-onglet${activeTab === tab.id ? ' actif' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon size={15} />
              {tab.label}
            </button>
          ))}
        </div>

        {/*
          Le témoin du travail en cours.

          ⚠️ **Il vit ici, et non dans le bloc de sélection.** Celui-ci ne paraît que si des
          produits sont cochés, et la mise en file vide justement la sélection : le seul
          signe du travail disparaissait au moment précis où il commençait.

          ⚠️ Il ne dépend pas de `suggestions.length` : le premier traitement d'un flux part
          alors qu'il n'y en a aucune, et c'est celui qu'on attend le plus.
        */}
        {activeTab === 'analysis' && fileGeneration.length > 0 && (
          <div className="fo-travail-en-cours" role="status">
            <Loader2 size={15} className="animate-spin" />
            <span>
              {avancementGeneration
                ? `Génération… ${avancementGeneration.faits}/${avancementGeneration.total}`
                : 'Génération…'}
              {/*
                La file ne se dit qu'à partir du deuxième travail : « 1 en attente » sur une
                file vide serait une information inventée.
              */}
              {fileGeneration.length > 1 && (
                <em> · {fileGeneration.length - 1} demande(s) en attente</em>
              )}
            </span>
          </div>
        )}

        {/*
          Les lots asynchrones en cours.

          ⚠️ **Distinct du témoin de la file.** Celui-ci dit « le navigateur travaille » ;
          celui-là dit « Anthropic travaille, vous pouvez fermer l'écran ». Les confondre
          ferait attendre devant un onglet qu'on peut quitter — c'est tout l'intérêt du lot.
        */}
        {activeTab === 'analysis' && lotsIA.some(l => l.statut === 'EN_COURS') && (
          <div className="fo-lot-en-cours" role="status">
            <Layers size={15} />
            <span>
              {(() => {
                const ouverts = lotsIA.filter(l => l.statut === 'EN_COURS');
                const produits = ouverts.reduce((n, l) => n + l.demandes, 0);
                return `${produits} produit(s) en traitement par lot`
                  + (ouverts.length > 1 ? ` · ${ouverts.length} lots` : '');
              })()}
              <em> · vous pouvez fermer l'écran, les résultats arriveront seuls</em>
            </span>
          </div>
        )}

        {/*
          ⚠️ **La rangée paraît dès qu'un de ses trois groupes a quelque chose à dire**, et
          non plus sur la seule existence de suggestions. « Générer » s'y est installé, et il
          sert précisément quand il n'y en a **aucune** : la garder sous `suggestions.length`
          l'aurait rendu introuvable sur un flux qu'on vient d'analyser — le geste le plus
          naturel du premier passage.
        */}
        {activeTab === 'analysis' && !readOnly
          && (suggestions.length > 0 || selectedProducts.length > 0) && (
          <div className="fo-onglets-gestes">
            {/*
              ⚠️ Posé ici plutôt que dans la fiche : l'examen porte sur **tout le flux**, et
              une galerie examinée vaut pour toutes les variantes qui la partagent. Le mettre
              dans la fiche ferait repayer le même verdict produit par produit — les tailles
              T34, T36 et T40 d'un même modèle ont la même galerie, au fichier près.
            */}
            {suggestions.length > 0 && (
            <button
              onClick={chercherAmbiances}
              className="btn-ambiance"
              disabled={chercheAmbiances}
              title="Regarde les images supplémentaires et propose celles qui sont des vues en situation"
            >
              <Sparkles size={16} />
              {chercheAmbiances ? 'Examen des galeries…' : 'Chercher les images d\'ambiance'}
            </button>
            )}

            {/*
              Ce que la sélection permet, **entre l'examen des galeries et la validation**.
              Ces boutons vivaient en fin de barre de filtres, à l'autre bout de l'écran des
              deux autres gestes de masse : les trois agissent sur un lot de produits, ils se
              lisent donc ensemble.
            */}
            {selectedProducts.length > 0 && (
              <div className="fo-actions-selection">
                {/*
                  ⚠️ **Il ne se désarme plus pendant un traitement.**

                  Il l'était, et c'est le reproche d'origine : cent lignes cochées, un
                  bouton figé à « Génération… 0/100 », et rien d'autre à faire qu'attendre
                  plusieurs minutes. On peut désormais composer une autre sélection et la
                  mettre en file — les lignes déjà tenues sont grisées et leurs cases
                  désarmées, donc on ne peut pas redemander ce qui part déjà.
                */}
                <button
                  type="button"
                  className="btn-ia"
                  // Même parti qu'en fiche : le bouton ouvre la modale, qui lance.
                  onClick={() => setGenerationADemander({ lot: true })}
                  disabled={selectedProducts.length === 0}
                  title={fileGeneration.length > 0
                    ? 'Un traitement est en cours : celui-ci partira à sa suite'
                    : 'Générer les suggestions des produits cochés'}
                >
                  <Sparkles size={16} />
                  {fileGeneration.length > 0
                    ? `Ajouter à la file (${selectedProducts.length})`
                    : `Générer les suggestions (${selectedProducts.length})`}
                </button>
                <button
                  type="button"
                  className="btn-neutre"
                  onClick={() => setSelectedProducts([])}
                >
                  Désélectionner
                </button>

              </div>
            )}

            {/*
              ⚠️ **Le bouton agit sur ce que les filtres montrent**, toutes pages confondues —
              et son libellé porte le nombre réel, jamais un total. Il validait auparavant tout
              le flux : poser un filtre puis cliquer aurait fait bien plus que ce qu'on voit,
              et rien ne l'aurait dit.

              ⚠️ **Masqué, et non plus désarmé, quand il n'y a rien à valider.** C'est l'inverse
              du choix d'origine — « un bouton qui disparaît fait chercher où il est passé » —
              et c'est une décision de l'utilisateur, prise en voyant « Valider les 0 affichées »
              en tête d'écran. L'argument tient d'ailleurs moins ici qu'ailleurs : le bouton
              revient dès qu'une suggestion attend, donc son absence se lit « rien à faire »
              plutôt que comme une disparition.
            */}
            {aValider.length > 0 && (
            <button
              onClick={() => bulkValidate(aValider)}
              className="btn-validate-all"
              title={`Les ${aValider.length} suggestions à valider du périmètre filtré, `
                + 'toutes pages confondues — en attente comme régénérées. Elles partiront '
                + 'dans le flux XML.'}
            >
              <Check size={16} />
              {filtresActifs
                ? `Valider les ${aValider.length} affichées`
                : `Valider les ${aValider.length} à valider`}
            </button>
            )}
          </div>
        )}
        </div>
      )}

      {/* Contenu des onglets - uniquement si compte ET flux sélectionnés */}
      {!ficheOuverte && selectedAccount && selectedFeedLabel && activeTab === 'analysis' && (
        <div>
          {/*
            Trois états, là où il n'y en avait qu'un.

            L'onglet n'affichait quelque chose que si une analyse existait : un flux jamais
            analysé donnait une page **entièrement blanche**, sans un mot sur le geste à faire,
            et l'analyse en cours ne se signalait que par le petit sablier du bouton, deux
            panneaux plus haut.
          */}
          {!analyseCherchee || (loading && !analysis) ? (
            <div className="fo-surface">
              <div className="kc-vide">
                <Loader2 size={32} className="animate-spin" />
                <p className="kc-vide-titre">Analyse du flux…</p>
                <p className="kc-vide-texte">
                  Les produits sont relus chez Merchant Center, puis contrôlés un par un.
                </p>
              </div>
            </div>
          ) : !analysis ? (
            <div className="fo-surface">
              <div className="kc-vide">
                <Search size={40} />
                <p className="kc-vide-titre">Ce flux n'a pas encore été analysé</p>
                <p className="kc-vide-texte">
                  L'analyse relève les titres trop courts, les descriptions absentes et les
                  attributs manquants, produit par produit. Elle ne modifie rien.
                </p>
                {/*
                  ⚠️ Un client ne peut pas analyser : lui offrir le bouton ouvrirait une
                  impasse dont il accuserait l'outil. L'écran dit alors à qui s'adresser.
                */}
                {readOnly ? (
                  <p className="kc-vide-texte">
                    Votre agence n'a pas encore analysé ce flux.
                  </p>
                ) : (
                  <button className="fo-action" onClick={analyzeProducts}>
                    <RefreshCw size={16} />
                    Analyser le flux
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {/*
                Le résumé.

                Il comptait cinq tuiles pour quatre informations : « Score moyen » et
                « Grade moyen » sont la même mesure sous deux formes. Le grade rejoint donc le
                score, et la part des produits en défaut passe dans le libellé de sa tuile.
              */}
              <div className="fo-tuiles">
                <div className="fo-tuile">
                  <div className="fo-tuile-nombre">{analysis.totalProducts}</div>
                  <div className="fo-tuile-libelle">Produits analysés</div>
                </div>
                {/* La précision va dans le libellé, et non à côté du nombre : « 966 » suivi de
                    « 100 % » à huit pixels se lisait comme un seul nombre, « 966 100 ». */}
                <div className="fo-tuile red">
                  <div className="fo-tuile-nombre">{analysis.productsWithIssues}</div>
                  <div className="fo-tuile-libelle">
                    Avec problèmes
                    {analysis.totalProducts > 0
                      && ` · ${Math.round((analysis.productsWithIssues / analysis.totalProducts) * 100)} % du flux`}
                  </div>
                </div>
                <div className="fo-tuile green">
                  <div className="fo-tuile-nombre">{analysis.productsWithoutIssues}</div>
                  <div className="fo-tuile-libelle">Sans problème</div>
                </div>
                <div className="fo-tuile neutre">
                  <div className="fo-tuile-nombre">{analysis.averageScore} %</div>
                  <div className="fo-tuile-libelle">
                    Score moyen
                    {analysis.averageGrade && ` · grade ${analysis.averageGrade}`}
                  </div>
                </div>
              </div>

              {/*
                Top problèmes — **replié par défaut**.

                Il occupait sept rangées de pastilles entre les tuiles et la liste : sur un
                flux qui déclenche vingt-quatre types de problème, on faisait défiler tout le
                diagnostic avant d'atteindre les produits. C'est un instrument de tri qu'on
                ouvre quand on en a besoin, pas un résumé qu'on lit à chaque passage — les
                quatre tuiles au-dessus tiennent ce rôle.

                ⚠️ **Mais un filtre posé ici survit au repli**, et c'est tout le piège : la
                liste resterait courte sans que rien à l'écran n'en donne la raison, et les
                pastilles de l'en-tête annonceraient plus de produits qu'elle n'en ouvre. Le
                résumé porte donc le filtre actif **et** son bouton d'effacement, lisibles
                sans ouvrir.
              */}
              {analysis.topIssues?.length > 0 && (
                <details
                  className="top-issues-section"
                  open={problemesOuverts}
                  onToggle={(e) => setProblemesOuverts(e.currentTarget.open)}
                >
                  {/*
                    ⚠️ Un `summary` n'accepte pas un titre mêlé à d'autres contenus : le `h4`
                    d'avant y serait non conforme. Le titre est donc un `span`, et la taille
                    vient du style.
                  */}
                  <summary className="top-issues-entete">
                    <span className="top-issues-titre">Problèmes les plus fréquents</span>
                    {filterIssueType ? (
                      <>
                        <span className="top-issues-filtre">
                          filtré sur «&nbsp;{libelleDuFiltreProbleme}&nbsp;»
                        </span>
                        <button
                          type="button"
                          className="btn-neutre"
                          /*
                            ⚠️ `preventDefault` : un clic dans un `summary` replie le bloc.
                            Effacer le filtre refermerait donc ce qu'on vient d'ouvrir — et
                            l'ouvrirait quand il était fermé, ce qui est pire : on efface un
                            filtre et sept rangées surgissent.
                          */
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setFilterIssueType(null);
                            setProductsCurrentPage(1);
                          }}
                        >
                          <X size={13} />
                          Effacer le filtre
                        </button>
                      </>
                    ) : (
                      <span className="top-issues-compte">
                        {analysis.topIssues.length} type{analysis.topIssues.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </summary>
                  <div className="issues-list">
                    {/*
                      Une pastille filtrante est un bouton, et non un `div` cliquable : elle
                      gagne ainsi le clavier et le focus, et la pastille retenue se marque par
                      sa bordure plutôt que par un `outline` qui débordait sur ses voisines.
                    */}
                    {analysis.topIssues.map((issue, idx) => {
                      const retenue = filterIssueType === issue.type;
                      const estompee = filterIssueType && !retenue;
                      return (
                        <button
                          key={idx}
                          type="button"
                          aria-pressed={retenue}
                          className={`issue-chip ${issue.severity}${retenue ? ' actif' : ''}${estompee ? ' estompe' : ''}`}
                          title={retenue ? 'Retirer ce filtre' : 'Ne garder que les produits concernés'}
                          onClick={() => {
                            setFilterIssueType(retenue ? null : issue.type);
                            setProductsCurrentPage(1);
                          }}
                        >
                          {getSeverityIcon(issue.severity)}
                          <span>{issue.message}</span>
                          <span className="count">{issue.count}</span>
                        </button>
                      );
                    })}
                  </div>
                </details>
              )}

              {/*
                Filtres et actions de sélection, sur la même rangée et juste au-dessus de la
                liste.

                « Générer suggestions IA » vivait tout en haut de l'onglet, au-dessus du résumé —
                à quinze mille pixels des cases qu'on venait de cocher. Sa barre apparaissait et
                disparaissait avec la sélection, déplaçant tout ce qui la suivait.
              */}
              {products.length > 0 && (
                <div className="fo-filtres">
                  <div className="fo-filtre-gravite">
                    <Filter size={15} />
                    <select
                      className="fo-select"
                      value={filterSeverity}
                      onChange={(e) => {
                        setFilterSeverity(e.target.value);
                        setProductsCurrentPage(1);
                      }}
                    >
                      {/* Voir `ETATS_SUGGESTION` : le nom de l'axe, pas une formule. */}
                      <option value="all">Gravité</option>
                      <option value="critical">Critiques</option>
                      <option value="warning">Avertissements</option>
                      <option value="info">Informations</option>
                    </select>
                  </div>

                  {/*
                    Le filtre d'état, à côté de la gravité : les deux disent « où en est ce
                    produit », l'un côté qualité du flux, l'autre côté travail de réécriture.
                  */}
                  <select
                    className="fo-select"
                    value={filterSuggestion}
                    onChange={(e) => {
                      setFilterSuggestion(e.target.value);
                      setProductsCurrentPage(1);
                    }}
                  >
                    {ETATS_SUGGESTION.map(e => (
                      <option key={e.cle} value={e.cle}>{e.libelle}</option>
                    ))}
                  </select>

                  {/*
                    Les filtres de segment, rendus seulement pour les axes qui ont de quoi
                    filtrer. Voir `rendreFiltresSegment`.
                  */}
                  {rendreFiltresSegment(
                    produitsEtOrphelines.map(segmentsDeLaLigne),
                    () => setProductsCurrentPage(1)
                  )}
                  {rendreAxesSansFiltre(produitsEtOrphelines, segmentsDeLaLigne)}

                  <div className="kc-recherche">
                    <Search size={16} />
                    <input
                      type="text"
                      placeholder="Identifiant ou titre…"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setProductsCurrentPage(1);
                      }}
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        className="kc-recherche-effacer"
                        title="Effacer"
                        onClick={() => {
                          setSearchTerm('');
                          setProductsCurrentPage(1);
                        }}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {/* Pas de décompte ici : la pastille du titre le porte déjà, et elle suit les
                      filtres — « 122 sur 966 ». Le répéter à cinquante pixels invitait à
                      chercher pourquoi les deux nombres diffèrent. */}
                  {/*
                    ⚠️ **« Tout sélectionner » n'est PLUS ici.** Il y était, et il était
                    introuvable : « comment faire pour sélectionner tous les produits, au-delà
                    des 50 affichés ? ». La barre de filtres est à deux cents pixels de la
                    liste, derrière quatre listes déroulantes — on ne l'y cherche pas, parce
                    que ce n'est pas là qu'on sélectionne.

                    Il vit maintenant sous l'en-tête de la liste, et ne paraît qu'une fois la
                    page cochée : la convention de tous les écrans qui paginent une sélection.
                    Deux affordances pour un même geste, c'est ce que ce fichier reproche
                    ailleurs — « trois façons de régler la même phrase, toutes visibles à la
                    fois ».
                  */}

                </div>
              )}

              {/*
                Le gabarit du flux, replié.

                ⚠️ **Replié par défaut**, et ce n'est pas de la timidité : c'est un panneau à
                deux colonnes avec glisser-déposer, deux cents pixels de haut. Posé ouvert
                au-dessus de cent lignes, il repousse les données vers le bas — le reproche
                exact qui avait fait sortir le titre de sa carte blanche. Le résumé dit
                l'essentiel sans l'ouvrir.

                ⚠️ **Conditionné aux produits, pas aux suggestions.** Le gabarit gouverne la
                **prochaine** génération : l'exiger après coup le rendait inatteignable avant
                la première, ce qui était l'ordre inverse de l'usage.
              */}
              {/* Le gabarit est un réglage du flux : une écriture, donc hors de portée. */}
              {products.length > 0 && !readOnly && (
                <details className="fo-reglage-flux">
                  <summary>
                    <Tag size={14} />
                    Éléments du titre — défaut du flux
                    <span>
                      {Array.isArray(titreGabarit) && titreGabarit.length
                        ? `${titreGabarit.length} élément(s)`
                        : 'aucun'}
                    </span>
                  </summary>
                  {rendreGabaritTitre(titreGabarit, setTitreGabarit)}
                </details>
              )}

              {/*
                La liste, en lignes.

                Elle affichait cent cartes de 183 pixels — quinze mille pixels de défilement par
                page, et aucun tri possible. C'est exactement le constat qui avait fait convertir
                les Sites, les Clients et les Comptes : « lisibles à cinq éléments, illisibles à
                cinquante ». Les classes `kc-*` viennent de `styles/connecteurs.css`.
              */}
              {filteredProducts.length === 0 ? (
                <div className="fo-surface">
                  <div className="kc-vide">
                    <Search size={40} />
                    <p className="kc-vide-titre">Aucun résultat</p>
                    <p className="kc-vide-texte">
                      Aucun produit du flux ne correspond aux filtres en cours.
                    </p>
                    <button type="button" className="btn-neutre" onClick={reinitialiserFiltres}>
                      Réinitialiser les filtres
                    </button>
                  </div>
                </div>
              ) : (
                <div className="kc-liste fo-liste" ref={listeProduitsRef}>
                  <div className="kc-grille kc-entete">
                    <span className="fo-cellule-case">
                      {/* Les cases ne servent qu'à la génération : sans elle, rien à cocher. */}
                      <input
                        type="checkbox"
                        className="fo-case"
                        checked={pageEntiereCochee}
                        onChange={basculerPageEntiere}
                        disabled={readOnly || selectionnablesDeLaPage.length === 0}
                        title={`Cocher les ${selectionnablesDeLaPage.length} produits de cette page`}
                      />
                    </span>
                    {COLONNES_PRODUITS.map(colonne => (
                      <button
                        key={colonne.cle}
                        type="button"
                        className={`kc-tri${triProduits.cle === colonne.cle ? ' actif' : ''}${colonne.cle === 'suggestion' ? ' fo-entete-suggestion' : ''}`}
                        title={`Trier par ${colonne.libelle.toLowerCase()}`}
                        onClick={() => basculerTriProduits(colonne)}
                      >
                        {colonne.libelle}
                        {triProduits.cle === colonne.cle
                          ? <ChevronDown size={12} style={{ transform: triProduits.sens === 'desc' ? 'rotate(180deg)' : 'none' }} />
                          : <ChevronDown size={12} className="kc-tri-repos" />}
                      </button>
                    ))}
                    <span className="kc-entete-fin">Actions</span>
                  </div>

                  {/*
                    ⚠️ **L'offre d'étendre la sélection paraît là où le geste se fait.**

                    Elle existait — un bouton « Tout sélectionner (N) » dans la barre de
                    filtres —, et elle était introuvable : « comment faire pour sélectionner
                    tous les produits, au-delà des 50 affichés ? ». La barre de filtres est à
                    deux cents pixels au-dessus, derrière quatre listes déroulantes, loin de
                    la case qu'on vient de cocher.

                    C'est la convention de tous les écrans qui paginent une sélection : on
                    coche l'en-tête, la page est prise, et une ligne propose le reste. Elle
                    apparaît au moment exact où la question se pose, et nulle part ailleurs.

                    ⚠️ **Elle ne paraît que si la page entière est cochée ET qu'il reste
                    autre chose.** La poser plus tôt en ferait une barre permanente de plus ;
                    la poser quand tout est déjà pris la rendrait absurde.
                  */}
                  {pageEntiereCochee && selectionnablesFiltres.length > selectedProducts.length && (
                    <div className="fo-etendre-selection" role="status">
                      <span>
                        Les <strong>{selectionnablesDeLaPage.length}</strong> produits de cette
                        page sont sélectionnés.
                      </span>
                      <button
                        type="button"
                        onClick={() => setSelectedProducts(selectionnablesFiltres)}
                        title={filtresActifs
                          ? 'Tous les produits que les filtres montrent, toutes pages confondues'
                          : 'Tous les produits de l\'analyse, toutes pages confondues'}
                      >
                        Sélectionner les {selectionnablesFiltres.length}
                        {filtresActifs ? ' filtrés' : ''}
                      </button>
                    </div>
                  )}

                  {/*
                    Et le pendant : une fois tout pris, de quoi revenir à la page seule. Sans
                    lui, le geste serait à sens unique — on ne pourrait le défaire qu'en
                    décochant tout.
                  */}
                  {selectedProducts.length > selectionnablesDeLaPage.length
                    && selectedProducts.length === selectionnablesFiltres.length && (
                    <div className="fo-etendre-selection tout-pris" role="status">
                      <span>
                        Les <strong>{selectedProducts.length}</strong> produits
                        {filtresActifs ? ' filtrés' : ''} sont sélectionnés.
                      </span>
                      <button type="button" onClick={() => setSelectedProducts([])}>
                        Tout désélectionner
                      </button>
                    </div>
                  )}

                  {paginatedProducts.map(product => {
                    const suggestion = suggestionsParProduit.get(product.offerId);
                    const gravites = resumerGravites(product.issues);
                    const cochee = selectedProducts.includes(product.offerId);
                    const enVol = offerIdsEnVol.includes(String(product.offerId));
                    const enLot = offerIdsEnLot.includes(String(product.offerId));
                    const verrouille = offerIdsVerrouilles.has(String(product.offerId));

                    return (
                      <div
                        key={product.offerId}
                        /*
                          ⚠️ **Deux états, et non un seul.** « En vol » et « en file » ne
                          disent pas la même chose : le premier tourne chez le modèle, le
                          second attend son tour. Les confondre ferait croire que cent appels
                          partent de front, alors qu'ils vont par dix.
                        */
                        className={`kc-grille kc-ligne${
                          enVol ? ' fo-ligne-en-cours'
                            : enLot ? ' fo-ligne-en-lot'
                              : verrouille ? ' fo-ligne-en-file' : ''}`}
                        data-offre={product.offerId}
                      >
                        <span className="fo-cellule-case">
                          <input
                            type="checkbox"
                            className="fo-case"
                            checked={cochee}
                            /*
                              ⚠️ **Une orpheline ne se régénère pas.** Son produit a quitté
                              le flux : rappeler le modèle sur lui paierait des jetons pour
                              un titre que rien ne servira, et le résultat partirait dans le
                              flux XML sous un `offerId` que Merchant ne connaît plus. La
                              ligne reste — c'est la seule façon de dépublier — mais la case
                              est désarmée.
                            */
                            /*
                              ⚠️ **Désarmée aussi quand un traitement la tient.** La
                              recocher la ferait partir une seconde fois, la seconde
                              génération écrasant la première par `upsert` — des jetons payés
                              deux fois pour un résultat que rien n'explique.
                            */
                            disabled={readOnly || Boolean(product._horsAnalyse) || verrouille}
                            title={product._horsAnalyse
                              ? 'Absent de la dernière analyse : rien à régénérer'
                              : enVol ? 'Génération en cours sur ce produit'
                                : verrouille ? 'En file : ce produit part à la prochaine requête'
                                  : 'Inclure dans la prochaine génération'}
                            onChange={() => setSelectedProducts(prev => (
                              prev.includes(product.offerId)
                                ? prev.filter(id => id !== product.offerId)
                                : [...prev, product.offerId]
                            ))}
                          />
                        </span>

                        <div className="fo-produit">
                          {/* Un flux produit s'identifie à l'image autant qu'au titre ; le
                              cadre reste même sans image, pour ne pas désaligner la colonne. */}
                          <span className="fo-vignette">
                            {product.imageLink
                              ? <img src={product.imageLink} alt="" loading="lazy" />
                              : <ImageOff size={15} />}
                          </span>
                          {/* Le titre est le bouton qui ouvre la fiche. La carte d'origine
                              portait `cursor: pointer` sur toute sa surface alors que seule la
                              zone de texte réagissait : la main s'affichait sur l'image et sur
                              les marges sans que rien ne s'y passe. */}
                          <button
                            type="button"
                            className="fo-ouvrir"
                            /*
                              ⚠️ **Un produit qu'un traitement tient ne s'ouvre pas.**

                              Sa fiche serait une photo périmée d'avance : la génération en
                              cours va réécrire son titre, ses attributs et ses constats. Une
                              retouche faite là serait écrasée à la reprise — sans un mot,
                              puisque l'écriture réussit —, et un verdict rendu porterait sur
                              une version que le modèle est en train de remplacer.

                              ⚠️ Le même périmètre que la case à cocher : en vol, en file, ou
                              dans un lot. Deux règles pour un même état divergeraient.
                            */
                            disabled={verrouille}
                            title={verrouille
                              ? (enLot
                                ? 'Génération par lot en cours : la fiche s\'ouvrira à la fin'
                                : 'Génération en cours : la fiche s\'ouvrira à la fin')
                              : undefined}
                            onClick={() => openProductDetail(product)}
                          >
                            <span className="kc-nom">{product.title || 'Titre absent'}</span>
                            <span className="fo-identifiant">
                              {product.offerId}
                              {/*
                                ⚠️ Une suggestion dont le produit a quitté le flux garde sa
                                ligne — c'est la seule façon de la rouvrir, et de dépublier
                                celle qui est validée, car le flux XML la sert toujours. Mais
                                elle se dit : sans cette mention, on chercherait pourquoi elle
                                n'a ni note ni problèmes.
                              */}
                              {product._horsAnalyse && (
                                <em className="fo-hors-analyse">· absent de la dernière analyse</em>
                              )}
                            </span>
                          </button>
                        </div>

                        <div className="fo-cellule-score">
                          {/* Un produit sans grade n'affiche pas de badge vide : la classe
                              `grade-` n'existe pas, le carré serait blanc et sans lettre. */}
                          {product.grade && (
                            <span className={`grade-badge grade-${product.grade.toLowerCase()}`}>
                              {product.grade}
                            </span>
                          )}
                          {/*
                            ⚠️ Un tiret, jamais « 0 % » ni une case vide : l'analyse n'a pas
                            regardé ce produit, et annoncer une note qu'on n'a pas mesurée est
                            le mensonge que `analyseCherchee` existe pour empêcher ailleurs.
                          */}
                          <span className="product-score">
                            {product._horsAnalyse ? '—' : `${product.score} %`}
                          </span>
                          {rendreGainDuProduit(product)}
                        </div>

                        {/* Un décompte par gravité, les messages en infobulle : voir
                            `resumerGravites`. */}
                        <div className="fo-severites">
                          {product._horsAnalyse
                            ? <span className="fo-sans-suggestion">—</span>
                            : gravites.length === 0
                              ? <span className="kc-etat actif">Conforme</span>
                              : gravites.map(gravite => (
                              <span
                                key={gravite.cle}
                                className={`fo-severite ${gravite.cle}`}
                                title={gravite.messages}
                              >
                                {getSeverityIcon(gravite.cle, 12)}
                                {gravite.nombre} {gravite.nombre > 1 ? gravite.pluriel : gravite.singulier}
                              </span>
                            ))}
                        </div>

                        <div className="fo-cellule-suggestion">
                          {/*
                            ⚠️ **L'état du traitement passe DEVANT le statut.** Une ligne en
                            cours de régénération porte encore son ancien statut : l'afficher
                            laisserait croire qu'il ne se passe rien — c'est le reproche
                            d'origine, « cent lignes qui ne bougent pas ».
                          */}
                          {enVol ? (
                            <span className="fo-statut en-cours">
                              <Loader2 size={12} className="animate-spin" /> En cours
                            </span>
                          ) : enLot ? (
                            /*
                              ⚠️ **« En lot » n'est pas « en file ».** Un lot tourne chez
                              Anthropic et peut durer une heure ; la file tourne dans cet
                              onglet et se compte en minutes. Les confondre ferait attendre
                              devant un écran qu'on peut fermer.
                            */
                            <span className="fo-statut en-lot" title={
                              'Soumis en traitement par lot : résultats sous une heure en '
                              + 'général. Vous pouvez fermer l\'écran.'}>
                              <Layers size={12} /> En lot
                            </span>
                          ) : verrouille ? (
                            <span className="fo-statut en-file">En file</span>
                          ) : suggestion
                            ? (
                              aRevalider(product) ? (
                                <span
                                  className="fo-statut a-revalider"
                                  title={'Cette ligne montre la version régénérée. Le flux sert '
                                    + 'encore la précédente : validez-la pour qu\'elle parte '
                                    + 'chez Google.'}
                                >
                                  À valider
                                </span>
                              ) : (
                                <span className={`fo-statut ${suggestion.status.toLowerCase()}`}>
                                  {LIBELLES_STATUT[suggestion.status] || suggestion.status}
                                </span>
                              )
                            )
                            : <span className="fo-sans-suggestion">—</span>}
                        </div>

                        {/*
                          Les gestes de la ligne.

                          ⚠️ **`stopPropagation` est une ceinture, pas une nécessité.** La
                          rangée ne porte aucun `onClick` : seuls le titre et l'œil ouvrent la
                          fiche, et valider ne risque donc rien aujourd'hui. Il est là pour le
                          jour où quelqu'un rendra la rangée cliquable « pour le confort » —
                          ce jour-là, cliquer ✓ ouvrirait une fiche. Un contrôle épingle par
                          ailleurs que la rangée reste inerte.

                          ⚠️ **« Écarter » n'efface rien** : la suggestion reste en base et seul
                          le flux cesse de la servir. D'où le libellé, et « Reprendre » en face,
                          qui la remet en décision **sans rappeler l'IA**.
                        */}
                        <div className="kc-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="kc-action"
                            // Voir le bouton du titre : même règle, même périmètre.
                            disabled={verrouille}
                            title={verrouille
                              ? (enLot
                                ? 'Génération par lot en cours : la fiche s\'ouvrira à la fin'
                                : 'Génération en cours : la fiche s\'ouvrira à la fin')
                              : 'Ouvrir la fiche produit'}
                            onClick={() => openProductDetail(product)}
                          >
                            <Eye size={15} />
                          </button>

                          {/*
                            ⚠️ **Une publiée régénérée garde son ✓.** La condition écartait
                            `VALIDATED` — juste tant qu'une publiée n'avait rien de neuf, faux
                            dès qu'elle en a : la ligne montrait une proposition sans aucun
                            moyen de la publier, depuis la ligne comme depuis le bouton de
                            masse. `aRevalider` le dit, et c'est le même juge que la pastille.
                          */}
                          {!readOnly && suggestion && suggestion.status !== 'REJECTED'
                            && (suggestion.status !== 'VALIDATED' || aRevalider(product)) && (
                            <button
                              type="button"
                              className="kc-action valider"
                              disabled={Boolean(statutEnVol[suggestion.id])}
                              title="Valider — la suggestion part dans le flux"
                              onClick={() => validateSuggestion(suggestion.id, 'VALIDATED')}
                            >
                              <Check size={15} />
                            </button>
                          )}

                          {!readOnly && suggestion && suggestion.status !== 'REJECTED' && (
                            <button
                              type="button"
                              className="kc-action ecarter"
                              disabled={Boolean(statutEnVol[suggestion.id])}
                              title="Écarter du flux — la suggestion est conservée"
                              onClick={() => validateSuggestion(suggestion.id, 'REJECTED')}
                            >
                              <X size={15} />
                            </button>
                          )}

                          {!readOnly && suggestion && suggestion.status === 'REJECTED' && (
                            <button
                              type="button"
                              className="kc-action reprendre"
                              disabled={Boolean(statutEnVol[suggestion.id])}
                              title="Reprendre — la suggestion redevient décidable"
                              onClick={() => validateSuggestion(suggestion.id, 'PENDING')}
                            >
                              <RotateCcw size={15} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/*
                Pagination. « ← Precedent » : l'accent manquait et la flèche était un
                caractère. Changer de page ramène en tête de liste — sans quoi on arrivait au
                pied de la page suivante, cinquante lignes plus bas que son premier produit.

                ⚠️ **Le bloc paraît même sur une seule page**, et c'est une correction : il
                était conditionné à `totalProductsPages > 1`, si bien que filtrer jusqu'à
                trente produits faisait disparaître **tout décompte** — on ne savait plus
                combien de fiches le filtre avait retenues, au moment précis où c'est la
                question qu'on se pose. Les boutons, eux, restent conditionnés : « Page 1 sur
                1 » entre deux flèches mortes est du bruit.
              */}
              {filteredProducts.length > 0 && (
                <div className="fo-pagination">
                  <span>
                    {/*
                      ⚠️ **Le décompte dit qu'il est filtré, et sur quoi il porte.** Il
                      affichait « sur 122 » sans plus : le nombre était juste, mais rien ne
                      disait que 122 était une part de 3 568 — on pouvait croire le flux
                      réduit à cent vingt-deux produits.
                    */}
                    {totalProductsPages > 1 && (
                      <>
                        Produits {((productsCurrentPage - 1) * PRODUCTS_PER_PAGE) + 1}
                        –{Math.min(
                          productsCurrentPage * PRODUCTS_PER_PAGE, filteredProducts.length)}
                        {' '}sur{' '}
                      </>
                    )}
                    <strong>{filteredProducts.length}</strong>
                    {' '}fiche{filteredProducts.length > 1 ? 's' : ''}
                    {filtresActifs && (
                      <em className="fo-pagination-filtre">
                        {' '}filtrée{filteredProducts.length > 1 ? 's' : ''}
                        {' '}sur {produitsEtOrphelines.length}
                      </em>
                    )}
                  </span>
                  {/* Les boutons seulement s'il y a où aller : « Page 1 sur 1 » entre deux
                      flèches mortes est du bruit. */}
                  {totalProductsPages > 1 && (
                  <div className="fo-pagination-boutons">
                    <button
                      type="button"
                      className="fo-page-btn"
                      onClick={() => allerPageProduits(productsCurrentPage - 1)}
                      disabled={productsCurrentPage === 1}
                    >
                      <ChevronLeft size={15} />
                      Précédent
                    </button>
                    <span className="fo-pagination-rang">
                      Page {productsCurrentPage} sur {totalProductsPages}
                    </span>
                    <button
                      type="button"
                      className="fo-page-btn"
                      onClick={() => allerPageProduits(productsCurrentPage + 1)}
                      disabled={productsCurrentPage >= totalProductsPages}
                    >
                      Suivant
                      <ChevronRight size={15} />
                    </button>
                  </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!ficheOuverte && selectedAccount && selectedFeedLabel && activeTab === 'feeds' && (
        <div>
          {/* Le `div.xml-flux-tab` qui enveloppait cet onglet a été retiré : sa règle était
              `padding: 0`, et les trois sous-onglets qu'il séparait — programmation, journal,
              historique — ne figurent plus dans l'écran. */}
          {feeds.length === 0 ? (
            <div className="fo-surface">
              <div className="kc-vide">
                <FileText size={40} />
                <p className="kc-vide-titre">Aucun flux XML</p>
                <p className="kc-vide-texte">
                  Un flux XML ressert les produits validés à l'adresse que vous donnerez à
                  Merchant Center. Il se met à jour de lui-même après chaque modification.
                </p>
                <div className="fo-creation-flux">
                  <input
                    type="text"
                    value={newFeedName}
                    onChange={(e) => setNewFeedName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && newFeedName.trim()) createFeed(); }}
                    placeholder="Nom du flux"
                  />
                  <button
                    onClick={createFeed}
                    disabled={!newFeedName.trim() || loading}
                    className="fo-action"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Link size={16} />}
                    Créer le flux
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="feeds-list">
              {feeds.map(feed => (
                <div key={feed.id} className="feed-card">
                  <div className="feed-card-header">
                    <div className="feed-card-info">
                      <h4>{feed.name || 'Flux XML'}</h4>
                      <span className="feed-date">
                        Dernière mise à jour : {feed.lastUpdated
                          ? new Date(feed.lastUpdated).toLocaleString('fr-FR')
                          : 'jamais'}
                      </span>
                    </div>
                    <div className="feed-card-actions">
                      <button
                        onClick={() => copyFeedUrl(feed.feedUrl)}
                        className="fo-action-icone"
                        title="Copier l'adresse du flux"
                      >
                        <Copy size={15} />
                      </button>
                      <a
                        href={feed.feedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fo-action-icone"
                        title="Ouvrir le flux"
                      >
                        <ExternalLink size={15} />
                      </a>
                    </div>
                  </div>

                  <div className="feed-url">{feed.feedUrl}</div>

                  <div className="feed-stats">
                    <div className="fo-chiffre">
                      <span className="fo-chiffre-nombre">{feed.productsCount || 0}</span>
                      <span className="fo-chiffre-libelle">produits dans le flux</span>
                    </div>
                  </div>

                  <p className="fo-note-flux">
                    <CheckCircle size={14} />
                    Le flux est mis à jour après chaque modification de produit.
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/*
        La fiche d'un produit, dans la page.

        Elle était une modale de 800 pixels, et c'était trop petit pour ce qu'elle porte : deux
        champs de texte long, dix-sept attributs, les recommandations de l'IA et jusqu'à dix
        points forts. Dans la page, elle prend toute la largeur — les attributs tiennent sur cinq
        colonnes au lieu de trois, le titre et la description se posent côte à côte — et elle
        gagne ce qu'une modale ne pouvait pas donner : **le passage d'un produit au suivant**.

        La barre de navigation est collante. C'est elle qui porte le retour, le rang, les deux
        flèches et l'enregistrement : sur une fiche de deux mille pixels, les laisser en pied
        obligerait à redescendre après chaque saisie. Elle remplace aussi le pied de modale, qui
        n'aurait fait que répéter ses deux boutons.
      */}
      {ficheOuverte && (
        <div className="fo-fiche" ref={ficheRef}>
          <div className="fo-fiche-barre">
            <button type="button" className="btn-neutre" onClick={fermerFiche}>
              <ChevronLeft size={14} />
              Retour à la liste
            </button>

            {/*
              Le rang dans la liste, et les deux flèches.

              Absents quand la fiche a été ouverte depuis l'onglet Suggestions : le produit
              reconstitué à partir d'une suggestion ne figure pas dans la liste analysée, et
              annoncer « 1 sur 1 » laisserait croire que le flux n'en compte qu'un.
            */}
            {indexProduitOuvert >= 0 && (
              <>
                <span className="fo-fiche-rang">
                  Produit {indexProduitOuvert + 1} sur {sortedProducts.length}
                </span>
                <div className="fo-fiche-navigation">
                  <button
                    type="button"
                    className="fo-page-btn"
                    title="Produit précédent (flèche gauche)"
                    onClick={() => ouvrirProduitAuRang(indexProduitOuvert - 1)}
                    disabled={indexProduitOuvert === 0}
                  >
                    <ChevronLeft size={15} />
                    Précédent
                  </button>
                  <button
                    type="button"
                    className="fo-page-btn"
                    title="Produit suivant (flèche droite)"
                    onClick={() => ouvrirProduitAuRang(indexProduitOuvert + 1)}
                    disabled={indexProduitOuvert >= sortedProducts.length - 1}
                  >
                    Suivant
                    <ChevronRight size={15} />
                  </button>
                </div>
              </>
            )}

            {/*
              La confirmation d'enregistrement, dans la barre.

              Le bandeau vert est en tête de page, et la barre est collante : en enregistrant
              depuis le bas d'une fiche de deux mille pixels, on ne voyait rien se passer.
            */}
            {/*
              ⚠️ **La fin de barre est un groupe, et non trois voisins poussés à droite.**
              `margin-left: auto` posé sur deux enfants d'un même flex **partage** l'espace
              libre entre eux : « Enregistrer » se serait retrouvé au milieu de la barre dès
              que « Générer » l'aurait rejoint. Un seul `auto`, sur le conteneur.
            */}
            <div className="fo-fiche-barre-fin">
            {!editionsEnCours && success && (
              <span className="fo-fiche-etat enregistre">
                <Check size={13} />
                Enregistré
              </span>
            )}

            {/*
              Le bouton ne paraît que s'il y a quelque chose à enregistrer.

              Sa présence est devenue le signal, et la pastille ambre « Modifications non
              enregistrées » qui l'accompagnait disait donc deux fois la même chose. Un bouton
              toujours visible sur une fiche intacte invitait par ailleurs à un enregistrement
              sans objet — qui créait bel et bien une suggestion, recopiant les valeurs du flux.
            */}
            {!readOnly && editionsEnCours && (
              <button
                className="fo-action fo-fiche-enregistrer"
                onClick={saveProductChanges}
                disabled={savingProduct}
              >
                {savingProduct ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                Enregistrer
              </button>
            )}
            {/*
              La génération IA, **à l'extrémité droite de la barre collante**.

              Elle vivait dans l'en-tête du produit, qui défile : sur une fiche de deux mille
              pixels, régénérer après avoir lu les attributs demandait de remonter. C'est une
              action sur le produit au même titre que l'enregistrement, et sa place est donc
              à côté de lui — même raison qui l'avait déjà fait sortir de sa bande bleue.
            */}
              {!readOnly && (
              <button
                type="button"
                className="btn-ia fo-fiche-generer"
                /*
                  ⚠️ Le bouton **demande**, il ne lance plus : la modale porte le choix des
                  attributs et annonce le coût. Appeler la génération ici court-circuiterait
                  les deux.
                */
                onClick={() => setGenerationADemander({ produit: selectedProductDetail })}
                disabled={generatingForProduct}
              >
                {generatingForProduct
                  ? <Loader2 size={16} className="animate-spin" />
                  : <Sparkles size={16} />}
                {productEdits._suggestion ? 'Regénérer avec l’IA' : 'Générer avec l’IA'}
              </button>
              )}
            </div>
          </div>

          <div className="fo-fiche-identite">
            {/*
              La vignette porte le constat de l'image : sa carte d'attribut a quitté la grille,
              et un cadre vide ne dit pas s'il manque une image ou si l'analyse ne l'a pas
              relevée. Le constat, lui, le dit.

              Quand l'image existe, la vignette l'ouvre en grand dans un nouvel onglet : 72
              pixels suffisent à reconnaître un produit, pas à juger si la photo est nette, bien
              cadrée ou sur fond blanc — ce que Google regarde. Sans image, c'est un cadre et
              non un lien : il n'y aurait rien à ouvrir.
            */}
            {selectedProductDetail.imageLink ? (
              <a
                className="fo-fiche-image ouvrable"
                href={selectedProductDetail.imageLink}
                target="_blank"
                rel="noopener noreferrer"
                title="Ouvrir l’image en grand dans un nouvel onglet"
              >
                <img src={selectedProductDetail.imageLink} alt="" />
              </a>
            ) : (
              <span className={`fo-fiche-image${getIssueForField('image_link') ? ' manquante' : ''}`}>
                <ImageOff size={24} />
              </span>
            )}
            <div className="fo-fiche-titres">
              <p className="fo-fiche-nom">{selectedProductDetail.title || 'Titre absent'}</p>
              <div className="fo-fiche-reperes">
                {selectedProductDetail.grade && (
                  <span className={`grade-badge grade-${selectedProductDetail.grade.toLowerCase()}`}>
                    {selectedProductDetail.grade}
                  </span>
                )}
                {selectedProductDetail.score !== undefined && (
                  <span className="product-score">{selectedProductDetail.score} %</span>
                )}
                {/*
                  Le même repère qu'en liste, et pour la même raison : la fiche affichait elle
                  aussi la note du flux **source** sur un produit dont le flux sert l'enrichi.
                */}
                {rendreGainDuProduit(selectedProductDetail)}
                <span className="fo-fiche-identifiant">{selectedProductDetail.offerId}</span>
                {/* Le prix et la disponibilité : ce qu'on cherche en ouvrant une fiche, avec le
                    titre. Voir `repereCatalogue` pour les trois états possibles. */}
                {repereCatalogue('price')}
                {repereCatalogue('availability')}
                {/* Le statut de la suggestion, s'il y en a une : c'est l'information que la
                    liste donne en colonne, et la fiche la perdait. */}
                {productEdits._suggestion && (
                  <span className={`fo-statut ${productEdits._suggestion.status.toLowerCase()}`}>
                    {aRevalider(selectedProductDetail)
                      ? 'À valider'
                      : (LIBELLES_STATUT[productEdits._suggestion.status]
                        || productEdits._suggestion.status)}
                  </span>
                )}
                {getIssueForField('image_link') && (
                  <span className={`field-issue ${getIssueForField('image_link').severity}`}>
                    {getSeverityIcon(getIssueForField('image_link').severity)}
                    {getIssueForField('image_link').message}
                  </span>
                )}
              </div>

              {/*
                L'adresse du produit chez le marchand, et le lien pour aller la voir.

                C'est le seul moyen de vérifier ce qu'on corrige : le flux dit « GESSO STUDIO
                ACRYLICS 250 ML » et rien de plus, la page marchande dit la contenance, la
                finition et le conditionnement — c'est-à-dire ce qu'il faut pour réécrire un
                titre. Elle s'ouvre dans un nouvel onglet : la fiche garde ses saisies en cours.

                Le protocole est retiré de l'affichage, il ne distingue rien et mange la
                largeur ; le lien et l'infobulle portent l'adresse entière.
              */}
              {/*
                Les deux liens dans un conteneur à `gap`, et non côte à côte avec leurs marges
                propres : `inline-flex` les collait l'un à l'autre, « …origin=ads » touchant
                l'icône du suivant. Un `margin-left` aurait réglé l'espace mais pas le retour à
                la ligne, où il aurait décalé le lien revenu en début de ligne.
              */}
              <div className="fo-fiche-liens">
                {selectedProductDetail.link && (
                  <a
                    className="fo-fiche-lien"
                    href={selectedProductDetail.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Ouvrir ${selectedProductDetail.link} dans un nouvel onglet`}
                  >
                    <ExternalLink size={13} />
                    <span>{selectedProductDetail.link.replace(/^https?:\/\//, '')}</span>
                  </a>
                )}

                {/*
                  La fiche du produit dans Merchant Center, à côté de la page du marchand.

                  Les deux adresses ne mènent pas au même endroit et ne servent pas au même
                  moment : le lien ci-dessus ouvre la page **publique**, celui-ci la fiche **que
                  Google détient** — ses refus, ses avertissements, la valeur exacte de chaque
                  attribut tel qu'il est arrivé. C'est là qu'on va vérifier qu'une correction a
                  été prise en compte.

                  ⚠️ **Il n'apparaît que si les quatre paramètres sont connus.** `lienMerchant`
                  rend `null` sinon — un `offerId` désigne autant de produits que de couples
                  (langue, flux), et un lien bâti sur un repli ouvrirait la fiche allemande d'un
                  produit français. C'est la leçon de `lienGoogleAds` : un lien plausible qui
                  mène ailleurs est pire qu'un lien absent, parce qu'il ne se voit pas.

                  Les suggestions générées avant la colonne `productContentLanguage` n'ont donc
                  pas ce lien, et c'est le comportement voulu — pas une panne à corriger.
                */}
                {lienMerchant && (
                  <a
                    className="fo-fiche-lien fo-fiche-lien-merchant"
                    href={lienMerchant}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Ouvrir la fiche de ce produit dans Google Merchant Center"
                  >
                    <ExternalLink size={13} />
                    <span>Fiche Merchant Center</span>
                  </a>
                )}
              </div>
            </div>

          </div>

          {productEdits._suggestion?.aiReasoning && (
            <div className="ai-reasoning-box">
              <span className="ai-label"><Sparkles size={14} /> Analyse IA</span>
              <p>{productEdits._suggestion.aiReasoning}</p>
            </div>
          )}

          <div className="fo-fiche-corps">
            {/* Une photo prise par une version antérieure ne porte pas tous les attributs
                d'aujourd'hui. Le dire une fois, en tête, plutôt que dans chaque ligne — et dire
                aussi comment y remédier, puisque le bouton d'analyse n'est pas visible d'ici. */}
            {nonRelevesAffiches.length > 0 && (
              <p className="fo-analyse-ancienne">
                <AlertTriangle size={14} />
                <span>
                  {nonRelevesAffiches.length === 1 ? 'Un attribut n’a pas' : 'Ces attributs n’ont pas'}
                  {' '}été relevé{nonRelevesAffiches.length > 1 && 's'}
                  {analyzedAt && ` par l’analyse du ${new Date(analyzedAt).toLocaleDateString('fr-FR')}`}
                  {' : '}
                  {nonRelevesAffiches.map(a => a.libelle).join(', ')}.
                  {' '}Revenez à la liste et relancez l’analyse du flux pour les obtenir.
                </span>
              </p>
            )}

            {/*
              Le tableau comparatif : à gauche ce que le flux dit, à droite ce qui sera écrit.

              Les deux vivaient l'un sous l'autre dans la même carte — « Valeur actuelle », puis
              la recommandation, puis « Modifier manuellement » — et il fallait lire de haut en
              bas, carte par carte, pour savoir ce qui changeait. Mis en regard, un coup d'œil
              sur la colonne de droite suffit : ce qui est vide n'a pas bougé.

              Une seule grille, et des lignes en `display: contents` : c'est le motif de la liste
              des Connecteurs, et pour la même raison — trois grilles distinctes ne garderaient
              leurs colonnes alignées qu'au prix de largeurs figées.

              L'ordre des lignes est celui de la table servie par le serveur. Le titre et la
              description ouvrent le tableau parce qu'ils ouvrent la table, pas parce qu'on les
              a placés là.
            */}
            {/* La modale de choix d'un visuel, rendue par un portail — voir `rendreChoixVisuel`. */}
            {rendreChoixVisuel()}

            {/* L'infobulle des notes d'attribut. Elle sort par un portail : sa place dans
                l'arbre n'a donc aucune importance, seul son montage compte. */}
            {bulle}

            <div className="fo-comparatif">
              <div className="fo-comparatif-ligne fo-comparatif-entete">
                {/* La colonne des noms était la seule sans intitulé : les deux autres
                    s'annonçaient, elle se devinait. */}
                <span>Attribut</span>
                <span>Flux source</span>
                <span>Flux enrichi</span>
              </div>

              {champsDeLaFiche.map(a => {
                const reco = a.suggerable ? getRecommendationForField(a.nom) : null;
                const etat = productEdits._acceptedFields?.[a.nom];
                // `texteAttribut` et non une simple concaténation de tableau : le prix est un
                // objet, et le rendre tel quel faisait tomber l'écran entier.
                /*
                  ⚠️ **La colonne « Flux source » montre le FLUX.** C'est toute sa raison
                  d'être — le comparatif oppose ce que le marchand a écrit à ce que le modèle
                  propose. Pour une ligne publiée, `selectedProductDetail` porte désormais
                  l'état enrichi : la colonne affichait donc la même valeur des deux côtés, ce
                  qui se lit « le flux portait déjà ça » et fait refuser une suggestion utile.
                */
                const produitDuFlux = sourceDuMarchand(selectedProductDetail);
                const valeurBrute = produitDuFlux[a.cleProduit];
                const source = texteAttribut(valeurBrute, a.nom);
                /*
                  Toute valeur multiple se lit en puces, quel que soit le widget.

                  La règle visait d'abord les points forts — un widget `liste` —, mais les images
                  supplémentaires sont un tableau d'URL sous un widget `lecture`, et elles
                  s'affichaient en une seule ligne d'adresses séparées par des virgules. C'est la
                  forme de la valeur qui décide, pas celle de son éditeur.

                  Un élément seul reste du texte : une liste d'une puce n'est pas une liste.
                */
                const estValeurListe = Array.isArray(valeurBrute) && valeurBrute.length > 1;
                /*
                 * La valeur quand il n'y en a qu'une — un tableau d'un seul élément compris.
                 * C'est elle qu'on teste pour savoir si elle mérite d'être rendue cliquable.
                 */
                const valeurUnique = Array.isArray(valeurBrute)
                  ? (valeurBrute.length === 1 ? valeurBrute[0] : null)
                  : valeurBrute;
                /*
                  Le constat du FLUX, ou — pour les points forts seuls — la mention d'absence
                  que la fiche ajoute d'elle-même. Voir `CONSTAT_HORS_ANALYSE`.

                  ⚠️ **Il décrit la source, comme la colonne qui le porte.** Il venait de
                  `selectedProductDetail.issues`, c'est-à-dire de l'état **enrichi** pour une
                  ligne publiée : une couleur que le marchand avait laissée vide et que le
                  modèle a remplie s'affichait alors en case vide **sans** « Couleur
                  manquante ». La colonne disait « Flux source » et sous-déclarait ses propres
                  manques — une demi-correction de celle qui lui a rendu ses valeurs.

                  ⚠️ L'en-tête de la fiche, lui, garde les constats de l'état courant : il
                  affiche le titre et la note du flux servi, et ses badges doivent suivre.
                */
                const constatDuFlux = (produitDuFlux.issues || []).find(i => i.attribute === a.nom);
                const constat = constatDuFlux
                  || (source ? null : CONSTAT_HORS_ANALYSE[a.nom])
                  || null;

                /*
                 * ⚠️ **Le champ du titre ne se masque que derrière un « Modifier » qui existe.**
                 *
                 * Il était caché dès que l'attribut était le titre, hors retouche — y compris
                 * avant toute génération, où il n'y a ni proposition ni bouton : la colonne
                 * « Ce qui sera écrit » restait vide, et le titre devenait le seul attribut
                 * qu'on ne pouvait pas saisir.
                 *
                 * Le bouton n'existe qu'avec une proposition encore en attente de verdict :
                 * refusée, le bloc disparaît ; acceptée, ses actions aussi. C'est donc à cette
                 * condition exacte que le champ cède sa place.
                 */
                const propositionEnAttente = Boolean(
                  reco && etat !== 'rejected' && etat !== 'accepted'
                );

                return (
                  <div className="fo-comparatif-ligne" key={a.nom}>
                    <div className="fo-comparatif-nom">
                      {/*
                        ⚠️ **Le marqueur suit le libellé, sur sa ligne.**

                        La colonne est en `flex-direction: column` — le nom Merchant se pose
                        sous le libellé — si bien qu'un marqueur posé entre les deux prenait
                        une ligne à lui seul et éloignait le nom technique de ce qu'il nomme.
                        Dans le `<label>`, il suit le dernier mot, ce qui est la place d'une
                        précision sur ce libellé-là.
                      */}
                      <label>
                        {a.libelle}
                        {a.note && (
                          <span
                            className="ksi-survolable fo-note-bulle"
                            onMouseEnter={survoler(a.note)}
                            onMouseLeave={quitter}
                          >
                            <Info size={13} />
                          </span>
                        )}
                      </label>
                      {/*
                        Le nom de l'attribut dans la spécification Merchant, sous son libellé.

                        Le libellé est en français et parfois éloigné du nom technique — « Tranche
                        d'âge » pour `age_group`, « Référence fabricant » pour `mpn`. Qui compare
                        cet écran à son flux source, à Merchant Center ou à la documentation de
                        Google cherche le second, et devait le deviner.

                        ⚠️ Il vient de `nomGoogle`, servi par le catalogue — **jamais** de la clé
                        d'attribut. Les deux coïncident pour dix-huit d'entre eux, mais notre clé
                        `additional_image_links` est au pluriel quand l'attribut Google est
                        `additional_image_link`, au singulier et répété. Afficher la clé
                        apprendrait un nom faux à qui vient justement en chercher un exact.
                      */}
                      {/*
                        Le nom technique est lui-même le lien vers sa page de spécification :
                        c'est l'élément qu'on cherche quand on veut la règle exacte — la
                        longueur admise, les valeurs acceptées, le caractère répété.

                        ⚠️ Pas d'icône à côté : la rangée des libellés est étroite, et chacun
                        des vingt-six attributs en aurait une. Le nom souligné au survol suffit
                        à dire qu'il mène quelque part.
                      */}
                      {a.doc ? (
                        <a
                          className="fo-nom-google fo-nom-google-lien"
                          href={a.doc}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Spécification Google de l'attribut ${a.nomGoogle}`}
                        >[{a.nomGoogle}]</a>
                      ) : (
                        <code className="fo-nom-google">[{a.nomGoogle}]</code>
                      )}
                    </div>

                    <div className="fo-comparatif-source">
                      {/*
                        La valeur du flux, ou ce qui tient lieu de valeur.

                        ⚠️ **Un constat parle, et rien ne le double.** Ni « Non renseigné », ni
                        « Non relevé par cette analyse » : sous un constat, ils répètent la même
                        chose en plus pâle, sur une ligne de plus. C'est le constat qui reste —
                        il est plus précis, il porte sa gravité, et parfois une indication que le
                        vide n'a pas (« recommandé : 3-5 images »).

                        La règle ne valait d'abord que pour « Non renseigné », et c'est ce qui a
                        laissé passer « Non relevé par cette analyse » suivi de « Aucun point
                        fort ». La raison tient à l'origine des deux constats :
                        `attributsNonRelevesPour` écarte bien les attributs porteurs d'un constat
                        — mais seulement ceux de **l'analyse**, puisqu'elle ne lit que
                        `produit.issues`. `CONSTAT_HORS_ANALYSE` est fabriqué ici, pour les
                        attributs que l'analyse ne connaît pas : il échappait au filtre par
                        construction, et il n'y a pas moyen qu'il y entre — c'est donc
                        l'affichage qui tranche, une fois pour les deux mentions.
                      */}
                      {source ? (
                        <span className="fo-comparatif-valeur">
                          {/*
                            ⚠️ **Une adresse seule se lit comme une adresse en liste.**

                            `estValeurListe` exige plus d'un élément — « une liste d'une puce
                            n'est pas une liste » — et la valeur retombait alors sur son texte
                            brut : une URL Demandware de 140 caractères sur deux lignes, non
                            cliquable, là où la même adresse en liste donne « …_V3.jpg » et
                            s'ouvre d'un clic. Le nombre d'éléments ne devrait rien changer à
                            la façon de lire l'un d'eux.
                          */}
                          {estValeurListe
                            ? rendreValeurListe(valeurBrute)
                            : estAdresse(valeurUnique)
                              ? rendreValeurListe([valeurUnique])
                              : source}
                        </span>
                      ) : constat ? null : attributsNonReleves.has(a.nom) ? (
                        /*
                          ⚠️ **Un badge, comme les constats — parce que c'en est un.**

                          Les deux mentions étaient du texte libre : « Non relevé par cette
                          analyse » en orange italique, « Non renseigné » en gris pâle. Sur une
                          colonne où tout le reste parle en pastilles, elles se lisaient comme
                          des valeurs du flux plutôt que comme des verdicts sur son absence —
                          et l'orange italique du premier annonçait une gravité qu'il n'a pas.

                          ⚠️ **Deux badges distincts, et pas un seul**, malgré l'apparence
                          commune : « non relevé » parle de **nous** — l'analyse n'a pas
                          regardé cet attribut, il faut la relancer —, « non renseigné » parle
                          du **flux**, qu'il faut corriger chez le marchand. Les fondre
                          enverrait la moitié des lecteurs au mauvais endroit. C'est la
                          distinction que `attributsNonRelevesPour` existe pour tenir.
                        */
                        <span className="field-issue info fo-badge-non-releve">
                          {getSeverityIcon('info')}
                          Non relevé par cette analyse
                        </span>
                      ) : (
                        <span className="field-issue info fo-badge-vide">
                          {getSeverityIcon('info')}
                          Non renseigné
                        </span>
                      )}

                      {/*
                        Le constat se pose dans la colonne de la valeur qu'il juge, et non à
                        côté du libellé.

                        « Titre tout en majuscules » ne dit rien du champ « Titre » : il dit
                        quelque chose de ce que le flux y met. Dans la colonne des libellés, il
                        élargissait une piste qui n'a que des mots courts à porter, et laissait
                        deviner laquelle des deux valeurs il visait — celle de gauche, toujours,
                        puisque c'est elle que l'analyse a lue.
                      */}
                      {constat && (
                        <span className={`field-issue ${constat.severity}`} title={constat.description}>
                          {getSeverityIcon(constat.severity)}
                          {constat.message}
                        </span>
                      )}

                      {/*
                        ⚠️ **Le soupçon de marque tierce : une question, pas une correction.**

                        Relevé sur un cas réel : « Havaïanas Slim Square Rose Homme » déclare
                        `brand = BZB`. BZB est le revendeur, Havaianas le fabricant — et c'est
                        le fabricant que l'attribut `brand` de Merchant attend.

                        **Rien n'est corrigé, et ce n'est pas une prudence excessive.** `brand`
                        participe à l'appariement produit chez Google (brand+GTIN, brand+MPN) :
                        une marque fausse casse l'appariement ou déclenche un refus « marque
                        incorrecte ». Garder `BZB` est un défaut mineur ; écrire `Bordeaux` —
                        qui figure 7 fois parmi les candidats du même flux — est une faute qui
                        se paie. Le marchand est seul à savoir s'il revend la marque ou s'il
                        l'a brodée sur son propre vêtement.

                        Pas de `field-issue` : ce n'est pas un défaut constaté mais une
                        question posée, et elle ne doit pas se lire comme une alerte de plus.
                      */}
                      {a.nom === 'brand' && productEdits._suggestion?.marqueTierce?.nom && (
                        <span className="fo-marque-tierce">
                          <HelpCircle size={13} />
                          <span>
                            Le titre et la description nomment{' '}
                            <strong>{productEdits._suggestion.marqueTierce.nom}</strong>. Si ce produit est
                            fabriqué par cette marque, c'est elle que Merchant attend ici —
                            la marque déclarée étant le revendeur.
                          </span>
                        </span>
                      )}
                    </div>

                    <div className="fo-comparatif-retenue">
                      {/*
                        ⚠️ **La provenance d'une valeur déduite se dit du côté enrichi.**

                        Elle était d'abord posée dans la colonne « Flux source », sous une
                        valeur que le marchand n'a jamais écrite — la colonne mentait, et c'est
                        exactement ce qu'elle existe pour empêcher. `popularity_rank` et
                        `variant_option` sont reconstitués (classement de ventes rangé dans un
                        custom label, axes du groupe de variantes) ; le flux, lui, ne les porte
                        pas, et son constat « Aucune popularité » reste juste à gauche.

                        La mention explique donc ce qui apparaît à DROITE, et doit pouvoir être
                        retrouvée pour corriger à la source plutôt qu'ici.
                      */}
                      {a.nom === 'popularity_rank'
                        && selectedProductDetail.popularityRankProvenance && (
                        <span className="fo-valeur-deduite">
                          déduit de {selectedProductDetail.popularityRankProvenance.label
                            .replace(/^customLabel/, 'custom_label_')}
                          {' · '}{selectedProductDetail.popularityRankProvenance.niveau}
                        </span>
                      )}
                      {a.nom === 'item_group_title'
                        && selectedProductDetail.itemGroupTitleDeduit
                        && selectedProductDetail.variantOptionsProvenance && (
                        <span className="fo-valeur-deduite">
                          déduit du titre de la variante, privé de ce qui varie
                        </span>
                      )}
                      {a.nom === 'variant_options'
                        && selectedProductDetail.variantOptionsProvenance && (
                        <span className="fo-valeur-deduite">
                          {/*
                            ⚠️ **On nomme les valeurs du groupe, pas seulement leur nombre.**

                            « 3 produits » laissait croire que « taille : L » était la liste des
                            variantes, alors que c'est la valeur de CE produit — la spécification
                            est explicite : « la variante doit se distinguer des autres à l'aide
                            d'une combinaison de sous-attributs valeur qui lui est propre ».
                            Lire « taille : M, L, XL » lève le doute d'un coup d'œil.
                          */}
                          déduit du groupe de{' '}
                          {selectedProductDetail.variantOptionsProvenance.membres} variantes
                          {(selectedProductDetail.variantOptionsProvenance.axes || [])
                            .map(axe => ` · ${axe.nom} : ${(axe.valeurs || []).join(', ')}`)
                            .join('')}
                        </span>
                      )}
                      {/*
                        La recommandation du modèle, au-dessus du champ : on l'accepte, elle
                        descend dans le champ ; on la refuse, elle disparaît et la valeur du
                        flux reste.
                      */}
                      {reco && etat !== 'rejected' && (
                        <div className="fo-reco">
                          <div className="fo-reco-texte">
                            <Sparkles size={13} />
                            <span>
                              {/*
                                ⚠️ **Une liste d'une puce n'est pas une liste.** La règle vaut
                                déjà pour la colonne du flux, où `estValeurListe` exige plus
                                d'un élément ; la recommandation, elle, mettait une puce devant
                                tout tableau. Elle s'est vue le jour où `product_type` est
                                devenu répétable : un chemin unique s'affichait
                                « • Femme > Vêtements > Shorts ».
                              */}
                              {a.nom === 'google_product_category'
                                ? libelleCategorie(reco)
                                : (Array.isArray(reco) && reco.length > 1)
                                  ? rendreValeurListe(reco)
                                  : texteAttribut(reco, a.nom)}
                            </span>
                          </div>
                          {/*
                            ⚠️ **« Modifier » n'est pas un verdict, et il ne vit plus avec eux.**

                            Il était logé dans ce bloc, que `etat !== 'accepted'` fait
                            disparaître dès qu'une décision est rendue. Deux défauts en
                            découlaient, signalés ensemble :

                            1. **l'atelier s'ouvrait et ne se refermait plus** — l'ouverture
                               posait elle-même le verdict `accepted` (voir
                               `ouvrirEditionTitre`), ce qui emportait les trois boutons, celui
                               qui aurait pu refermer compris ;
                            2. **un titre accepté ne se retouchait plus jamais.** Accepter
                               retirait « Modifier » pour de bon, alors qu'accepter une
                               proposition et vouloir ensuite y changer un mot est le geste le
                               plus naturel du monde.

                            Accepter et Refuser sont des verdicts : rendus, ils s'effacent.
                            Modifier est un accès à l'atelier, et il reste.
                          */}
                          {(etat !== 'accepted' || a.nom === 'title') && (
                            <div className="fo-reco-actions">
                              {etat !== 'accepted' && (
                                <>
                                  <button
                                    type="button"
                                    className="btn-accept"
                                    onClick={() => accepterRecommandation(a, reco)}
                                  >
                                    <Check size={13} /> Accepter
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-reject"
                                    onClick={() => rejectRecommendation(a.nom)}
                                  >
                                    <X size={13} /> Refuser
                                  </button>
                                </>
                              )}
                              {/*
                                Le titre est le seul attribut qui se retouche autrement qu'au
                                clavier : il a des éléments, un ordre, une marque. D'où ce
                                troisième geste, et seulement pour lui.

                                ⚠️ **Une bascule, et un seul bouton pour les deux sens.** Un
                                « Fermer » posé ailleurs se cherche ; au même endroit, le geste
                                se défait là où il s'est fait.
                              */}
                              {a.nom === 'title' && (
                                <button
                                  type="button"
                                  className="btn-modifier"
                                  title={editionTitre
                                    ? 'Refermer l\'atelier — le titre du champ est conservé'
                                    : 'Retoucher le titre : le texte, ses éléments, leur ordre'}
                                  onClick={() => (editionTitre
                                    ? setEditionTitre(false)
                                    : ouvrirEditionTitre(reco))}
                                >
                                  {editionTitre
                                    ? <><ChevronUp size={13} /> Terminer</>
                                    : <><Pencil size={13} /> Modifier</>}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/*
                        Le champ du titre s'efface derrière « Modifier », et seulement tant que
                        ce bouton est là — voir `propositionEnAttente`. Les autres attributs
                        gardent le leur : ils n'ont qu'une valeur, et la masquer obligerait à un
                        clic pour corriger une faute de frappe.
                      */}
                      {a.widget === 'texte'
                        && (a.nom !== 'title' || editionTitre || !propositionEnAttente) && (
                        <input
                          type="text"
                          value={productEdits[a.nom] || ''}
                          maxLength={a.maxLongueur}
                          onChange={(e) => updateProductField(a.nom, e.target.value)}
                          placeholder={a.libelle}
                          /*
                            La marque est le seul attribut dont la saisie réécrit un autre
                            champ : ce qu'on écrit ici part dans `g:brand` **et** dans le
                            titre, que Google veut concordants. Au repos, jamais à la frappe.
                          */
                          onKeyDown={a.nom === 'brand'
                            ? (e) => { if (e.key === 'Enter') e.currentTarget.blur(); }
                            : undefined}
                          onBlur={a.nom === 'brand' && productEdits._suggestion
                            ? validerGraphieDuProduit
                            : undefined}
                          /*
                            ⚠️ Grisé le temps de la réécriture, et pour le seul champ Marque.
                            Le drapeau grisait le champ « Orthographe » de la barre d'actions,
                            parti avec son panneau ; le geste vit maintenant ici, et la course
                            qu'il évitait est la même — taper pendant l'aller-retour perdait la
                            frappe, le serveur renvoyant le titre composé sur la valeur d'avant.
                          */
                          disabled={a.nom === 'brand' && applyingBrandPosition}
                        />
                      )}

                      {a.widget === 'zone' && (
                        <textarea
                          value={productEdits[a.nom] || ''}
                          onChange={(e) => updateProductField(a.nom, e.target.value)}
                          placeholder={a.libelle}
                          rows={4}
                        />
                      )}

                      {a.widget === 'choix' && (
                        <select
                          value={productEdits[a.nom] || ''}
                          onChange={(e) => updateProductField(a.nom, e.target.value)}
                        >
                          <option value="">Sélectionner…</option>
                          {a.choix.map(c => (
                            <option value={c.valeur} key={c.valeur}>{c.libelle}</option>
                          ))}
                        </select>
                      )}

                      {a.widget === 'categorie' && rendreChoixCategorie()}

                      {a.widget === 'liste' && rendreListe(a)}

                      {a.widget === 'groupe' && rendreGroupe(a)}

                      {/*
                        Un champ numérique, et le seul de la fiche : `popularity_rank` est
                        déclaré `number` par l'API, sur une échelle bornée. Un champ texte
                        laisserait écrire « très populaire », que Google refuse — et le refus
                        ne nomme pas le produit fautif.

                        `min`, `max` et `pas` viennent de la table : le navigateur refuse la
                        saisie hors bornes sans qu'on écrive une validation de plus.
                      */}
                      {a.widget === 'nombre' && (
                        <input
                          type="number"
                          value={productEdits[a.nom] ?? ''}
                          min={a.min}
                          max={a.max}
                          step={a.pas}
                          placeholder={`${a.min} à ${a.max}`}
                          onChange={(e) => updateProductField(a.nom, e.target.value)}
                        />
                      )}

                      {/*
                        La quantité vendue : un nombre et son unité, en **un seul champ**.

                        ⚠️ Deux champs pour une grandeur se remplissent de travers — on tape
                        « 250 ml » dans la case du nombre et l'unité reste vide —, et une liste
                        de vingt unités se choisit mal. Le champ accepte donc « 250 ml », et
                        `lireMesureSaisie` le ramène à la forme de l'API.

                        ⚠️ Une saisie que le module refuse est **conservée telle quelle**
                        plutôt qu'effacée : l'effacer à la frappe empêcherait d'écrire
                        « 250 ml », dont les quatre premiers caractères sont invalides. La
                        valeur n'atteint le flux que si elle se lit, et le champ le dit.
                      */}
                      {a.widget === 'mesure' && (() => {
                        const brut = productEdits[a.nom];
                        const affiche = typeof brut === 'object' && brut
                          ? `${brut.value} ${brut.unit}`
                          : (brut ?? '');
                        const lisible = affiche === '' || lireMesureSaisie(affiche);
                        return (
                          <>
                            <input
                              type="text"
                              value={affiche}
                              placeholder="250 ml"
                              aria-invalid={!lisible}
                              onChange={(e) => updateProductField(a.nom, e.target.value)}
                              onBlur={(e) => {
                                const lu = lireMesureSaisie(e.target.value);
                                updateProductField(a.nom, lu || (e.target.value.trim() ? e.target.value : null));
                              }}
                            />
                            {!lisible && (
                              <small className="fo-champ-refus">
                                Écrivez un nombre et une unité, par exemple « 250 ml ».
                                Unités acceptées : {(a.unites || []).join(', ')}.
                              </small>
                            )}
                          </>
                        );
                      })()}

                      {/*
                        Le placement de la marque appartient au titre : c'est lui qu'il réécrit.
                        Il vivait sous l'en-tête du produit, à côté du bouton de génération, où
                        rien ne disait sur quel champ il agissait.

                        Et il n'apparaît qu'une fois l'IA passée : sans titre suggéré, il n'a
                        rien à déplacer. Il l'avouait lui-même — « s'appliquera à la prochaine
                        génération » —, c'est-à-dire qu'il occupait sept lignes sous le champ
                        pour annoncer qu'il ne ferait rien.
                      */}
                      {/*
                        ⚠️ Ce que le titre suggéré a perdu du titre d'origine.

                        Relevé sur un cas réel : « Short brodé Ecru Femme, taille XS, Fluide »
                        est devenu « …en Lin et Coton, broderies florales ». Le modèle a
                        enrichi, et perdu « Fluide » — la coupe, c'est-à-dire un mot que les
                        acheteurs tapent.

                        On le dit sans rien refuser : le titre suggéré reste un vrai travail.
                        Mais une perte muette est une perte qu'on ne corrige jamais, et c'est
                        ici, sous le champ, qu'on peut la réparer d'un mot.
                      */}
                      {a.nom === 'title' && productEdits._suggestion?.titreMotsPerdus?.length > 0 && (
                        <p className="fo-titre-perdus">
                          {/*
                            ⚠️ Formulé en constat, pas en reproche. Le mot manquant est
                            parfois une **correction** : le titre du flux BZB porte « Autre »
                            là où l'attribut couleur dit « Multicolore », et le modèle a eu
                            raison de l'écarter. « Ne reprend pas » présentait ce progrès
                            comme une faute ; on dit ce qui diffère, et on laisse juger.
                          */}
                          Le titre d’origine portait aussi «&nbsp;
                          {productEdits._suggestion.titreMotsPerdus.join(' », « ')}&nbsp;».
                          Vérifiez qu’il ne s’agit pas d’une information utile.
                        </p>
                      )}

                      {/*
                        ⚠️ Plus de rubrique « Marque dans le titre » ici.

                        La marque est un **élément du titre** comme les autres : sa place se
                        règle par sa brique dans le gabarit, son orthographe par le champ
                        « Marque [brand] » du tableau. Deux rubriques pour une même phrase
                        obligeaient à deviner laquelle gagnait.

                        Le réglage en lot du flux garde, lui, ses trois boutons : il agit sur
                        toutes les suggestions, y compris celles sans morceaux relevés, que le
                        gabarit ne peut pas recomposer.
                      */}
                      {a.nom === 'title' && productEdits._suggestion && editionTitre
                        && rendreGabaritTitre(
                          productTitreGabarit,
                          (suivant) => {
                            // Toucher au gabarit est ce qui autorise la réécriture du champ.
                            gabaritTouche.current = true;
                            setProductTitreGabarit(suivant);
                          },
                          [productEdits._suggestion.id]
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedOptimizerTool;
